
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, ShieldCheck, AlertTriangle, ChevronDown, ChevronRight, Download,
  Search, ScrollText, Pencil, Flag, Check,
} from 'lucide-react';
import {
  parseVulgarFractionPercent,
  isVulgarFractionPercent,
  isCleanThirdPercent,
  spelledFractionValue,
} from './vulgarFractionPercent';

// ─── Backend payload types (unchanged — these match what the Python
// extractor returns; we just render them differently in the new UI). ─────────

export interface ContractQuant {
  kind: 'currency' | 'percentage' | 'duration' | 'date' | 'count';
  value: string;            // verbatim source substring (never reformatted)
  normalized?: any;
  offset?: number;
  // ── Doc-6 row-context preservation ────────────────────────────────────────
  row_label?: string;
  column_label?: string;
  // FIX 22: per-project sublimit tag (set on quants inside a sublimit exhibit).
  project_entity?: string | null;
  facility_name?: string | null;
  is_sublimit?: boolean;
  // ── Doc-7 OCR transparency ────────────────────────────────────────────────
  ocr_corrected?: boolean;
  // ── Doc-8 tracked-changes delta ───────────────────────────────────────────
  original_value?: string;
  // ── Doc-9 compound definition surfacing ───────────────────────────────────
  definition_compound?: boolean;
  definition_alternative?: string;
  definition_compound_modifier?: string;
  // ── Defined-term audit trail ──────────────────────────────────────────────
  resolved_from?: string;
  // ── Doc-10 numeric-range low endpoint ─────────────────────────────────────
  range_low?: boolean;
  // ── Client FIX 14 (June 2026): backend marks values rescued from PDF
  // text-layer whitespace injection ('$ 50 ,000,000' → $50M, '(10 3 %)' →
  // 103%, '66 2/3%' → 0.6667). Frontend Fix #6 reads this and emits
  // PARSE_UNCERTAIN — never the comfort flag SPACING_NORMALIZED — so a
  // reviewer never sees a 'we cleaned this' badge on a magnitude that came
  // from corrupted text. ── ──────────────────────────────────────────────
  reassembled?: boolean;
  // ── Client FIX 16 (June 2026): backend marks currency tokens whose comma
  // grouping breaks the US 1k convention ('$325,00,000', '$40,000,00') so
  // the frontend can either cross-reference resolve to a single intended
  // value OR surface dual-candidate AMBIGUOUS_SOURCE_VALUE — never silently
  // 'correct' in the wrong direction. `candidates` carries the two
  // plausible intended values (straight parse + pad-short-group), already
  // scaled by any K/M/B suffix.
  malformed?: boolean;
  candidates?: number[];
  // ── Fix #4 re-audit (2026-06-13): per-VALUE Haiku description ──────────
  // Backend describeAllValues runs Haiku once per quant with the clause
  // title, document type, value type, and source sentence as deterministic
  // context, and writes the result here. When populated the frontend's
  // extractDescription surfaces it on the Excel Description column instead
  // of the shared per-clause plain_english summary (which was the same
  // string across every value in a clause — the gap the client re-audit
  // flagged on the §1.1 Applicable Margin grid). Empty string on any Haiku
  // failure so the quality gate cleanly falls back to NEEDS_REVIEW.
  value_description?: string;
  // ── FIX 12 re-test (2026-06-22): ISO currency code captured by the
  // backend extractor (`_classify_currency`) at the same moment the value
  // was identified, so the Amount / Display Value / Unit columns all read
  // from the same source-truthed marker instead of each re-detecting from
  // the value string. Optional — old sessions stored before this change
  // omit it, and `inferUnit` falls back to the symbol/ISO scan it always
  // did.
  currency?: string;
}

export interface ContractClause {
  clause_no: string;
  raw_text: string;
  plain_english?: string;
  page?: number | null;
  line?: number;
  scheme?: string;
  quants?: ContractQuant[];
  conflicts_with?: string[];
  ambiguous?: boolean;
  ambiguity_note?: string;
  supersedes?: string;
  delegated_to?: string[];
  delegated_missing?: string[];
  truncated?: boolean;
  tracked_changes?: boolean;
  has_carveout?: boolean;
  // ── Fix #1 (June 2026): content-type tagging ──────────────────────────────
  // The backend extractor flags every clause as 'provision' | 'schedule' |
  // 'exhibit' | 'definition' so the default review surface can hide
  // schedule/exhibit rows (capitalisation tables, subsidiary ownership lists,
  // permitted-indebtedness exhibits — typically 30%+ of rows in dense credit
  // agreements and never the actual operative provisions a reviewer needs to
  // approve). schedule_name carries the schedule's title when available
  // (e.g., "Schedule 4.1(c) — Subsidiaries") so the UI can label it.
  // Backward compat: when these fields are absent (old responses, DOCX with
  // no page markers), the UI treats the clause as a provision.
  content_type?: 'provision' | 'schedule' | 'exhibit' | 'definition';
  schedule_name?: string | null;
  // ── Client FIX 10 (June 2026): backend _strip_pdf_footers captures the
  // printed page number from the centered '-NNN-' footer pattern BEFORE
  // removing the footer from raw_text (so quant extractors never see the
  // doc-management artifacts). Frontend extractPrintedPage reads this
  // first — that's the authoritative source for FIX 13's FOOTER page
  // (clause-level; superseded by printed_page_offsets when per-value
  // attribution is available — see ContractClause.printed_page_offsets).
  // attribution. Absent on cached responses from before FIX 10 deployed;
  // in that case extractPrintedPage falls back to its raw_text scan, so
  // back-compat holds.
  printed_page?: number | null;
  // FIX 13 v2 (June 2026): per-value page attribution. List of
  // [offset_in_raw_text, printed_page] tuples sorted by offset. The
  // frontend's extractPrintedPage uses binary search by quant offset
  // to resolve the printed page of each extracted value individually —
  // closes the Preamble bug where 47 values shared Page 1 despite
  // spanning ~100 pages of the definitions section.
  printed_page_offsets?: Array<[number, number]>;
  // ── Fix #2 (June 2026): clause_title resolution from TOC ──────────────────
  // The backend parses the document's own Table of Contents and emits a
  // human-readable heading per clause ("Defined Terms", "Incremental
  // Facilities") instead of leaving the UI to guess from raw_text. When the
  // TOC didn't cover this clause the backend emits 'UNRESOLVED' and the UI
  // falls back to deriving from raw_text — i.e., older response payloads
  // without the field still render correctly.
  clause_title?: string;
  // FIX 9: definitions-section atomization (backend). When a definitions
  // section is split into one row per defined term, each child carries the
  // term name and a flag so the export pipeline skips re-atomizing.
  defined_term_name?: string | null;
  parent_clause_no?: string | null;
  atomized?: boolean;
  definition_intro?: boolean;
  // FIX 21: parent facility identity for multi-facility credit agreements.
  facility_name?: string | null;
  facility_id?: string | null;
  facility_confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  // Set by the backend when the "Skip plain-English summaries" checkbox is
  // on (backend/src/routes/intelligence.js FIX 28 path) — plain_english is
  // the literal placeholder '— summary skipped —', not a real generated
  // description. extractDescription reads this so a deliberately-skipped
  // summary isn't scored as a quality failure.
  summary_skipped?: boolean;
}

export interface ContractVerificationIssue {
  severity: 'warning' | 'error';
  code: string;
  clause_no: string;
  message: string;
}

export interface ContractVerification {
  status: 'ok' | 'warnings' | 'yield_low';
  summary: Record<string, number>;
  issues: ContractVerificationIssue[];
}

export interface ContractAnalysisFileResult {
  file: string;
  error?: string;
  extracted_text_preview?: string;
  summary?: { total_clauses: number; total_quants: number };
  verification?: ContractVerification;
  clauses?: ContractClause[];
  // FIX 27 — KDE-Mapped Output Schema v2. Null/absent when the assembler
  // failed; UI falls back to the legacy flat download in that case.
  schema_v2?: SchemaV2 | null;
  // FIX 21 Phase 2-lite (Asana 1215605277493857) — the THREADED Layer-1
  // registry, captured in the Node route straight off the extractor's
  // [CLAUSES] payload. Deliberately a sibling of schema_v2, NOT a key inside
  // it: schema_v2.facility_registry is an ASSEMBLER_RECOMPUTE over the full
  // text, this one is the real stage output. Absent on any session stored
  // before this build.
  facility_registry_stage?: FacilityRegistryStage | null;
}

// FIX 21 Phase 2-lite — shared shape for both registry provenances.
export interface FacilityRegistryEntry {
  facility_id?: string;
  display_name?: string;
  aliases?: string[];
  designators?: string[];
  facility_type?: string | null;
  status?: string;            // 'DECLARED' | 'INFERRED'
}

export interface FacilityRegistrySublimit {
  name?: string;
  parent_id?: string | null;
  kind?: string;
  reason?: string;
}

// The assembler's diagnostic block (source: 'ASSEMBLER_RECOMPUTE').
export interface FacilityRegistryBlock {
  source?: string;
  facilities?: FacilityRegistryEntry[];
  sublimits?: FacilityRegistrySublimit[];
  flags?: string[];
  declared_count?: number;
}

// The Node-threaded stage output (source: 'EXTRACTOR_STAGE'). Same core
// shape; carries no declared_count (the extractor does not compute one) and
// adds the segmentation caveat.
export interface FacilityRegistryStage {
  source?: string;
  facilities?: FacilityRegistryEntry[];
  sublimits?: FacilityRegistrySublimit[];
  flags?: string[];
  registry_segment_note?: string;   // 'WHOLE_DOCUMENT' | 'FIRST_SEGMENT_ONLY'
  segment_count?: number;
}

// FIX 27 — KDE-Mapped Output Schema v2 (consumed by Structured Export).
export interface SchemaV2 {
  schema_version: string;
  document_id: string | null;
  template_name: string;
  agreement_header: {
    borrower: string | null;
    deal_size: { amount: number; currency: string; display: string } | null;
    // WP-3a-bis split (2026-08-11): asserted/derived are separate fields.
    // Export deal-size column sources aggregate_commitment_asserted ONLY (I-P5).
    aggregate_commitment_asserted: { amount: number; currency: string; display: string } | null;
    deal_amount_derived: { amount: number; currency: string; display: string; completeness_qualifier: string } | null;
    ASSERTED_COMPUTED_DIVERGENCE: { asserted: number; derived: number; delta_pct: number } | null;
    closing_date: string | null;
    admin_agent: string | null;
    confidence: string;
    // FC-8: per-field confidence keyed by field name (borrower, closing_date,
    // admin_agent, deal_size). Distinct from aggregate `confidence`.
    field_confidence?: Record<string, string | null> | null;
    _missing: string[];
    // FC-10 / I-U1: set on Linux production when the 10s wall-clock budget
    // fires; lists the field extractor(s) that were cut off.
    time_budget_exceeded?: string[] | null;
  } | null;
  parties: {
    parties: Array<{ name: string; aliases: string[]; role: string; first_page: number }>;
    lender_table: Array<{
      lender_name: string;
      commitment_amount: number;
      currency: string;
      commitment_share: number | null;
      facility: string | null;
    }>;
    total_commitment_verified: boolean;
    expected_total: number | null;
    actual_total: number;
    confidence: string;
    _missing: string[];
  } | null;
  facilities: Array<{
    facility_name: string;
    facility_type: string;
    completeness_score: number;
    // FIX 26 follow-up — pricing_grid is emitted as a structured array of
    // tier objects per Ryan's Session 512 DoD; other KDEs remain primitives.
    kdes: Record<string, string | number | unknown[]>;
    // TRACK A (2026-07-31): per-KDE reviewer flags from the commitment
    // provenance gate. Absent key = no flag for that KDE.
    kde_flags?: Record<string, string>;
    // Loan IQ display layer (2026-09): short value beside the verbatim KDE.
    // method: 'as-is' | 'rule' | 'llm' | 'verbatim'; flag set when the trim
    // could not be verified and the verbatim text was kept.
    kdes_loan_iq?: Record<string, { value: string; method: string; chars_before?: number; chars_after?: number; flag?: string }>;
    provisions: unknown[];
  }>;
  unattributed_provisions: unknown[];
  // FIX 18 (June 2026): Material-Term Recall Gate. Backend material_term_auditor
  // emits MISSING flag rows for deal-defining terms detected in the source
  // document but absent from the extracted KDE map. Optional (older payloads
  // won't carry the field — downstream code must defensive-check).
  material_term_flags?: Array<{
    kde_id: string;
    term_name: string;
    status: string;
    note: string;
    page_ref: string | null;
  }>;
  override_log_rows: string[][] | null;
  // TRACK A (2026-07-31): facilities demoted from the grid (sublimits,
  // incrementals) with reasons — present on all Track A+ builds.
  demoted_candidates?: Array<{ facility_name: string; reason: string }>;
  // TRACK B PHASE 1 OBSERVABILITY (PR #471) — diagnostic block the assembler
  // RECOMPUTES from full text + final clauses. Optional: null when
  // facility_registry.py could not be imported, absent on older sessions.
  facility_registry?: FacilityRegistryBlock | null;
  completeness_summary: {
    header_kdes_found: number;
    header_kdes_total: number;
    facility_kdes_found_avg_pct: number;
    facility_kdes_total_per_facility: number;
    facility_count: number;
    material_term_flags_count?: number;
  };
  _failed_blocks: Array<{ block: string; error: string }>;
}

export interface ContractAnalysisResultsProps {
  results: ContractAnalysisFileResult[];
}


// ─── New value-record model used by the Clause Review UI ────────────────────
// Backend quant → frontend ValueRecord via quantToValueRecord(). The mapping
// is purely frontend-side; backend extractors stay untouched.

type ValueType = 'currency' | 'percent' | 'ratio' | 'date' | 'duration' | 'count';
type ValueStatus = 'pending' | 'approved' | 'flagged';
type StatusFilter = 'all' | 'pending' | 'approved' | 'flagged';

// Fix #1: Scope filter — surfaces only operative provisions by default and
// hides schedule/exhibit rows (capitalisation tables, subsidiary ownership
// lists, etc., which can be 30%+ of the extracted rows in dense credit
// agreements but never the actual operative clauses a reviewer is meant to
// approve). User can toggle to see schedules or everything via the toolbar
// pill group.
type ScopeFilter = 'provisions' | 'schedules' | 'all';

// True when the clause carries non-operative content (schedule/exhibit).
// Centralised so the workspace, gutter, and exporter all agree on which
// rows belong to which scope.
function isScheduleScopeClause(c: ContractClause): boolean {
  const ct = c.content_type;
  return ct === 'schedule' || ct === 'exhibit';
}

function hasSublimitData(c: ContractClause): boolean {
  return (c.quants || []).some(q => q.is_sublimit);
}

// Client F-02 fix (June 2026) — EXPORT-ONLY noise filter:
// Bare list-item clause numbers like "20.", "64.", "103." are schedule list
// items (title exceptions, easement recordation dates, ALTA endorsements,
// …) that DECIMAL_RE picked up as clause heads but whose page was outside
// any detected schedule range — backend tagged them content_type='provision'
// by default. They flood "Provisions only" exports (387 fake rows in a
// 654-row audit). A bare ^\d{1,4}\.$ is not a section number in any modern
// contract (real heads have sub-numbering or a following Title-Cased word).
//
// Also catches the Unix-timestamp clause_no anomaly ('1754083217.10'
// observed in the same audit — already INVALID_LABEL flagged by Fix #3,
// but still leaked into the export).
//
// Scoped to the Excel export pipeline ONLY — the workspace/gutter still
// shows the rows so a reviewer can confirm them. UI behaviour unchanged.
const _LIST_ITEM_CLAUSE_NO_RE = /^\d{1,4}\.$/;
const _TIMESTAMP_CLAUSE_NO_RE = /^\d{10,}(?:\.\d+)?$/;

// Client FIX 1 re-audit (June 2026): defence-in-depth filters for two
// patterns the backend's _detect_clause now rejects directly — kept on
// the frontend so older backend outputs (cached responses, snapshots
// pre-deployment) still get cleaned at export time.
//
//   _FOOTER_DOC_ID_CLAUSE_NO_RE — PDF page-footer document management
//     IDs like '8131502.4' (38 Schedule C-1 rows in the MUFG/Revlon audit),
//     '163857010.19' (US-DOCS\… style). Real clause numbers cap at 4-digit
//     leading groups; 5+ digits is never a real section.
//
//   _PROPER_NOUN_PAREN_CLAUSE_NO_RE — ownership/jurisdiction row labels
//     like '(Netherlands)', '(Belgium)', '(Cayman Islands)' (192 rows on
//     page 427 in the MUFG/Revlon audit). Title Case (capital + 2+
//     lowercase) signals a proper noun, not a clause marker. Single-
//     letter '(C)', Roman '(II)', all-lower '(viii)' legitimate sub-clause
//     markers are preserved.
const _FOOTER_DOC_ID_CLAUSE_NO_RE = /^\d{5,}\.\d+$/;
const _PROPER_NOUN_PAREN_CLAUSE_NO_RE = /^\(\s*[A-Z][a-z]{1,}(?:\s+[A-Z][a-z]+)*\s*\)$/;

function isExportNoiseClause(c: ContractClause): boolean {
  const no = (c.clause_no || '').trim();
  if (!no) return false;
  // Whitelist: '(Preamble)' is a backend-assigned synthetic clause_no for
  // the pre-first-clause body content. It IS Title Case in parens but it's
  // a deliberate sentinel, not a parsed ownership row label — must never
  // be filtered out of the export.
  if (no === '(Preamble)') return false;
  if (_LIST_ITEM_CLAUSE_NO_RE.test(no)) return true;
  if (_TIMESTAMP_CLAUSE_NO_RE.test(no)) return true;
  if (_FOOTER_DOC_ID_CLAUSE_NO_RE.test(no)) return true;
  if (_PROPER_NOUN_PAREN_CLAUSE_NO_RE.test(no)) return true;
  return false;
}

interface ValueRecord {
  id: string;
  type: ValueType;
  label: string;
  op: string;
  sym: string;
  value: string;
  unit: string;
  src: string;
  page: number | null;
  phrase: string;
  offset?: number;
  // pass-through metadata so badges + tooltips can still surface them
  row_label?: string;
  // FIX 3 re-audit (2026-06-13): column_label carries the pricing-grid axis
  // ('Term SOFR' vs 'Base Rate') that disambiguateLabelsInPlace uses to
  // separate row-aligned duplicates inside the same availability tier.
  // Previously only row_label was carried through; the column axis was
  // dropped here even though inferLabel consumed it upstream.
  column_label?: string;
  // FIX 3 re-audit (2026-06-13): synthesized tier marker extracted from
  // clause.raw_text when the backend's _table_context_for_offset couldn't
  // tag this row (PDF flattened the visual grid). Used by the disambiguator
  // ONLY as a fallback to backend's row_label, never an override — so
  // documents that DO emit pipe-tables keep their canonical row labels.
  // Empty string when the raw_text window has no tier signal at all.
  synthesized_row_label?: string;
  ocr_corrected?: boolean;
  original_value?: string;
  resolved_from?: string;
  definition_compound?: boolean;
  definition_alternative?: string;
  definition_compound_modifier?: string;
  range_low?: boolean;
  // Fix #3 (June 2026): full sentence around the value (debug/audit) and a
  // per-row flag that classifies the label cleanup outcome — INVALID_LABEL
  // (doc-id stripped → label nulled), NEEDS_REVIEW (label too short after
  // cleanup), DISAMBIGUATED (suffix appended because >1 row shared the same
  // (clause, label) pair), or null when the row is clean.
  context_snippet?: string;
  label_flag?: 'INVALID_LABEL' | 'DISAMBIGUATED' | 'NEEDS_REVIEW' | null;
  // FIX 14 (June 2026): backend flagged this row as rescued from PDF
  // intra-token whitespace ('1. 75 %', '$ 50 ,000,000', '66 2/3%').
  // Drives PARSE_UNCERTAIN in validateDisplayValue.
  reassembled?: boolean;
  // FIX 16 (June 2026): backend flagged a malformed comma-grouped currency
  // token ('$325,00,000', '$40,000,00'). `candidates` is the two plausible
  // intended values (low = straight parse, high = pad-short-group);
  // resolveMalformedInPlace() walks the document for cross-reference
  // corroboration and writes `malformed_resolution` + `resolved_amount`
  // before the export loop reads them. Drives the new Display Flag values
  // AMBIGUOUS_SOURCE_VALUE (zero or both candidates corroborated) and
  // RESOLVED_FROM_CROSS_REFERENCE (exactly one corroborated).
  malformed?: boolean;
  candidates?: number[];
  malformed_resolution?: 'RESOLVED' | 'AMBIGUOUS' | null;
  resolved_amount?: number | null;
  resolution_note?: string;
  // FIX 22: per-project sublimit attribution carried through to the export.
  // facility_name = per-quant facility tag (Exhibit J sublimit rows belong
  // to a specific facility, not the exhibit-clause facility); is_sublimit
  // distinguishes the synthetic sublimit row from ordinary clause quants.
  project_entity?: string | null;
  facility_name?: string | null;
  is_sublimit?: boolean;
  // FIX 12 re-test (2026-06-22): authoritative ISO currency code captured
  // by the backend extractor (`_classify_currency`) at extraction time.
  // Carried through to the Excel export path so detectCurrency uses it as
  // the highest-priority signal instead of re-deriving from the value
  // string — kills the Row-15 USD-misclassification at the root.
  currency?: string;
}

interface LiveValueState {
  status: ValueStatus;
  edited: boolean;
  note?: string;
  // Overridable fields for the inline edit form
  label?: string;
  op?: string;
  sym?: string;
  value?: string;
  unit?: string;
}


// ─── Design tokens (mapped onto Tailwind arbitrary values) ──────────────────

const TYPE_TOKENS: Record<ValueType, {
  text: string;           // body color for value-record label
  tint: string;           // approved bg tint for the source mark
  rail: string;           // accent rail color for number badge + mark underline
  dot: string;            // small swatch (type dot, 7×7) bg
}> = {
  currency: { text: 'text-[#176043]', tint: 'bg-[#e9f4ee]', rail: 'bg-[#1f8a5b]', dot: 'bg-[#1f8a5b]' },
  percent:  { text: 'text-[#583f9c]', tint: 'bg-[#efebf9]', rail: 'bg-[#7c5cd0]', dot: 'bg-[#7c5cd0]' },
  ratio:    { text: 'text-[#2a539b]', tint: 'bg-[#e9f0fa]', rail: 'bg-[#3a6fc4]', dot: 'bg-[#3a6fc4]' },
  date:     { text: 'text-[#15646f]', tint: 'bg-[#e5f2f4]', rail: 'bg-[#1f8a97]', dot: 'bg-[#1f8a97]' },
  duration: { text: 'text-[#8a6011]', tint: 'bg-[#f6efdf]', rail: 'bg-[#c0892a]', dot: 'bg-[#c0892a]' },
  count:    { text: 'text-[#374151]', tint: 'bg-[#f3f4f6]', rail: 'bg-[#6b7280]', dot: 'bg-[#6b7280]' },
};

const TYPE_RAIL_HEX: Record<ValueType, string> = {
  currency: '#1f8a5b',
  percent:  '#7c5cd0',
  ratio:    '#3a6fc4',
  date:     '#1f8a97',
  duration: '#c0892a',
  count:    '#6b7280',
};

const TYPE_LABEL: Record<ValueType, string> = {
  currency: 'Currency',
  percent:  'Percent',
  ratio:    'Ratio',
  date:     'Date',
  duration: 'Duration',
  count:    'Count',
};


// ─── File helpers ───────────────────────────────────────────────────────────


function stripMarkdownNoise(text: string): string {
  if (!text) return '';
  return text
    .replace(/__([^_\n]+)__/g, '$1')
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Doc-id stamps and bare-number noise that occasionally survive into a
// derived title — kept here so the raw_text fallback below doesn't surface
// junk like "- 70 - 8033780", "8033780", or single digits ("2", "3") that
// would otherwise leak into the gutter and the Excel Title column.
const _DOC_ID_TITLE_RE = /^-\s*\d+\s*-\s*\d{7,}|^\d{7,}\b/;
const _BARE_NUMBER_RE = /^\d+(?:\.\d+)*\.?$/;

// Demo-blocker fix (Session 489): section heading shape "2.1 Indemnification"
// / "5.3 Reporting Obligations" — used as a body-text fallback when the
// backend TOC resolver returned UNRESOLVED. Captures the heading words
// AFTER the section number so the Title column never leaks a literal
// UNRESOLVED string into the export.
const _SECTION_HEADING_RE = /^\s*(?:Section\s+|Article\s+|§\s*)?\d+(?:\.\d+)*\.?\s+([A-Z][\w\s\-/.,&'’]{2,80}?)(?:[.;:]|$)/i;

function deriveClauseTitle(clause: ContractClause): string {
  // Fix #2: prefer the TOC-resolved title from the backend when present.
  // Backend emits 'UNRESOLVED' when the TOC didn't cover this clause; that's
  // the only case we fall through to the raw_text derivation. Older response
  // payloads without the field also fall through, preserving back-compat.
  const backendTitle = (clause.clause_title || '').trim();
  if (backendTitle && backendTitle !== 'UNRESOLVED') {
    return backendTitle.length > 64
      ? backendTitle.slice(0, 64).trimEnd() + '…'
      : backendTitle;
  }

  const raw = stripMarkdownNoise(clause.raw_text || '');
  if (!raw) return clause.clause_no || 'Clause';

  // Demo-blocker fix (Session 489): pull the section-heading words directly
  // from the first line ('2.1 Indemnification' → 'Indemnification') so a
  // backend TOC miss does not surface a literal 'UNRESOLVED' label.
  const firstLine = raw.split(/[\n]/)[0] || raw;
  const headingMatch = firstLine.match(_SECTION_HEADING_RE);
  if (headingMatch) {
    const heading = headingMatch[1].trim();
    if (heading && heading.length >= 3) {
      return heading.length > 64 ? heading.slice(0, 64).trimEnd() + '…' : heading;
    }
  }

  const sentenceFirst = raw.split(/[\n.]/)[0] || raw;
  const trimmed = sentenceFirst.trim();
  if (!trimmed) return clause.clause_no || 'Clause';
  // Reject the noise patterns the backend already filters — bare digits and
  // 7+ digit doc-id stamps — so a TOC miss never surfaces them in the UI.
  if (_BARE_NUMBER_RE.test(trimmed) || _DOC_ID_TITLE_RE.test(trimmed)) {
    return clause.clause_no || 'Clause';
  }
  // Defensive final guard — never let a literal 'UNRESOLVED' token escape
  // into the rendered title even if the backend repeats it inside raw_text.
  if (/^UNRESOLVED\b/i.test(trimmed)) {
    return clause.clause_no || 'Clause';
  }
  // Cap to ~64 chars so the gutter stays narrow
  return trimmed.length > 64 ? trimmed.slice(0, 64).trimEnd() + '…' : trimmed;
}

// Resolved title for the Excel Source column — same fallback chain as the
// gutter, but never returns 'UNRESOLVED' (we drop it so Source stays as
// just §clause_no in that case).
function resolvedClauseTitle(clause: ContractClause): string {
  const backendTitle = (clause.clause_title || '').trim();
  if (backendTitle && backendTitle !== 'UNRESOLVED') return backendTitle;
  const derived = deriveClauseTitle(clause);
  if (!derived || derived === (clause.clause_no || '') || derived === 'Clause') {
    return '';
  }
  return derived;
}


// ─── Backend quant → ValueRecord mapping (the heart of the new UI) ──────────

function mapKindToType(kind: ContractQuant['kind'], value: string): ValueType {
  // Ratio detection — "4.50 to 1.00" or "4.50 : 1.00" come through from the
  // Python extractor as 'count' or 'duration'; visually they're ratios.
  if (/[\d.]+\s*(?::|to)\s*[\d.]+/.test(value || '')) return 'ratio';
  switch (kind) {
    case 'currency':   return 'currency';
    case 'percentage': return 'percent';
    case 'duration':   return 'duration';
    case 'date':       return 'date';
    case 'count':
    default:           return 'count';
  }
}

// FIX 12 re-test (2026-06-22): full multi-currency vocabulary. The prior
// implementation checked only €/£/¥ symbols and defaulted everything else
// (including "EUR 100,000" / "GBP 50,000" / "CAD 75,000" ISO-prefix forms)
// to USD — Ryan's QA Row 15 failure. We now follow the spec priority:
//   (1) backendCurrency  — backend extractor's _classify_currency result
//   (2) ISO code in value — "EUR 100,000", "GBP 50,000", ...
//   (3) currency symbol  — $, €, £, ¥, ₹
//   (4) context keyword  — "Euros", "Sterling", "Canadian Dollars", ...
//   (5) USD default      — only when nothing above matches and we are
//                          confident the value is currency-typed at all.
// The default has to stay USD (not "Unknown") because changing it would
// re-tag every legacy quant in a USD-only document; the new priority chain
// guarantees non-USD currencies are detected when ANY signal is present.
const _ISO_RE = /\b(USD|EUR|GBP|CAD|AUD|CHF|JPY|INR|SGD|HKD|AED)\b/i;
const _CCY_KEYWORDS: Array<{re: RegExp; code: string}> = [
  { re: /\bcanadian\s+dollars?\b/i,    code: 'CAD' },
  { re: /\baustralian\s+dollars?\b/i,  code: 'AUD' },
  { re: /\bswiss\s+francs?\b/i,        code: 'CHF' },
  { re: /\bsingapore\s+dollars?\b/i,   code: 'SGD' },
  { re: /\bhong\s*kong\s+dollars?\b/i, code: 'HKD' },
  { re: /\bus\s+dollars?\b/i,          code: 'USD' },
  { re: /\bsterling\b/i,               code: 'GBP' },
  { re: /\beuros?\b/i,                 code: 'EUR' },
  { re: /\bpounds?\b/i,                code: 'GBP' },
  { re: /\bdollars?\b/i,               code: 'USD' },
  { re: /\brupees?\b/i,                code: 'INR' },
  { re: /\byen\b/i,                    code: 'JPY' },
];

function inferUnit(
  type: ValueType,
  value: string,
  kind: ContractQuant['kind'],
  backendCurrency?: string,
): string {
  switch (type) {
    case 'currency': {
      // (1) backend-resolved code wins — single source of truth across cols.
      if (backendCurrency) return backendCurrency.toUpperCase();
      // (2) explicit ISO code in the captured value substring.
      const iso = value.match(_ISO_RE);
      if (iso) return iso[1].toUpperCase();
      // (3) currency symbol.
      if (value.includes('€')) return 'EUR';
      if (value.includes('£')) return 'GBP';
      if (value.includes('¥')) return 'JPY';
      if (value.includes('₹')) return 'INR';
      // (4) context keyword ("Euros", "Sterling", ...).
      for (const { re, code } of _CCY_KEYWORDS) {
        if (re.test(value)) return code;
      }
      // (5) Fall through to USD default — preserves legacy behavior on
      // bare-$ matches and pure-numeric captures.
      return 'USD';
    }
    case 'percent':
      return /(?:p\.?a\.?|per\s+annum)/i.test(value) ? '% p.a.' : '%';
    case 'ratio':
      return 'ratio';
    case 'date':
      return 'date';
    case 'duration': {
      const m = value.match(/\b(years?|months?|weeks?|days?|hours?|minutes?|business\s+days?|calendar\s+days?)\b/i);
      return m ? m[1].toLowerCase().replace(/^./, c => c.toUpperCase()) : (kind === 'count' ? 'count' : 'days');
    }
    case 'count':
    default:
      return 'count';
  }
}

// Operator/symbol detection — scans the 80 chars immediately before the
// quant offset for common contractual operator phrases. Order matters:
// longer / negated phrases come first so "not less than" isn't
// mis-classified as "less than".
const OP_PATTERNS: Array<{ re: RegExp; sym: string; op: string; dateOnly?: boolean }> = [
  { re: /not\s+less\s+than\s*$/i,                              sym: '≥', op: 'not less than' },
  { re: /not\s+more\s+than\s*$/i,                              sym: '≤', op: 'not more than' },
  { re: /(?:shall\s+)?not\s+(?:to\s+)?exceed(?:ing)?\s*$/i,    sym: '≤', op: 'not to exceed' },
  { re: /no\s+more\s+than\s*$/i,                               sym: '≤', op: 'no more than' },
  { re: /no\s+less\s+than\s*$/i,                               sym: '≥', op: 'no less than' },
  { re: /at\s+least\s*$/i,                                     sym: '≥', op: 'at least' },
  { re: /up\s+to\s*$/i,                                        sym: '≤', op: 'up to' },
  { re: /minimum\s+(?:of\s+)?\s*$/i,                           sym: '≥', op: 'minimum' },
  { re: /maximum\s+(?:of\s+)?\s*$/i,                           sym: '≤', op: 'maximum' },
  { re: /equal\s+to\s*$/i,                                     sym: '=', op: 'equal to' },
  { re: /(?<!not\s)less\s+than\s*$/i,                          sym: '<', op: 'less than' },
  { re: /(?<!not\s)more\s+than\s*$/i,                          sym: '>', op: 'more than' },
  { re: /(?<!not\s)greater\s+than\s*$/i,                       sym: '>', op: 'greater than' },
  { re: /(?:on\s+or\s+)?(?:before|after)\s*$/i,                sym: '≥', op: 'on or after', dateOnly: true },
  { re: /exceed(?:s|ing)?\s*$/i,                               sym: '>', op: 'exceeding' },
  { re: /approximately\s*$/i,                                  sym: '≈', op: 'approximately' },
  // Date-only context cues — sym intentionally blank so the chip renders
  // just the context word, not a meaningless "=" next to a date.
  { re: /effective\s*$/i,                                      sym: '',  op: 'effective' },
  { re: /dated\s*$/i,                                          sym: '',  op: 'dated' },
  { re: /as\s+of\s*$/i,                                        sym: '',  op: 'as of' },
  { re: /commencing\s+(?:on\s+)?\s*$/i,                        sym: '',  op: 'commencing' },
  { re: /starting\s+(?:on\s+)?\s*$/i,                          sym: '',  op: 'starting' },
];

function inferOperator(text: string, offset: number | undefined, type: ValueType): { sym: string; op: string } {
  if (!text || offset == null) {
    // For dates without explicit comparison, suppress the chip — a bare "="
    // next to a date reads as noise rather than information.
    return type === 'date' ? { sym: '', op: '' } : { sym: '=', op: '' };
  }
  const before = text.slice(Math.max(0, offset - 80), offset);
  for (const p of OP_PATTERNS) {
    if (p.dateOnly && type !== 'date') continue;
    if (p.re.test(before)) return { sym: p.sym, op: p.op };
  }
  return type === 'date' ? { sym: '', op: '' } : { sym: '=', op: '' };
}

// Label inference — picks the noun phrase that names what the value
// represents. Priority: backend-supplied row/column/resolved labels → noun
// phrase heuristic over preceding text → empty (the value record then
// renders just the type name + value, which still reads cleanly).
//
// The window is scrubbed of parentheticals, quotes, bracketed asides, and
// section references before extraction so the candidate noun phrase isn't
// contaminated by document boilerplate like "Section 1.1) falls below" or
// 'Meridian Capital Partners, LLC ("Customer")'.
function inferLabel(text: string, offset: number | undefined, quant: ContractQuant, type: ValueType): string {
  // Fix #3 (June 2026): repair OCR intra-word spaces on backend-supplied
  // labels too — table row_label / column_label coming from OCR'd
  // capitalisation schedules routinely arrive as "Equivale nt of US Dollars"
  // or "Indebted ness". Without this pre-pass the heuristic returns the
  // broken text verbatim and the post-pass cleanLabel can no longer rejoin
  // it because inferLabel returned earlier.
  if (quant.row_label && quant.row_label.trim())         return repairOcrSpaces(quant.row_label.trim());
  if (quant.resolved_from && quant.resolved_from.trim()) return repairOcrSpaces(quant.resolved_from.trim());
  if (quant.column_label && quant.column_label.trim())   return repairOcrSpaces(quant.column_label.trim());
  if (!text || offset == null) return '';
  // Apply OCR-space repair BEFORE the noun-phrase / word-fallback steps so
  // a broken pair like "con stitute" survives as the joined "constitute".
  // Otherwise the last-4-words fallback chops "con" off and the post-call
  // cleanLabel sees only the orphaned "stitute" with no way to repair it.
  const raw = repairOcrSpaces(text.slice(Math.max(0, offset - 100), offset));
  // Aggressively strip noise that shouldn't be part of a semantic label
  const window = raw
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/["“”'‘’]/g, ' ')
    .replace(/§\s*[\d.()a-z]+/gi, ' ')
    .replace(/\b(?:Section|Subsection|Article|Schedule|Exhibit|Annex)\s+[\d.()a-z]+\s*\)?/gi, ' ')
    .replace(/\(+\s*$/, ' ');  // strip trailing unclosed paren (e.g., "Million Dollars (")
  // Take only the LAST sentence/line of the window. Clause headers
  // ("2.3 Late Payment\n") and prior sentences shouldn't leak into the
  // label heuristic — every meaningful noun phrase lives in the sentence
  // immediately preceding the quant.
  const segments = window.split(/\n+|\.\s+(?=[A-Z])/);
  const lastSegment = segments[segments.length - 1] || window;

  // Light strip — collapse whitespace + trim trailing minor punctuation.
  // Run date-specific detection on THIS (before generic operator stripping
  // removes "Effective" / "Dated" / "Commencing" etc., which are the very
  // keywords that name a date).
  const lightStripped = lastSegment
    .replace(/\s+/g, ' ')
    .replace(/[—–\-:,;.]+\s*$/, '')
    .trim();

  if (type === 'date') {
    // "Commencement Date" / "Effective Date" / "Termination Date" etc.
    const dateNoun = lightStripped.match(/((?:[A-Z][a-zA-Z]*\s+){0,3}(?:Commencement|Termination|Effective|Expiration|Expiry|Renewal|Maturity|Closing|Execution|Anniversary)\s+Date)\s*$/i);
    if (dateNoun) return dateNoun[1].replace(/\s+/g, ' ').trim();
    // Bare trailing date verb ("...Effective" → "Effective")
    const dateVerb = lightStripped.match(/\b(Effective|Dated|Commencing|Starting|Beginning|Expiring|Renewing|Terminating)\s*$/i);
    if (dateVerb) return dateVerb[1].charAt(0).toUpperCase() + dateVerb[1].slice(1).toLowerCase();
  }

  // Generic strip — drops operator/filler/verb/date-cue words so the noun
  // phrase extraction lands on a real noun rather than a verb leak like
  // "System Availability Vendor guarantees". Iterated until stable so chained
  // fillers ("is due within") all peel off in one inferLabel call.
  let stripped = lightStripped;
  let prevStripped = '';
  while (prevStripped !== stripped) {
    prevStripped = stripped;
    stripped = stripped
      .replace(/(?:guarantees?|warrants?|covenants?|agrees?|promises?|undertakes?|means|signifies|denotes|refers\s+to|(?:shall|may|will|must|would|should|can|might)\s+(?:not\s+)?[a-z]+|fails?\s+to\s+\w+|not\s+(?:to\s+)?exceed(?:ing)?|equal\s+to|less\s+than|more\s+than|greater\s+than|at\s+least|not\s+less\s+than|no\s+more\s+than|up\s+to|minimum\s+(?:of\s+)?|maximum\s+(?:of\s+)?|amount\s+of|fee\s+of|rate\s+of|per\s+annum|within|due|effective|dated|as\s+of|commencing|starting|of|at|to|in|for|the|a|an|any|and|or|by|with|that|which|is|are|on|from|until|before|after)\s*$/i, '')
      .replace(/[—–\-:,;]+\s*$/, '')
      .replace(/\b(?:headline|note|comment|provision)\s*$/i, '')
      .replace(/[—–\-:,;]+\s*$/, '')
      .trim();
  }

  // Last 2-5 capitalised noun phrase (allows lowercase connectors)
  const capMatch = stripped.match(/((?:[A-Z][a-zA-Z&]+(?:\s+(?:of|the|and|&|or|for|to|in))?\s+){1,4}[A-Z][a-zA-Z&]+)\s*$/);
  if (capMatch) return capMatch[1].replace(/\s+/g, ' ').trim();

  // Fallback: last 2-4 plain words — clean punctuation + drop leading
  // articles so "breach, the breaching party" reads as "breaching party".
  // Client FIX 14b (June 2026): the decimal-internal preservation rule that
  // FIX 14 applied to value extraction also applies to label generation.
  // The naive [,;.:]+ stripper turned numeric labels '1.875%' into '1875%'
  // and '3.00 to 1.00' into '300 to 100' on the margin-grid rows where the
  // row label IS a numeric value, since label cleanup ran the same global
  // strip as ordinary punctuation. Negative-lookbehind/lookahead for digits
  // preserves the decimal/comma/colon when it sits BETWEEN two digits, while
  // still stripping the trailing 'Inc.' / leading ', the' / sentence-end
  // punctuation that the original regex was meant to catch.
  const words = stripped.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  let phrase = (words.length >= 2 ? words.slice(-Math.min(4, words.length)) : [words[0]]).join(' ');
  phrase = phrase
    .replace(/(?<!\d)[,;.:]+|[,;.:]+(?!\d)/g, '')
    .replace(/^(?:the|a|an|of|to|in|for|by|and|or)\s+/i, '')
    .replace(/\s+(?:the|a|an|of|to|in|for|by|and|or)$/i, '')
    .trim();
  return phrase;
}

// ─── Fix #3 (June 2026): Value Label cleanup + duplicate disambiguation ────
// Client-reported issue (MUFG/Revlon credit agreement):
// Value_Label was the raw text snippet immediately before the quant offset,
// surfacing OCR artefacts ("ni nety percent", "Equivale nt of US"), internal
// doc-id stamps ("- 72 - 8033780") and unbounded fragments. Reviewers can't
// use these as semantic labels. This block:
//   1. Detects doc-id labels and nulls them with flag='INVALID_LABEL'.
//   2. Repairs OCR intra-word spaces using a small vocab of number-words and
//      common legal/finance terms PLUS broken-stem regex patterns ("ighty"
//      stands alone only when OCR dropped the leading 'e' from "eighty").
//   3. Normalises hyphen spacing ("eighty - five" → "eighty-five") on
//      alphabetic-only pairs so numeric ranges stay untouched.
//   4. Disambiguates duplicate (Clause, Label) pairs at file level by
//      appending positional brackets — "[2 of 5]".

type LabelFlag = 'INVALID_LABEL' | 'DISAMBIGUATED' | 'NEEDS_REVIEW' | null;

// Document-id stamps embedded in headers/footers of credit agreements that
// leak into the label window when the quant sits near the top/bottom margin.
// Matches both "- 72 - 8033780" / "- 125 - 8033780" and "8033780.16".
const DOC_ID_LABEL_RE = /^-?\s*\d{1,3}\s*-?\s*\d{7,}|^\d{7,}\.\d+$/;

// Number words. Used by the concatenation-repair pass to recognise OCR-split
// pairs like "ni nety" → "ninety" and "n i ne" → "nine".
const NUM_WORDS_FOR_OCR = new Set([
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen', 'twenty', 'thirty', 'forty', 'fifty',
  'sixty', 'seventy', 'eighty', 'ninety',
  'hundred', 'thousand', 'million', 'billion', 'trillion',
  'half', 'quarter', 'third', 'fourth', 'fifth', 'sixth', 'seventh',
  'eighth', 'ninth', 'tenth',
]);

// Legal / finance vocabulary that OCR routinely splits ("con stitute",
// "Equivale nt", "Indebt edness"). Lower-cased; the repair preserves the
// original casing of the first token via preserveCase().
//
// FIX 3 re-audit (2026-06-13): MUFG/Revlon margin-grid rows surface comparison
// words ('greater', 'lesser', 'than') and fractional words ('thirds',
// 'quarters', 'halves') split by OCR as well — 'no grea ter th' on the
// availability-threshold rows, 'thirds percent' on the 66 2/3% rate cells.
// Adding those to vocab + a longer fraction list closes the gaps the client
// flagged on the 2026-06-10 re-audit.
const LEGAL_VOCAB_FOR_OCR = new Set([
  'constitute', 'constitutes', 'constituted', 'constituting',
  'equivalent', 'equivalents',
  'indebtedness',
  'aggregate', 'aggregated', 'aggregating',
  'commitment', 'commitments',
  'consolidated',
  'obligation', 'obligations',
  'permitted',
  'subsidiary', 'subsidiaries',
  'liability', 'liabilities',
  'mortgage', 'mortgaged',
  'principal',
  'amortization', 'amortized',
  'reasonable', 'reasonably',
  'specified',
  'borrowing', 'borrowings',
  'covenant', 'covenants',
  'guaranty', 'guarantee', 'guaranteed',
  'available', 'availability',
  'maturity',
  'leverage',
  'incremental',
  'business',
  'revolving',
  // FIX 3 re-audit 2026-06-13 — comparison + connector words from margin
  // grids ('no greater than', 'less than', 'more than').
  'greater', 'greatest', 'lesser', 'least', 'less', 'more', 'most', 'than',
  // FIX 3 re-audit 2026-06-13 — fractional words from vulgar-fraction
  // percentages ('66 2/3 percent' → 'sixty six and two thirds percent',
  // 'one half percent').
  'thirds', 'halves', 'quarters', 'quarter', 'third', 'half',
  // FIX 3 re-audit 2026-06-13 — date-arithmetic anchors ('Closing Date plus
  // 90 days') and period qualifiers ('notice period', 'cure period') that
  // OCR splits when they straddle a line break.
  'closing', 'notice', 'cure',
  // FIX 3 re-audit 2026-06-13 — tier / level / class words common in
  // capitalisation tables and pricing grids.
  'tier', 'level', 'class', 'category', 'priority',
]);

const OCR_REPAIR_VOCAB = new Set<string>([
  ...Array.from(NUM_WORDS_FOR_OCR),
  ...Array.from(LEGAL_VOCAB_FOR_OCR),
]);

// Stand-alone OCR-broken stems that always represent a known word with the
// leading letter dropped. Whitelisted by client-flagged cases only — broad
// prefix repair would corrupt legitimate prose like "ity" → "city".
//
// FIX 3 re-audit (2026-06-13): the MUFG/Revlon margin grid surfaced 'reater'
// (greater), 'esser' (lesser), 'uarter' (quarter) as stand-alone stems on
// rows whose leading letter was dropped at the column boundary. Each stem
// is ≥5 chars and has only one common-English completion, so the false-
// positive risk is the same low band as the original 5 entries.
const OCR_STEM_REPAIRS: Record<string, string> = {
  'ighty':  'eighty',
  'inety':  'ninety',
  'eventy': 'seventy',
  'ifty':   'fifty',
  'ixty':   'sixty',
  'reater': 'greater',
  'esser':  'lesser',
  'uarter': 'quarter',
};

function preserveCase(original: string, repaired: string): string {
  if (!original || !repaired) return repaired;
  if (original[0] === original[0].toUpperCase()) {
    return repaired[0].toUpperCase() + repaired.slice(1);
  }
  return repaired;
}

function repairOcrSpaces(input: string): string {
  if (!input) return input;
  // Pass A — replace stand-alone broken stems on word boundaries.
  let out = input;
  for (const [broken, repaired] of Object.entries(OCR_STEM_REPAIRS)) {
    const re = new RegExp(`\\b${broken}\\b`, 'gi');
    out = out.replace(re, m => preserveCase(m, repaired));
  }
  // Pass B — concatenation repair: join adjacent tokens that form a known
  // word when fused (e.g., "con stitute" → "constitute"). Split keeps the
  // whitespace separators so re-joining preserves the original spacing on
  // tokens we don't touch. Both tokens must contribute alphabetic content
  // so a bare hyphen ("one - half") or a number ("60 million") doesn't
  // get glued onto a vocab word it doesn't belong to.
  const parts = out.split(/(\s+)/);
  const result: string[] = [];
  let i = 0;
  while (i < parts.length) {
    if (i + 2 < parts.length && /^\s+$/.test(parts[i + 1])) {
      const a = parts[i];
      const b = parts[i + 2];
      const cleanA = a.replace(/[^a-zA-Z]/g, '').toLowerCase();
      const cleanB = b.replace(/[^a-zA-Z]/g, '').toLowerCase();
      if (cleanA.length >= 1 && cleanB.length >= 1) {
        const joinedClean = cleanA + cleanB;
        if (joinedClean.length >= 4 && OCR_REPAIR_VOCAB.has(joinedClean)) {
          result.push(a + b);
          i += 3;
          continue;
        }
      }
    }
    result.push(parts[i]);
    i++;
  }
  return result.join('');
}

// "eighty - five" → "eighty-five". Restricted to alphabetic neighbours so
// numeric ranges ("$50 - 60 million") and trailing "X - 8033780" footer
// stamps stay untouched.
function normalizeHyphenSpacing(s: string): string {
  return s.replace(/([A-Za-z]+)\s+-\s+([A-Za-z]+)/g, '$1-$2');
}

// Trim trailing fragments — open parenthetical that lost its content
// ("(85"), stray operator characters, period-runs.
//
// FIX 3 re-audit (2026-06-13): MUFG/Revlon margin-grid labels surface a
// trailing fragment family the original trim missed:
//   • "(66 2/"      — vulgar-fraction parenthetical that lost its closing
//                     digit. Forward slash wasn't in the char class.
//   • " th"         — dangling 2-letter stem from a "no greater than" cut
//                     mid-word. With 'than' now in vocab the OCR pass
//                     joins it when both halves survive; this catches the
//                     case where the second half ('an') was already dropped.
//   • " plus / to / from / minus / before / after"
//                   — dangling date-arithmetic connectors that leak the
//                     label into a downstream fragment ('Closing Date plus'
//                     then nothing). The label can't be completed without
//                     the trailing operand, so dropping the connector is
//                     safer than carrying the broken phrase forward.
function trimTrailingPartials(s: string): string {
  // FIX 3 re-audit (2026-06-13): iterate to a fixed point so chained tails
  // like 'no greater th plus' fully reduce. Single-pass would strip ' plus'
  // and leave 'th' as the trailing fragment; loop catches the residue.
  let prev = '';
  let cur = s;
  while (prev !== cur) {
    prev = cur;
    cur = cur
      .replace(/\s*\(\s*[\d.,\s/]*$/, '')
      .replace(/\s+(?:plus|minus|to|from|before|after|of|or|and|in|for|with)\s*$/i, '')
      .replace(/\s+th\s*$/i, '')
      .replace(/\s*[—–\-:,;.]+\s*$/, '')
      .trim();
  }
  return cur;
}

// FIX 3 re-audit (2026-06-13): a label that reads as a spelled-out currency
// amount ('Twenty Four Dollars and Twenty Cents') is a VALUE that leaked
// into the label window — never a real label. Pattern: chain of capitalised
// number-words (possibly joined by 'and'/hyphens) terminating in a currency
// unit. Salt Berry showed this labeling a $200M cap, per Ryan. Always flag
// NEEDS_REVIEW so the reviewer doesn't trust the misattribution silently.
const SPELLED_CURRENCY_LABEL_RE = /^\s*(?:(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|million|billion|trillion)[\s-]+){1,8}(?:and\s+)?(?:dollars?|cents?|pounds?|euros?|yen|rupees?|usd|gbp|eur)\b/i;

// FIX 3 re-audit (2026-06-13): after trim of arithmetic connectors
// ('Closing Date plus' → 'Closing Date'), the surviving stem can be a bare
// date anchor on a row whose value is a count or duration ('30 days from
// Closing Date'). The anchor names the REFERENCE point, not the duration
// itself. Surfaced when the caller (quantToValueRecord) knows the value
// type and finds an anchor / non-date mismatch.
const DATE_ANCHOR_LABEL_RE = /\b(?:closing|effective|maturity|termination|expiration|commencement|anniversary|execution|delivery)\s+date\s*$/i;

function isLabelLikelyAnchorMismatch(label: string, valueType: ValueType): boolean {
  if (!label || !valueType) return false;
  if (valueType === 'date') return false; // bare date anchor IS a valid label for a date value
  return DATE_ANCHOR_LABEL_RE.test(label);
}

// FIX 3 re-audit (2026-06-19, Ryan QA): high-confidence sentence-fragment
// detection. The label heuristic still leaks raw text-window fragments past
// the OCR / hyphen / partial-trim passes when the underlying clause text
// runs together without clear sentence boundaries — Ryan flagged examples
// like 'administrator) on the date' (orphan closing paren),
// '2.8 or 2.9 (Pro' (truncated trailing paren), and labels opened by a
// mid-sentence connector ('nor since the date', 'but not later',
// 'but in any event'). These three pattern classes are evidence the span
// is a sentence fragment, not a semantic noun phrase. Tag NEEDS_REVIEW
// (preserve text for the reviewer; do not silently drop) so downstream
// counters and reviewers can route them to manual review instead of
// treating them as clean labels. Patterns are deliberately narrow to
// avoid false-flagging legitimate single-word labels (Affiliate, Loan
// Cap, Construction Loan Maturity Date) — opens that DO start real
// labels ('No', 'In', 'At', 'On', 'For', 'As', 'By') are excluded from
// the connector list.
function looksLikeFragmentLabel(s: string): boolean {
  if (!s) return false;
  // (1) Orphan ')' — more closes than opens means the span started after
  //     the opening bracket of an outer parenthetical.
  const opens  = (s.match(/\(/g) || []).length;
  const closes = (s.match(/\)/g) || []).length;
  if (closes > opens) return true;
  // (2) Trailing unclosed '(' followed by 1-5 letters (mid-word
  //     truncation: '...2.9 (Pro', '...date (not', '...amount (prov').
  if (/\([A-Za-z]{1,5}\s*$/.test(s)) return true;
  // (3) Starts with a mid-sentence connector / subordinating conjunction
  //     that essentially never opens a real defined-term / KDE label.
  if (/^(?:nor|but|or|though|although|because|while|unless|whether|either|neither|since|when|where|if)\s+/i.test(s)) {
    return true;
  }
  return false;
}

function cleanLabel(rawLabel: string): { label: string; flag: LabelFlag } {
  const initial = (rawLabel || '').trim();
  if (!initial) return { label: '', flag: null };

  if (DOC_ID_LABEL_RE.test(initial)) {
    return { label: '', flag: 'INVALID_LABEL' };
  }

  // FIX 3 re-audit 2026-06-13: spelled-out currency amounts captured as
  // labels are never real labels. Surface the verbatim text (so the reviewer
  // sees what the heuristic grabbed) but tag NEEDS_REVIEW so the row never
  // counts as a clean label.
  if (SPELLED_CURRENCY_LABEL_RE.test(initial)) {
    return { label: initial.replace(/\s+/g, ' ').trim(), flag: 'NEEDS_REVIEW' };
  }

  let s = initial.replace(/\s+/g, ' ').trim();
  s = repairOcrSpaces(s);
  s = normalizeHyphenSpacing(s);
  s = trimTrailingPartials(s);

  if (!s) return { label: '', flag: 'INVALID_LABEL' };

  // FIX 3 re-audit (2026-06-19, Ryan QA): high-confidence fragment
  // detection runs AFTER the OCR / hyphen / partial-trim passes so a
  // fragment the cleanup would have rescued isn't pre-flagged.
  if (looksLikeFragmentLabel(s)) {
    return { label: s, flag: 'NEEDS_REVIEW' };
  }

  if (s.length < 10) {
    return { label: s, flag: 'NEEDS_REVIEW' };
  }
  return { label: s, flag: null };
}

// ─── FIX 3 re-audit (2026-06-13): tier-marker synthesis from raw_text ─────
//
// Backend `_table_context_for_offset` only populates row_label / column_label
// when the source clause is a markdown pipe-table. The MUFG/Revlon margin
// grid arrives in the PDF text layer FLATTENED into a single paragraph:
//
//   "...Greater than or equal to sixty-six and two-thirds percent (66 2/3%)
//    of the Aggregate Tranche A Commitments 1.50% 2.50% 2.50% 2 but less
//    than sixty-six and two-thirds percent (66 2/3%) ... 1.75% 2.75% ..."
//
// No pipes → backend row_label = null → tier-aware disambig falls through to
// the ordinal '[N of M]' fallback the client flagged. extractTierMarker
// reads the same window the label heuristic uses and pulls the most
// proximate tier descriptor (parenthetical percent, comparison clause, or
// explicit 'Tier N' / 'Level N' marker) for the disambiguator to consume.
// Pure pattern match — no spec-required vocab; safe for back-compat.
function extractTierMarker(rawText: string, offset: number | undefined): string {
  if (!rawText || offset == null) return '';
  const lookBack = rawText.slice(Math.max(0, offset - 300), offset);

  // Strongest signal: explicit 'Tier N' / 'Level N' / 'Class N' marker.
  // Array.from() instead of iterator spread — project tsconfig lacks the
  // downlevelIteration / es2015 target needed for `[...iterator]` syntax.
  const tierMatches = Array.from(lookBack.matchAll(/\b(?:Tier|Level|Class)\s+([0-9IVX]+)\b/gi));
  if (tierMatches.length > 0) {
    return tierMatches[tierMatches.length - 1][0].trim();
  }

  // Parenthetical pct with optional preceding comparator — captures the
  // descriptive phrase ('Greater than or equal to ... (66 2/3%)') so two
  // adjacent tiers that share the same parenthetical 66 2/3% are still
  // distinguishable by their comparator ('or equal to' vs 'but less than').
  const pctParenRe = /(\b(?:greater|less|equal|but|than|or|to)[^.()]{1,80})?\(\s*(\d{1,3})(?:\s+(\d)\s*\/\s*(\d))?\s*%?\s*\)/gi;
  const matches = Array.from(lookBack.matchAll(pctParenRe));
  if (matches.length > 0) {
    const last = matches[matches.length - 1];
    const ctx = (last[1] || '').replace(/\s+/g, ' ').trim();
    const whole = last[2];
    const num = last[3];
    const den = last[4];
    const pctStr = (num && den) ? `${whole} ${num}/${den}%` : `${whole}%`;
    if (ctx) {
      return `${ctx} ${pctStr}`.replace(/\s+/g, ' ').slice(0, 80);
    }
    return pctStr;
  }

  // Comparator-only clause (no parenthetical) — common in covenants
  // schedule rows that print 'less than fifty percent' but not '(50%)'.
  const cmpMatches = Array.from(lookBack.matchAll(/((?:greater|less)\s+than(?:\s+or\s+equal\s+to)?\s+(?:[a-z]+[-\s]*){1,8}percent)/gi));
  if (cmpMatches.length > 0) {
    return cmpMatches[cmpMatches.length - 1][1].replace(/\s+/g, ' ').trim().slice(0, 80);
  }

  // FIX 3 re-audit (Ryan QA 2026-06-15): leverage-ratio tier descriptor.
  // Margin/applicable-rate grids gate by Total Leverage Ratio:
  //   '> 3.00 to 1.00', '<= 3.00 to 1.00', '< 2.50:1', '>= 4.00x'
  // Previous patterns ONLY matched percent-based thresholds (66 2/3%) and
  // missed ratio-based ones entirely, so §2.4 grid rows fell to the
  // ordinal '[N of M]' fallback. Catches the comparator + ratio shape
  // and returns it as the tier descriptor.
  const leverageMatches = Array.from(lookBack.matchAll(
    /([><]=?\s*\d+\.?\d*\s*(?:to\s+1\.?0*|:\s*1|x))/gi
  ));
  if (leverageMatches.length > 0) {
    return leverageMatches[leverageMatches.length - 1][1].replace(/\s+/g, ' ').trim().slice(0, 80);
  }

  return '';
}

// ─── FIX 3 re-audit (Ryan QA 2026-06-15): margin-grid column synthesis ──
//
// Backend `_table_context_for_offset` only emits column_label for pipe-
// table sources. The §2.4 Applicable Rate margin grid (and equivalents
// in MUFG/Revlon, Salt Berry) arrive in the PDF text layer FLATTENED to
// a single paragraph:
//
//   "Total Leverage Ratio Term SOFR Margin Base Rate Margin Revolver SOFR
//    Revolver Base Rate > 3.00 to 1.00 2.50% 1.50% 2.75% 1.75%"
//
// No pipes → backend column_label = null → 11 grid rows all label as
// adjacent-cell context ('3.00 to 1.00 2.50%', '1.00 2.50% 1.50%', …)
// and downstream Loan IQ booking cannot tell which rate is Term SOFR vs
// Base Rate. extractColumnMarker reads the same 500-char lookback and
// returns the column the value sits under, deciding by position-within-
// the-data-row when multiple headers precede the offset. Pure pattern
// match against a known-headers list — safe for back-compat.

const MARGIN_GRID_COLUMN_HEADERS: ReadonlyArray<string> = [
  // Loan-pricing columns Ryan's §2.4 QA test targets verbatim.
  'Revolver Base Rate Margin',
  'Revolver Base Rate',
  'Revolver SOFR Margin',
  'Revolver SOFR',
  'Term SOFR Margin',
  'Term SOFR Spread',
  'Term SOFR',
  'Base Rate Margin',
  'Base Rate Spread',
  'Base Rate',
  'ABR Margin',
  'ABR Spread',
  'Eurocurrency Margin',
  'Eurocurrency Spread',
  'Eurodollar Margin',
  'Eurodollar Spread',
  'Applicable Margin',
  // Fee-grid columns observed in the same audit set.
  'Commitment Fee Rate',
  'Commitment Fee',
  'Unused Line Fee',
  'LC Fee Rate',
  'LC Fee',
  'LC Issuance Fee',
];

// Order longest→shortest so 'Term SOFR Margin' wins over plain 'Term SOFR'
// when both could match the same span.
const _COLUMN_HEADERS_SORTED = [...MARGIN_GRID_COLUMN_HEADERS]
  .sort((a, b) => b.length - a.length);

function _findHeaderMatches(text: string): Array<{ name: string; pos: number; end: number }> {
  const claimed: Array<{ start: number; end: number }> = [];
  const matches: Array<{ name: string; pos: number; end: number }> = [];
  for (const header of _COLUMN_HEADERS_SORTED) {
    const re = new RegExp('\\b' + header.replace(/\s+/g, '\\s+') + '\\b', 'gi');
    let m;
    while ((m = re.exec(text)) !== null) {
      const start = m.index;
      const end = m.index + m[0].length;
      // Skip if this span overlaps a previously-claimed (longer) header.
      if (claimed.some(c => !(end <= c.start || start >= c.end))) continue;
      claimed.push({ start, end });
      matches.push({ name: m[0].replace(/\s+/g, ' ').trim(), pos: start, end });
    }
  }
  return matches.sort((a, b) => a.pos - b.pos);
}

// Group consecutive header hits into clusters separated by gaps > 30 chars.
// The actual TABLE HEADER ROW prints columns adjacent ('Term SOFR Margin
// Base Rate Margin Revolver SOFR Margin' — single-space gaps in a
// flattened PDF), while singleton hits in the surrounding prose
// ('the Applicable Margin for each Loan…' in §2.4's intro, or the section-
// level 'The Applicable Margin shall be the applicable rate per annum…'
// preamble) sit alone with multi-word gaps. The threshold has to fit
// inside a single label width (~20–25 chars) so that a singleton intro
// mention can never accidentally merge with the real header row.
function _largestHeaderCluster(
  headers: Array<{ name: string; pos: number; end: number }>,
): Array<{ name: string; pos: number; end: number }> {
  if (headers.length === 0) return [];
  const clusters: Array<Array<{ name: string; pos: number; end: number }>> = [[headers[0]]];
  for (let i = 1; i < headers.length; i++) {
    const prev = headers[i - 1];
    const curr = headers[i];
    if (curr.pos - prev.end <= 30) {
      clusters[clusters.length - 1].push(curr);
    } else {
      clusters.push([curr]);
    }
  }
  return clusters.reduce((best, c) => (c.length > best.length ? c : best), [] as typeof headers);
}

function extractColumnMarker(rawText: string, offset: number | undefined): string {
  if (!rawText || offset == null) return '';
  const lookBack = rawText.slice(Math.max(0, offset - 500), offset);
  const allHeaders = _findHeaderMatches(lookBack);
  if (allHeaders.length === 0) return '';

  // Filter to the actual table-header cluster (rejects 'Applicable Margin'
  // mentions sprinkled through the intro paragraph that aren't column names).
  const tableHeaders = _largestHeaderCluster(allHeaders);
  if (tableHeaders.length === 0) return '';

  // Locate the LAST tier descriptor in the lookback. Numeric values AFTER
  // the tier descriptor map slot→column against the table header cluster.
  const TIER_RE = /([><]=?\s*\d+\.?\d*\s*(?:to\s+1\.?0*|:\s*1|x))/gi;
  const tierMatches = Array.from(lookBack.matchAll(TIER_RE));
  if (tierMatches.length === 0) {
    return tableHeaders[tableHeaders.length - 1].name;
  }
  const lastTier = tierMatches[tierMatches.length - 1];
  const tierEnd = (lastTier.index ?? 0) + lastTier[0].length;

  // Count numeric (% / $ / decimal) cell-values between the tier descriptor
  // and the current value offset — that slot index picks the column.
  const between = lookBack.slice(tierEnd);
  const cellRe = /(?:\$\s*[\d,]+(?:\.\d+)?|\d+\.?\d*\s*%|\d+\.\d+|\d{1,3}(?:,\d{3})+(?:\.\d+)?)/g;
  const cellMatches = between.match(cellRe) || [];
  const slot = cellMatches.length;
  const safeIndex = Math.min(slot, tableHeaders.length - 1);
  return tableHeaders[safeIndex].name;
}

// Returns true when the current label looks like adjacent-cell contamination
// from a margin/pricing grid (chains of percent values, raw leverage ratios,
// or values without semantic words). Used to GATE the rewrite — clean labels
// like 'aggregate Term A' or 'Permitted Indebtedness' MUST NOT be touched.
function _labelLooksLikeGridContamination(label: string): boolean {
  if (!label) return false;
  const trimmed = label.trim();
  if (trimmed.length < 5) return false;
  const percentMatches = trimmed.match(/\d+\.?\d*\s*%/g) || [];
  const hasLeverageRatio = /[><]=?\s*\d+\.?\d*\s*(?:to\s+1\.?0*|:\s*1|x)/i.test(trimmed);
  const wordRatio = (trimmed.match(/[a-zA-Z]{3,}/g) || []).length;
  // Multiple percents → almost certainly adjacent cells.
  if (percentMatches.length >= 2) return true;
  // Leverage ratio + few real words → grid row context, not a real label.
  if (hasLeverageRatio && wordRatio <= 3) return true;
  // Bare percent label with no descriptive words.
  if (percentMatches.length === 1 && wordRatio === 0) return true;
  return false;
}

// Generic margin-grid header words. When the cleaned label is one of these
// AND a tier+column are both detectable in the surrounding text, the row
// sits inside a grid where the cell-level disambiguation needs to ride on
// the label itself. Ryan's MUFG/Revlon §1.1 audit showed 65 rows all
// labeled 'Applicable Margin' — same label across every tier-and-column
// cell in the margin grid, leaving the reviewer unable to distinguish
// 2.50% (Tier 1 SOFR) from 1.50% (Tier 1 Base Rate). Conservative gate:
// only fires when BOTH tier AND column are synthesized, so a definition
// clause that legitimately reads "Applicable Margin means ..." is safe.
const _GRID_GENERIC_LABELS: ReadonlySet<string> = new Set([
  'applicable margin',
  'applicable rate',
  'applicable percentage',
  'applicable margin percentage',
  'applicable unused line fee percentage',
  'applicable unused line fee',
  'applicable commitment fee',
  'applicable lc fee',
]);

function _labelIsGridGenericHeader(label: string): boolean {
  if (!label) return false;
  return _GRID_GENERIC_LABELS.has(label.trim().toLowerCase());
}

// Build a semantic label from the synthesized tier + column when the
// extracted label is contaminated OR a known grid-generic header. Returns
// null when the inputs don't justify a rewrite — caller must keep the
// original label.
function refineMarginGridLabel(
  currentLabel: string,
  synthesizedRow: string,
  synthesizedCol: string,
): string | null {
  if (!synthesizedRow && !synthesizedCol) return null;
  const isContaminated = _labelLooksLikeGridContamination(currentLabel);
  const isGridGeneric = _labelIsGridGenericHeader(currentLabel);
  // Grid-generic labels need BOTH tier AND column synthesized — without
  // both, the rewrite would pad noise onto a definition that legitimately
  // says "Applicable Margin means ...". Contaminated labels already carry
  // no signal, so one of the two synthesized markers is enough.
  if (isGridGeneric && (!synthesizedRow || !synthesizedCol)) return null;
  if (!isContaminated && !isGridGeneric) return null;
  // For grid-generic labels, preserve the original header word as the
  // prefix so 'Applicable Unused Line Fee' doesn't collapse to a generic
  // 'Applicable Margin' family. For contaminated labels, use the canonical
  // 'Applicable Margin' family prefix per Ryan's spec.
  const prefix = isGridGeneric ? currentLabel.trim() : 'Applicable Margin';
  const parts = [prefix];
  if (synthesizedRow) parts.push(`Tier (${synthesizedRow})`);
  if (synthesizedCol) parts.push(synthesizedCol);
  if (parts.length < 2) return null;
  return parts.join(' — ');
}

// Sentence-level context around the quant offset, capped to 200 chars and
// trimmed to the nearest sentence boundary in either direction. Stored on
// the record purely for downstream debug / audit and surfaced in the Excel
// export's Context Snippet column.
function extractContextSnippet(text: string, offset: number | undefined): string {
  if (!text || offset == null) return '';
  const start = Math.max(0, offset - 120);
  const end = Math.min(text.length, offset + 120);
  let snippet = text.slice(start, end);
  // Trim to sentence boundaries on either side when present.
  const dotBefore = snippet.lastIndexOf('. ', offset - start);
  if (dotBefore > 0 && dotBefore < (offset - start) - 5) {
    snippet = snippet.slice(dotBefore + 2);
  }
  const dotAfter = snippet.indexOf('. ');
  if (dotAfter > 30 && dotAfter < snippet.length - 1) {
    snippet = snippet.slice(0, dotAfter + 1);
  }
  return snippet.replace(/\s+/g, ' ').trim().slice(0, 200);
}

// File-level disambiguation: mutate labels of duplicate (clause_no, label)
// pairs so each row's label uniquely identifies its row. Appends an
// "[N of M]" bracket — the spec-defined fallback that covers every case
// without needing surrounding-text heuristics. Records are mutated in
// place; label_flag is tagged 'DISAMBIGUATED' on every affected row that
// didn't already carry a flag.
function disambiguateLabelsInPlace(records: ValueRecord[]): void {
  const groups = new Map<string, number[]>();
  records.forEach((r, idx) => {
    if (!r.label) return;
    const key = `${r.src}${r.label.toLowerCase()}`;
    const list = groups.get(key) || [];
    list.push(idx);
    groups.set(key, list);
  });
  groups.forEach(indices => {
    if (indices.length < 2) return;

    // FIX 3 re-audit (2026-06-13): the original implementation only emitted
    // the ordinal fallback "[N of M]" — useless for a reviewer trying to
    // read the Applicable Margin grid (6 rows labeled '[1 of 6]'…'[6 of 6]'
    // tell you nothing about which availability tier 3.25% belongs to).
    // Backend already extracts row_label + column_label from pipe-table
    // context; prefer those over the ordinal when they uniquely separate
    // the duplicate group.
    // FIX 3 re-audit (2026-06-13): fall back to the synthesized tier marker
    // when the backend left row_label empty (PDF flattened the visual grid).
    // Backend label always wins — synthesized only fills the gap so tables
    // that DID emit pipe context keep their canonical labels.
    const rows = indices.map(i =>
      (records[i].row_label || records[i].synthesized_row_label || '').trim()
    );
    const cols = indices.map(i => (records[i].column_label || '').trim());
    const allHaveRow = rows.every(r => r.length > 0);
    const allHaveCol = cols.every(c => c.length > 0);
    const rowsDistinct = new Set(rows).size === indices.length;
    const colsDistinct = new Set(cols).size === indices.length;
    // FIX 3 re-audit (2026-06-13): "varies" = has ≥2 unique values. Used to
    // suppress the combined-tuple path when one axis is uniform — adding a
    // constant 'Floor' prefix to every cell in a 1×N grid is redundant noise.
    const rowVaries = allHaveRow && new Set(rows).size > 1;
    const colVaries = allHaveCol && new Set(cols).size > 1;
    // FIX 3 re-audit (2026-06-13): on a 3×2 margin grid (3 availability
    // tiers × Term SOFR + Base Rate) neither axis is alone distinct across
    // the 6-row group, but the (row, col) PAIR uniquely identifies each
    // cell. Check the combined-tuple distinctness BEFORE the single-axis
    // paths so the most descriptive suffix wins for grid layouts.
    const combos = indices.map((_, n) => `${rows[n]}${cols[n]}`);
    const combosDistinct = allHaveRow && allHaveCol && rowVaries && colVaries &&
      new Set(combos).size === indices.length;

    // Combined-tuple path: every cell uniquely identified by row+col PAIR.
    if (combosDistinct) {
      indices.forEach((idx, n) => {
        const rec = records[idx];
        rec.label = `${rec.label} [${rows[n]} — ${cols[n]}]`;
        if (!rec.label_flag) rec.label_flag = 'DISAMBIGUATED';
      });
      return;
    }

    // Tier-aware path: row_label distinct → use as suffix, enriched by
    // column_label when that axis is also distinct.
    if (allHaveRow && rowsDistinct) {
      indices.forEach((idx, n) => {
        const rec = records[idx];
        const colSuffix = (allHaveCol && colsDistinct) ? ` — ${cols[n]}` : '';
        rec.label = `${rec.label} [${rows[n]}${colSuffix}]`;
        if (!rec.label_flag) rec.label_flag = 'DISAMBIGUATED';
      });
      return;
    }

    // Column-only path: row_label collides but column_label separates
    // (axis-flipped tables like capitalisation schedules).
    if (allHaveCol && colsDistinct) {
      indices.forEach((idx, n) => {
        const rec = records[idx];
        rec.label = `${rec.label} [${cols[n]}]`;
        if (!rec.label_flag) rec.label_flag = 'DISAMBIGUATED';
      });
      return;
    }

    // FIX 3 re-audit (2026-06-13): tier-with-slot path. Every row has a
    // tier marker (synthesized from raw_text) but adjacent cells share the
    // same tier — MUFG/Revlon margin grid prints 2-3 rate values per tier
    // on the same flattened line. Group by tier, append a slot index
    // within each tier so the suffix carries BOTH the tier description
    // and the cell's position inside it ('[≥66 2/3% — slot 1]'). Runs AFTER
    // the column-only path so axis-flipped tables (uniform row, distinct
    // col) still prefer the descriptive column suffix over a slot index.
    if (allHaveRow && !rowsDistinct) {
      const tierIndices = new Map<string, number[]>();
      rows.forEach((r, n) => {
        const list = tierIndices.get(r) || [];
        list.push(n);
        tierIndices.set(r, list);
      });
      indices.forEach((idx, n) => {
        const rec = records[idx];
        const tier = rows[n];
        const slotList = tierIndices.get(tier) || [];
        const slot = slotList.indexOf(n) + 1;
        const slotSuffix = slotList.length > 1 ? ` — slot ${slot}` : '';
        rec.label = `${rec.label} [${tier}${slotSuffix}]`;
        if (!rec.label_flag) rec.label_flag = 'DISAMBIGUATED';
      });
      return;
    }

    // Ordinal fallback — covers free-text duplicates with no table context.
    indices.forEach((idx, n) => {
      const rec = records[idx];
      rec.label = `${rec.label} [${n + 1} of ${indices.length}]`;
      if (!rec.label_flag) rec.label_flag = 'DISAMBIGUATED';
    });
  });
}


// ─── Client FIX 16 (June 2026): Malformed-Source Cross-Reference Resolver ──
//
// A malformed currency token ('$325,00,000') has two plausible intended
// values (straight parse = $32.5M; pad-short-group = $325M). The previous
// pipeline silently picked the straight parse and labeled it CORRECTED /
// MAGNITUDE_ERROR — both of which read as "we fixed this" to a reviewer.
// FIX 16 spec: NEVER a single silent pick. Either:
//   (a) exactly one candidate is corroborated by a non-malformed currency
//       value elsewhere in the same document → resolve to that value with
//       Display Flag = RESOLVED_FROM_CROSS_REFERENCE and a citation note
//   (b) zero or both candidates are corroborated → keep BOTH and mark
//       Display Flag = AMBIGUOUS_SOURCE_VALUE. Amount is set to null so
//       the row never silently loads with the wrong value.
//
// Corroboration uses absolute-dollar tolerance ($1 or 1e-6 relative). The
// non-malformed corpus is built once across the whole record set so the
// $325,000,000 occurrence in §1.1 (Aggregate Tranche A Commitments) and
// Schedule C-1 corroborates the '$325,00,000' typo in the
// North American/UK Loan Limit definition, even though they live in
// different clauses.
//
// Runs in place — mutates record.malformed_resolution, resolved_amount,
// resolution_note on each malformed record. Non-malformed records are
// untouched. Idempotent: calling twice on the same array is a no-op.
function resolveMalformedInPlace(records: ValueRecord[]): void {
  // Build the non-malformed currency-value corpus once.
  const corpus: number[] = [];
  for (const r of records) {
    if (r.type !== 'currency') continue;
    if (r.malformed) continue;
    const n = parseDisplayToNumber(r.value, 'currency');
    if (n != null && Number.isFinite(n)) corpus.push(n);
  }

  const corroborated = (target: number): boolean => {
    for (const v of corpus) {
      if (Math.abs(v - target) < 1) return true;
      const denom = Math.max(Math.abs(target), 1);
      if (Math.abs(v - target) / denom < 1e-6) return true;
    }
    return false;
  };

  for (const r of records) {
    if (!r.malformed || !r.candidates || r.candidates.length < 2) continue;
    const corro = r.candidates.filter(c => corroborated(c));
    if (corro.length === 1) {
      r.malformed_resolution = 'RESOLVED';
      r.resolved_amount = corro[0];
      r.resolution_note =
        `Resolved from cross-reference: $${corro[0].toLocaleString('en-US')} ` +
        `appears elsewhere in the document for a related term.`;
    } else {
      r.malformed_resolution = 'AMBIGUOUS';
      r.resolved_amount = null;
      r.resolution_note =
        `Source token malformed — both candidates plausible (` +
        `${r.candidates.map(c => '$' + c.toLocaleString('en-US')).join(' OR ')}` +
        `). No single value corroborated in the document.`;
    }
  }
}


// ─── Fix #5 (June 2026): Zero-Value Parse Failures ────────────────────────
// Client-reported issue: PDF extraction truncates a parenthetical confirming
// number (e.g., "twenty percent (20%)" → "(2") so the backend can't recover
// the decimal and emits Amount=0. Fix #5 reads the Context_Snippet (Fix #3)
// — which preserves a wider window — and rebuilds the percentage from the
// surviving spelled-out phrase + parenthetical. Genuine zeros (e.g.,
// "shall be deemed to be $0") are detected and tagged ZERO_CONFIRMED so
// they're not "corrected" away.

type ParseFlag = 'REEXTRACTED' | 'ZERO_CONFIRMED' | 'PARSE_FAILED' | null;

// Spelled-out percentage word → decimal mapping. Values are the DECIMAL form
// (0.20 = 20%, 0.85 = 85%) so they slot directly into the Excel Amount cell
// which uses the '0.00%' format that multiplies by 100 on display.
const WORD_TO_PCT: Record<string, number> = {
  one:     0.01, two:    0.02, three:   0.03, four:    0.04, five:    0.05,
  six:     0.06, seven:  0.07, eight:   0.08, nine:    0.09, ten:     0.10,
  eleven:  0.11, twelve: 0.12, fifteen: 0.15, twenty:  0.20, thirty:  0.30,
  forty:   0.40, fifty:  0.50, sixty:   0.60, seventy: 0.70, eighty:  0.80,
  ninety:  0.90,
};

const SPELLED_NUMBER_TRIGGER_RE = /\b(?:one|two|three|four|five|six|seven|eight|nine|ten|fifteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|half|quarter|third)\b/i;

// Convert a spelled-out percentage phrase to a decimal.
// Handles single words ("twenty" → 0.20), tens+ones compounds
// ("eighty-five" → 0.85), hundred forms ("one hundred ten" → 1.10), and the
// small fractional forms ("one-half percent" → 0.005, "one-quarter" → 0.0025).
function spelledToDecimal(words: string): number | null {
  if (!words) return null;
  const norm = words.toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
  if (!norm) return null;

  // Fractional small forms
  const bareFraction = spelledFractionValue(norm);
  if (bareFraction != null) return Math.round((bareFraction / 100) * 1e4) / 1e4;

  // Domingo QA fix (2026-09-15): "<whole> and one-half/quarter/third" compound
  // forms — the standard legal-drafting way to write an X.5 / X.25 / X.33
  // percentage ("twelve and one-half percent (12.5%)", "two and one-half
  // percent (2.5%)"). Without this, the phrase-matching regex below skipped
  // the "<whole> and" lead-in (it isn't a hyphen/space-chained compound) and
  // matched only the trailing "one-half" fragment, which this function then
  // resolved to the WRONG value (0.5%) — silently overwriting a correctly
  // parsed 12.5% / 2.5% via the REEXTRACTED cross-check below.
  const andFracMatch = norm.match(/^(.+?)\s+and\s+(.+)$/);
  if (andFracMatch) {
    const wholeDecimal = spelledToDecimal(andFracMatch[1]);
    const fraction = spelledFractionValue(andFracMatch[2]);
    if (wholeDecimal != null && fraction != null) {
      return Math.round((wholeDecimal + fraction / 100) * 1e4) / 1e4;
    }
  }

  // Hundred forms: "one hundred" = 1.00, "one hundred X" = 1.X
  if (norm === 'one hundred' || norm === 'hundred') return 1.00;
  const hundredCompound = norm.match(/^(?:one\s+)?hundred\s+(\w+)$/);
  if (hundredCompound && WORD_TO_PCT[hundredCompound[1]] != null) {
    return 1.00 + WORD_TO_PCT[hundredCompound[1]];
  }

  // Tens + ones compound: "eighty-five" / "twenty-five" / "sixty-five".
  // Math.round(x * 1e4) / 1e4 strips IEEE-754 drift like 0.85000…01 → 0.85;
  // four decimals comfortably covers every spelled-out percentage in use
  // (half-percent is the finest grain at 0.005).
  // The separator is optional: this PDF renders "sixty-six" as "sixtysix"
  // wherever the hyphen was dropped, and the tens word is unambiguous.
  const compound = norm.match(/^(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)\s*(\w+)$/);
  if (compound) {
    const tens = WORD_TO_PCT[compound[1]];
    const ones = WORD_TO_PCT[compound[2]];
    if (tens != null && ones != null) return Math.round((tens + ones) * 1e4) / 1e4;
  }

  // Single word
  if (WORD_TO_PCT[norm] != null) return WORD_TO_PCT[norm];

  return null;
}

// Tolerance for "spelled-out word agrees with parenthetical digit" cross-check.
// Whole-percent forms (twenty / eighty-five) round within ±0.01 (1 percentage
// point). Sub-percent fractional forms (one-half, one-quarter) need a tighter
// window so 0.005 stays distinct from 0.01.
function pctConsistencyTolerance(expected: number): number {
  return expected < 0.01 ? 0.003 : 0.01;
}

// Format a decimal back to the canonical "<n>%" display the Excel column uses.
// Trims trailing zeros after the decimal so 0.20 → "20%" not "20.00%".
function formatPctValue(decimal: number): string {
  return `${(decimal * 100).toFixed(2).replace(/\.?0+$/, '')}%`;
}

// Find the FIRST spelled-out percentage phrase ("twenty", "eighty-five",
// "one hundred ten") that precedes the word "percent" in `text`. Returns the
// phrase itself (not the decimal) so callers can both resolve it and quote it
// in audit notes. Used by the consistency cross-check (Bug B fix).
// Trailing (?:\s+and\s+...)? tail added in the Domingo QA fix (2026-09-15) so
// "twelve and one-half percent" captures the FULL phrase instead of
// truncating to the trailing "one-half" fragment (see spelledToDecimal).
const SPELLED_PCT_PHRASE_SOURCE =
  '\\b((?:(?:twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?:one|two|three|four|five|six|seven|eight|nine)|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|(?:one|two|three)(?:half|quarter|quarters|third|thirds)|half|halves|quarter|quarters|third|thirds)'
  + '(?:[-\\s](?:(?:twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?:one|two|three|four|five|six|seven|eight|nine)|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|(?:one|two|three)(?:half|quarter|quarters|third|thirds)|half|halves|quarter|quarters|third|thirds))*'
  + '(?:\\s+and\\s+(?:one[-\\s]|two[-\\s]|three[-\\s]|a\\s+)?(?:(?:one|two|three)(?:half|quarter|quarters|third|thirds)|half|halves|quarter|quarters|third|thirds))?)'
  + '\\s+percent\\b';

/** The phrase alone. */
const SPELLED_OUT_PCT_PHRASE_RE = new RegExp(SPELLED_PCT_PHRASE_SOURCE, 'i');

/** The phrase plus an optional confirming parenthetical. */
const SPELLED_PCT_WITH_DIGIT_SOURCE =
  SPELLED_PCT_PHRASE_SOURCE + '\\s*\\(?\\s*(\\d+(?:\\.\\d+)?)?\\s*%?\\s*\\)?';


function findSpelledOutPctPhrase(text: string): string | null {
  if (!text) return null;
  const m = text.match(SPELLED_OUT_PCT_PHRASE_RE);
  return m ? m[1] : null;
}

// Domingo QA fix (2026-09-15): the word/numeral disagreement check below
// only makes sense when a digit actually sits next to the spelled-out word
// ("twenty percent (2 0.0%)" — the digit is the corrupted half of THIS
// value). A bare spelled phrase with no adjacent digit gave no evidence the
// row's own value was corrupted at all. Require the pairing before trusting
// the word. (The actual Domingo case — "twelve and one-half percent
// (12.5%)" overwritten to 0.5% — turned out to have a paired digit; the
// real bug there was the phrase capture truncating to "one-half" and
// resolving via the wrong value. Fixed in spelledToDecimal / the phrase
// regex's "and half/quarter/third" tail. This pairing requirement is a
// second, independent hardening: without any adjacent digit at all there
// is no disagreement to resolve in the first place.)
function findSpelledOutPctPhraseWithDigit(
  text: string,
): { spelled: string; numericStr: string | null } | null {
  if (!text) return null;
  const re = new RegExp(SPELLED_PCT_WITH_DIGIT_SOURCE, 'i');
  const m = text.match(re);
  if (!m) return null;
  return { spelled: m[1], numericStr: m[2] || null };
}

// Scan the context for a spelled-out percentage phrase, optionally followed
// by a confirming parenthetical. Prefers the parenthetical number when it
// AGREES with the spelled-out phrase (parenthetical IS the source-of-truth in
// legal drafting — "twenty percent (20%)" means exactly 20%). When the
// parenthetical disagrees with the spelled-out word — the corrupted-digit case
// Domingo flagged: "twenty percent (2 0.0%)" → digit "2" parses to 2%, but
// "twenty" demands 20% — TRUST THE SPELLED-OUT WORD. Spec rule:
// "REEXTRACTED never coexists with a word/number inconsistency."
function recoverPercentageFromContext(context: string): {
  decimal: number;
  rawPercent: string;
} | null {
  if (!context) return null;
  const re = new RegExp(SPELLED_PCT_WITH_DIGIT_SOURCE, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(context)) !== null) {
    const spelled = match[1];
    const numericStr = match[2];
    const fromSpelled = spelledToDecimal(spelled);
    if (numericStr) {
      const num = parseFloat(numericStr);
      if (isFinite(num)) {
        const numDecimal = num / 100;
        // Cross-check: when both spelled-out and parenthetical resolve,
        // they must agree within tolerance. If they don't, the parenthetical
        // is a corrupted token (FIX 14 root cause) and the spelled-out word
        // is the trusted source.
        if (fromSpelled != null) {
          const tol = pctConsistencyTolerance(fromSpelled);
          if (Math.abs(fromSpelled - numDecimal) > tol) {
            return { decimal: fromSpelled, rawPercent: formatPctValue(fromSpelled) };
          }
        }
        return { decimal: numDecimal, rawPercent: `${numericStr}%` };
      }
    }
    if (fromSpelled != null) {
      return { decimal: fromSpelled, rawPercent: formatPctValue(fromSpelled) };
    }
  }
  return null;
}

// Genuine-zero confirmation pattern — used when the parser landed on 0 and
// the surrounding text genuinely says so ("shall be deemed to be $0").
const ZERO_CONFIRMED_RE = /\b(?:deemed\s+to\s+be|shall\s+be|is)\s+(?:equal\s+to\s+)?(?:\$\s*0\b|zero\b)/i;

export function validateZeroAmount(
  record: ValueRecord,
  initialNum: number | null,
  context: string,
): {
  amount: number | null;
  value?: string;
  parseFlag: ParseFlag;
  amountConfirmed: boolean;
} {
  // ── Branch 1: non-zero percent rows — word/numeral consistency check.
  // Catches the "(1 10 %)" / "(10 3 %)" / "(2 0.0%)" cases Domingo flagged
  // where the parenthetical digit got truncated/split before our code sees
  // it. The parsed amount sneaks through as a wrong but non-zero value
  // (0.01 / 0.10 / 0.02) and silently lands in the export with NO Parse
  // Flag. Cross-check against the spelled-out word in the label and, when
  // they disagree, prefer the word. Spec rule: "REEXTRACTED never coexists
  // with a word/number inconsistency."
  //
  // Domingo QA fix (2026-09-15): a margin/pricing grid lists several tiers
  // close together ("...sixty-six and two-thirds percent (66 2/3%)...
  // thirty-three and one-third percent (33 1/3%)..."), so a row's `context`
  // snippet can legitimately contain ANOTHER row's spelled-phrase+digit
  // pair, not just this row's own. All 9 REEXTRACTED overrides found in the
  // live QA sample were false positives of exactly this shape — every one
  // of them had a clean, correctly-formed vulgar-fraction source value
  // ("66 2/3%", "33 1/3%") with nothing to rescue, so skip this whole
  // cross-check for them: there's no ambiguity for it to resolve, only a risk
  // of borrowing a neighboring row's tier.
  //
  // 2026-09-24: the skip read one shape while numericOf() parsed three, so the
  // OCR forms this grid actually ships — "(33 13%)", "(66 2 3%)" — fell
  // through and were flagged anyway. Both sides read vulgarFractionPercent.ts
  // now, and a row already sitting on a third is held back on its value too.
  const isVulgarFraction = isVulgarFractionPercent(record.value)
    || isCleanThirdPercent(initialNum);
  if (
    initialNum != null
    && initialNum !== 0
    && record.type === 'percent'
    && !isVulgarFraction
  ) {
    const labelText = (record.label || '').toLowerCase();
    if (SPELLED_NUMBER_TRIGGER_RE.test(labelText)) {
      const fromLabel = findSpelledOutPctPhraseWithDigit(labelText);
      const pair = fromLabel || findSpelledOutPctPhraseWithDigit(context || '');
      if (pair && pair.numericStr) {
        const expected = spelledToDecimal(pair.spelled);
        if (expected != null) {
          const tol = pctConsistencyTolerance(expected);
          if (Math.abs(expected - initialNum) > tol) {
            // REEXTRACTED means "we rebuilt this number, go and check it".
            // When the phrase came from the row's own label there is nothing
            // to check: the label states the percentage and the value now
            // matches it. A margin grid labels every tier, so its rows were
            // carrying the flag for being tiers. A phrase borrowed from the
            // surrounding context is a different matter and still flags.
            return {
              amount: expected,
              value: formatPctValue(expected),
              parseFlag: fromLabel ? null : 'REEXTRACTED',
              amountConfirmed: !!fromLabel,
            };
          }
        }
      }
    }
    return { amount: initialNum, parseFlag: null, amountConfirmed: false };
  }

  // ── Branch 2: pure pass-through for non-percent non-zero rows.
  if (initialNum !== 0) {
    return { amount: initialNum, parseFlag: null, amountConfirmed: false };
  }

  // ── Branch 3: initialNum === 0 — original zero-handling logic.
  // Genuine-zero check FIRST — applies regardless of type. A row with a
  // "deemed to be $0" sentence stays 0 and is tagged ZERO_CONFIRMED so
  // future passes don't try to "fix" it.
  if (context && ZERO_CONFIRMED_RE.test(context)) {
    return { amount: 0, parseFlag: 'ZERO_CONFIRMED', amountConfirmed: true };
  }

  // Re-extraction only applies to percent type — the bug only manifests
  // through truncated "(<digits>%)" parentheticals attached to a spelled-
  // out word, and currency / count / date rows don't share that pattern.
  if (record.type !== 'percent') {
    return { amount: initialNum, parseFlag: null, amountConfirmed: false };
  }

  // The label must contain a spelled-out number word for this to be a
  // re-extraction candidate. Otherwise a legitimate "0%" row would get
  // rewritten just because some other percentage appears in the context.
  const labelText = (record.label || '').toLowerCase();
  if (!SPELLED_NUMBER_TRIGGER_RE.test(labelText)) {
    return { amount: initialNum, parseFlag: null, amountConfirmed: false };
  }

  const recovered = recoverPercentageFromContext(context);
  if (recovered) {
    // REEXTRACTED means "we rebuilt this number and you should check it". When
    // the row's own label states the same percentage the context recovered,
    // there is nothing left to check: the two independent readings agree. A
    // margin grid labels every tier, so its rows were carrying the flag purely
    // for being tiers.
    const fromLabel = findSpelledOutPctPhraseWithDigit(labelText);
    const labelDecimal = fromLabel ? spelledToDecimal(fromLabel.spelled) : null;
    const confirmedByLabel = labelDecimal != null
      && Math.abs(labelDecimal - recovered.decimal) <= pctConsistencyTolerance(labelDecimal);
    if (confirmedByLabel) {
      return {
        amount: recovered.decimal,
        value: recovered.rawPercent,
        parseFlag: null,
        amountConfirmed: true,
      };
    }
    return {
      amount: recovered.decimal,
      value: recovered.rawPercent,
      parseFlag: 'REEXTRACTED',
      amountConfirmed: false,
    };
  }

  return { amount: 0, parseFlag: 'PARSE_FAILED', amountConfirmed: false };
}


// ─── Fix #4 (June 2026): Plain English Description column ──────────────────
// The backend's contractAnalysisService already attaches an AI-generated
// `plain_english` summary to every clause via Claude. Fix #4 surfaces that
// summary as a Description column on every value row in the Excel export and
// flags rows whose generated description fails the spec's quality bar
// (doc-id artefact, hedging language, < 20 words). When multiple values
// share a clause they share the same description — per-value uniqueness
// would require fresh per-quant Claude calls (out of scope for this fix).

type DescriptionSource = 'AI_Generated' | 'Manual' | 'AI_Reviewed' | 'NEEDS_REVIEW' | 'Skipped';

// Doc-id stamps embedded in headers/footers that occasionally leak into
// upstream AI summaries.
const DOC_ID_DESC_RE = /-?\s*\d{1,3}\s*-?\s*\d{7,}|\b\d{7,}\.\d+\b/g;

// Conservative hedging detector — flags only PHRASES that almost always
// signal speculation. Plain "may" is intentionally NOT in the list because
// legal text routinely uses it as a permission verb ("the Borrower may
// request"); flagging every such clause would make the signal useless.
const HEDGING_RE = /\b(?:possibly|perhaps|unclear|presumably|allegedly|seems?\s+to|appears?\s+to|might\s+be|may\s+govern|may\s+relate\s+to)\b/i;

const DESC_MIN_WORDS = 20;

function extractDescription(clause: ContractClause): {
  description: string;
  source: DescriptionSource;
  flag: boolean;
} {
  // Domingo QA fix (2026-09-15): "Skip plain-English summaries" was
  // deliberately leaving plain_english as the placeholder '— summary
  // skipped —'. That's non-empty text, so it fell through into the
  // word-count/hedging quality gate below and every row failed it (< 20
  // words) — turning "user asked to skip this" into "every row is
  // Flagged". A deliberate skip isn't a quality failure; don't flag it.
  if (clause.summary_skipped) {
    return { description: '— summary skipped —', source: 'Skipped', flag: false };
  }

  const raw = (clause.plain_english || '').trim();
  if (!raw) {
    return { description: '', source: 'NEEDS_REVIEW', flag: true };
  }

  // Strip doc-id stamps inline so a "- 70 - 8033780" never reaches the
  // Description column. Collapse whitespace introduced by the strip.
  const cleaned = raw
    .replace(DOC_ID_DESC_RE, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) {
    return { description: '', source: 'NEEDS_REVIEW', flag: true };
  }

  const wordCount = cleaned.split(/\s+/).filter(Boolean).length;
  const hasHedging = HEDGING_RE.test(cleaned);
  const tooShort = wordCount < DESC_MIN_WORDS;

  return {
    description: cleaned,
    source: 'AI_Generated',
    flag: hasHedging || tooShort,
  };
}

// Fix #4 re-audit (2026-06-13): per-VALUE description path. Prefers the
// per-quant Haiku description the backend now writes onto quant.value_description
// (see backend/src/services/contractAnalysisService.js::describeAllValues).
// Falls through to the original clause-level extractDescription when the
// per-value field is missing — happens on cached pre-fix payloads, on
// quants the backend failed to describe (Haiku error → empty string), and
// on docs uploaded before the backend deploy. All quality gates from
// extractDescription apply identically so the Description Flag column
// stays consistent across both code paths.
function extractValueDescription(quant: ContractQuant, clause: ContractClause): {
  description: string;
  source: DescriptionSource;
  flag: boolean;
} {
  const raw = (quant.value_description || '').trim();
  if (!raw) {
    return extractDescription(clause);
  }
  const cleaned = raw
    .replace(DOC_ID_DESC_RE, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) {
    return extractDescription(clause);
  }
  const wordCount = cleaned.split(/\s+/).filter(Boolean).length;
  const hasHedging = HEDGING_RE.test(cleaned);
  const tooShort = wordCount < DESC_MIN_WORDS;
  return {
    description: cleaned,
    source: 'AI_Generated',
    flag: hasHedging || tooShort,
  };
}


function quantToValueRecord(
  quant: ContractQuant,
  clause: ContractClause,
  quantIdx: number,
  fileIdx: number,
  clauseIdx: number,
): ValueRecord {
  const type = mapKindToType(quant.kind, quant.value);
  const { sym, op } = inferOperator(clause.raw_text || '', quant.offset, type);
  const rawLabel = inferLabel(clause.raw_text || '', quant.offset, quant, type);
  const { label: cleanedLabel, flag: cleanFlag } = cleanLabel(rawLabel);
  // FIX 3 re-audit (2026-06-13): after the spell-currency + trim passes,
  // surviving labels can still be a bare date anchor ('Closing Date') on a
  // row whose value is a count or duration — the original heuristic grabbed
  // the REFERENCE point instead of the delta noun. Tag NEEDS_REVIEW so the
  // anchor never reads as the canonical label. Preserves any earlier flag
  // (INVALID_LABEL stays terminal; explicit NEEDS_REVIEW already on the row
  // stays NEEDS_REVIEW).
  const labelFlagAfterClean = (cleanFlag == null && isLabelLikelyAnchorMismatch(cleanedLabel, type))
    ? 'NEEDS_REVIEW' as LabelFlag
    : cleanFlag;

  // FIX 3 re-audit (Ryan QA 2026-06-15): when the extracted label is
  // adjacent-cell contamination from a margin/pricing grid, replace it
  // with a semantic 'Applicable Margin — Tier (X) — <Column>' label
  // synthesized from the raw_text window. Skips clean labels and only
  // runs when both a tier descriptor AND a column header are detectable
  // — so the rewrite cannot fabricate context that isn't in the source.
  // INVALID_LABEL stays terminal; the rewrite never resurrects a doc-id row.
  const synthesizedRowMarker = extractTierMarker(clause.raw_text || '', quant.offset);
  const synthesizedColMarker = extractColumnMarker(clause.raw_text || '', quant.offset);
  const refinedGridLabel = (labelFlagAfterClean !== 'INVALID_LABEL')
    ? refineMarginGridLabel(cleanedLabel, synthesizedRowMarker, synthesizedColMarker)
    : null;
  const finalLabel = refinedGridLabel ?? cleanedLabel;
  const labelFlag: LabelFlag = refinedGridLabel
    ? 'DISAMBIGUATED'
    : labelFlagAfterClean;
  const contextSnippet = extractContextSnippet(clause.raw_text || '', quant.offset);
  const unit = inferUnit(type, quant.value, quant.kind, quant.currency);
  // Fix #2: Source = "§<clause_no> — <section heading>" when the TOC resolved
  // a title for this clause; bare §clause_no otherwise. Format is consistent
  // across all rows so reviewers can scan a column of mixed sources without
  // wondering which entries lost their heading.
  const headingForSrc = resolvedClauseTitle(clause);
  const src = headingForSrc
    ? `§${clause.clause_no} — ${headingForSrc}`
    : `§${clause.clause_no}`;
  return {
    id: `f${fileIdx}-c${clauseIdx}-q${quantIdx}`,
    type,
    label: finalLabel,
    op,
    sym,
    value: quant.value,
    unit,
    src,
    page: clause.page ?? null,
    phrase: quant.value,
    offset: quant.offset,
    row_label: quant.row_label,
    // FIX 12 re-test (2026-06-22): carry backend-captured currency through
    // to the Excel-export detectCurrency path (same single source of truth
    // the UI inferUnit already uses).
    currency: quant.currency,
    // FIX 3 re-audit (2026-06-13): carry column_label so the file-level
    // disambiguator can use it as a tier suffix instead of "[N of M]".
    column_label: quant.column_label,
    // FIX 3 re-audit (2026-06-13): when backend left row_label null (PDF
    // text layer flattened the visual grid before pipe-table reassembly
    // could fire), synthesize a tier marker from the raw_text window
    // around quant.offset. Only populated as fallback — pipe-table-tagged
    // rows keep their canonical backend row_label.
    synthesized_row_label: (!quant.row_label && clause.raw_text)
      ? extractTierMarker(clause.raw_text, quant.offset) || undefined
      : undefined,
    ocr_corrected: quant.ocr_corrected,
    original_value: quant.original_value,
    resolved_from: quant.resolved_from,
    definition_compound: quant.definition_compound,
    definition_alternative: quant.definition_alternative,
    definition_compound_modifier: quant.definition_compound_modifier,
    range_low: quant.range_low,
    context_snippet: contextSnippet,
    label_flag: labelFlag,
    // FIX 14: carry the backend reassembled flag so validateDisplayValue
    // emits PARSE_UNCERTAIN (never SPACING_NORMALIZED) on rescued rows.
    reassembled: quant.reassembled,
    // FIX 16: carry the malformation signal and the two candidate values
    // through to resolveMalformedInPlace, which runs across the full
    // record set before the export loop reads malformed_resolution.
    malformed: quant.malformed,
    candidates: quant.candidates,
    // FIX 22: per-project sublimit attribution. A sublimit quant carries its
    // own facility tag (an Exhibit J table row belongs to a specific facility,
    // not to the exhibit clause as a whole), so surface it on the record.
    project_entity: quant.project_entity ?? null,
    facility_name: quant.facility_name ?? null,
    is_sublimit: quant.is_sublimit ?? false,
  };
}


// ─── Numeric parse helper for Excel Amount cells ────────────────────────────

const CURRENCY_MAGNITUDE: Record<string, number> = {
  k: 1e3, thousand: 1e3,
  m: 1e6, mm: 1e6, mn: 1e6, million: 1e6,
  b: 1e9, bn: 1e9, billion: 1e9,
};

export function numericOf(record: { type: ValueType; value: string }): { num: number | null; fmt: string | null } {
  const raw = record.value || '';
  if (record.type === 'currency') {
    // Domingo QA fix (2026-09-15): stripping everything but digits also
    // stripped the magnitude suffix ("~$146mm" -> "146" instead of
    // 146,000,000; "$250k" -> "250" instead of 250,000). Source text was
    // captured correctly — only this normalization step lost the scale.
    // Detect a trailing k/m/mm/bn/thousand/million/billion suffix on the
    // digit run and apply the multiplier before falling back to the
    // plain digit-strip (which still covers already-expanded values like
    // "$146,000,000" with no suffix present).
    const magMatch = raw.match(
      /([\d,]+(?:\.\d+)?)\s*(thousand|million|billion|mm|mn|bn|k|m|b)\b/i,
    );
    if (magMatch) {
      const base = parseFloat(magMatch[1].replace(/,/g, ''));
      const mult = CURRENCY_MAGNITUDE[magMatch[2].toLowerCase()];
      if (Number.isFinite(base) && mult) {
        return { num: base * mult, fmt: '$#,##0' };
      }
    }
    const n = parseFloat(raw.replace(/[^\d.\-]/g, ''));
    return Number.isFinite(n) ? { num: n, fmt: '$#,##0' } : { num: null, fmt: null };
  }
  if (record.type === 'percent') {
    // FIX 11: detect vulgar-fraction forms BEFORE stripping all non-digit chars.
    // Stripping "66 2/3%" → "662/3" → "6623" gives 6623/100=66.23 (wrong: off
    // by 100×). Same corruption for slash-dropped "(66 23%)" and slash-as-space
    // "(66 2 3%)" forms. Guard num < den prevents false positives like "(80 33%)".
    const fraction = parseVulgarFractionPercent(raw);
    if (fraction != null) {
      return { num: fraction, fmt: '0.00%' };
    }
    const n = parseFloat(raw.replace(/[^\d.\-]/g, ''));
    return Number.isFinite(n) ? { num: n / 100, fmt: '0.00%' } : { num: null, fmt: null };
  }
  if (record.type === 'ratio') {
    const m = raw.match(/([\d.]+)\s*(?::|to)\s*[\d.]+/);
    if (m) {
      const n = parseFloat(m[1]);
      return Number.isFinite(n) ? { num: n, fmt: '0.00" : 1.00"' } : { num: null, fmt: null };
    }
    return { num: null, fmt: null };
  }
  if (record.type === 'duration') {
    const m = raw.match(/(\d+)/);
    if (m) {
      const n = parseFloat(m[1]);
      return Number.isFinite(n) ? { num: n, fmt: '0' } : { num: null, fmt: null };
    }
  }
  return { num: null, fmt: null };
}


// ─── Fix #6 (June 2026): Display Value formatting & cross-validation ────────
// Client audit surfaced Excel rows where the Amount column and the Display
// Value column disagreed visually — e.g. Amount=32,500,000 but Display
// '$ 325,00,000', which a reviewer might misread as $325M. Fix #6 emits a
// canonical Display Value from Amount + Type + Unit and flags rows where
// the document-extracted display disagrees with Amount:
//
//   • CORRECTED         — display has formatting noise but parses to a
//                         value within 5× of Amount (typo, dropped digit,
//                         malformed trailing comma)
//   • MAGNITUDE_ERROR   — display parses or visually misreads to a value
//                         >5× off Amount; the prime symptom is misplaced
//                         INTERNAL commas (e.g. '325,00,000' looks like
//                         $325M not $32.5M)
//   • SPACING_NORMALIZED — value matches AND spacing-only normalization left
//                         the parsed magnitude IDENTICAL ('$ 50,000,000' →
//                         '$50,000,000', '65.08  %' → '65.08%'). Per Ryan's
//                         MUFG/Revlon re-audit 2026-06-10 correction #2, this
//                         flag must be reserved for cosmetic-only fixes —
//                         if normalization changes the parsed magnitude, the
//                         row falls through to PARSE_UNCERTAIN instead (FIX 14
//                         rule: "a flag that fires on corruption must never
//                         imply the value is safe").
//   • null              — display agrees with canonical, no change needed
//
// Date / Ratio / Count rows pass through unchanged — they have no canonical
// numeric form to validate against. Duration is canonicalised as
// "<amount> <unit>".

// Client FIX 14 (June 2026) added two flags that take priority over the
// existing Fix #6 set:
//   • PARSE_UNCERTAIN   — backend reassembled the value from PDF-injected
//                          intra-token whitespace. Magnitude was rescued
//                          but the source text was corrupted, so the
//                          reviewer must verify against the document. Wins
//                          over SPACING_NORMALIZED (per client: "a flag
//                          that fires on corruption must never imply the
//                          value is safe").
//   • MAGNITUDE_SUSPECT — magnitude sanity bound tripped: currency below
//                          $1,000 in a Provision context, or percentage
//                          above 100 outside an explicit collateralisation
//                          context. The value MAY be correct (102% / 105%
//                          collateralisation does exist) but warrants
//                          eyeballing.
// Client FIX 16 (June 2026) adds two flags for malformed source numbers:
//   • AMBIGUOUS_SOURCE_VALUE       — comma grouping breaks US 1k convention
//                                    AND no single candidate is corroborated
//                                    elsewhere in the document. Amount=null,
//                                    Display Value shows BOTH candidates with
//                                    "— source malformed" suffix. Never a
//                                    silent single pick.
//   • RESOLVED_FROM_CROSS_REFERENCE — exactly one of the two candidates
//                                    matches a non-malformed value elsewhere
//                                    in the document. Amount = resolved
//                                    candidate, Display Value formatted from
//                                    that value, with citation in the
//                                    resolution_note.
// Per spec: a 'CORRECTED' label on a wrong resolution is the worst output
// class — these flags REPLACE that path for malformed currency tokens.
type DisplayFlag =
  | 'CORRECTED'
  | 'MAGNITUDE_ERROR'
  | 'MAGNITUDE_SUSPECT'
  | 'PARSE_UNCERTAIN'
  | 'SPACING_NORMALIZED'
  | 'AMBIGUOUS_SOURCE_VALUE'
  | 'RESOLVED_FROM_CROSS_REFERENCE'
  | null;

function formatDisplay(amount: number, type: ValueType, unit: string): string {
  if (amount == null || !Number.isFinite(amount)) return '';
  if (type === 'currency') {
    const n = Math.round(amount).toLocaleString('en-US');
    // Fix #12 (June 2026): EUR/GBP/AUD/CHF moved from single-char symbol to
    // ISO prefix form ("EUR 100,000") so the Display Value column matches
    // the Unit column at a glance — spec acceptance "Display Value as EUR
    // 100,000 — not USD in any column". USD keeps $ and JPY keeps ¥ because
    // both glyphs are universally unambiguous; CAD already used the prefix
    // form (from Fix #6). UNKNOWN renders unitless so a reviewer never sees
    // a confidently-wrong symbol on an unresolvable currency.
    if (unit === 'USD')     return `$${n}`;
    if (unit === 'EUR')     return `EUR ${n}`;
    if (unit === 'GBP')     return `GBP ${n}`;
    if (unit === 'CAD')     return `CAD ${n}`;
    if (unit === 'AUD')     return `AUD ${n}`;
    if (unit === 'CHF')     return `CHF ${n}`;
    if (unit === 'JPY')     return `¥${n}`;
    if (unit === 'UNKNOWN') return n;
    return `${unit} ${n}`;
  }
  if (type === 'percent') {
    // toFixed(4) covers every percentage in real use (smallest = 0.005 / half
    // a percent). Trim trailing zeros and dangling decimal so 0.9 → "90%",
    // 0.00375 → "0.375%".
    let s = (amount * 100).toFixed(4);
    s = s.replace(/0+$/, '').replace(/\.$/, '');
    return `${s}%`;
  }
  if (type === 'duration') {
    return `${amount} ${unit || 'days'}`.trim();
  }
  return '';
}

// Strip currency symbols / spaces / commas → float. Percent values are
// returned in DECIMAL form (65.08% → 0.6508) so they can be compared
// directly to record.amount which is stored decimal-side.
function parseDisplayToNumber(rawDisplay: string, type: ValueType): number | null {
  if (!rawDisplay) return null;
  // Duration and other non-currency/percent types: match the FIRST digit run
  // directly. Avoids mis-parsing spelled-out hyphenated numbers like
  // 'seventy-two (72) hours' or 'ninety-five (95) days' as negatives
  // (the hyphen between letters is a word-boundary, not a sign).
  if (type !== 'currency' && type !== 'percent') {
    const m = rawDisplay.match(/\d+(?:\.\d+)?/);
    if (!m) return null;
    const n = parseFloat(m[0]);
    return Number.isFinite(n) ? n : null;
  }
  const cleaned = rawDisplay.replace(/[^\d.\-,]/g, '').replace(/,/g, '');
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return null;
  if (type === 'percent') return n / 100;
  return n;
}

// Inspect a currency display for malformed comma placement. US convention:
// the first group is 1-3 digits, every subsequent group is exactly 3. A
// malformed MIDDLE group (e.g. '325,00,000' → groups [3,2,3]) shifts the
// visual scale by an order of magnitude — that's MAGNITUDE_ERROR. A
// malformed TRAILING group only ('40,000,00' → [2,3,2]) reads as a typo
// in the lowest digits — that's CORRECTED.
function detectCommaIssue(rawDisplay: string): 'CORRECTED' | 'MAGNITUDE_ERROR' | null {
  const stripped = rawDisplay.replace(/[^\d.,]/g, '');
  if (!stripped.includes(',')) return null;
  const intPart = stripped.split('.')[0];
  const groups = intPart.split(',');
  if (groups[0].length < 1 || groups[0].length > 3) return 'CORRECTED';
  for (let i = 1; i < groups.length; i++) {
    if (groups[i].length !== 3) {
      if (i < groups.length - 1) return 'MAGNITUDE_ERROR';
      return 'CORRECTED';
    }
  }
  return null;
}

function normalizeDisplaySpacing(raw: string): string {
  return raw
    .replace(/^(\s*)([\$€£¥])\s+/, '$1$2')   // strip space after leading $/€/£/¥
    .replace(/\s{2,}/g, ' ')                  // collapse double+ spaces
    .replace(/\s+%/g, '%')                    // strip space before %
    .trim();
}

// Client FIX 14 sanity bounds. Used by validateDisplayValue below.
//
//   currency < $1,000 in a credit-agreement Provision body → MAGNITUDE_SUSPECT
//     (legit small values exist — administrative fees, per-unit charges — but
//      the audit-surfaced symptom of decimal-split corruption is sub-$100
//      figures like $37 / $50 / $20,000 standing in for $37M / $50M / $20M)
//
//   percent > 100 OUTSIDE a collateralisation context → MAGNITUDE_SUSPECT
//     (102%/103%/105%/110% are genuine collateralisation requirements; a
//      stray 875% is the F-01 footprint and must never load silently)
const _COLLATERALISATION_RE = /\b(cash\s+collateral(?:i[sz]ed?|isation|ization)?|over[\s-]?collateral(?:i[sz]ed?|isation|ization)?|collateral\s+coverage|secured\s+at|posted\s+collateral|hedge\s+notional)\b/i;

function detectMagnitudeSuspect(
  type: ValueType,
  amount: number,
  context: string,
): boolean {
  if (type === 'currency') {
    // Threshold tuned to the audit: $37 / $50 / $20,000 are the smallest
    // historical footprints. $1,000 is high enough to catch them and low
    // enough to let real small-dollar admin fees through.
    return amount > 0 && amount < 1000;
  }
  if (type === 'percent') {
    // amount stored as decimal (0.85 = 85%, 1.03 = 103%). Trip >100% only
    // when the surrounding context isn't a collateralisation clause where
    // 102%/103%/105%/110% are normal.
    return amount > 1.0 && !_COLLATERALISATION_RE.test(context || '');
  }
  return false;
}

function validateDisplayValue(
  record: { type: ValueType; unit: string; reassembled?: boolean },
  amount: number | null,
  rawDisplay: string,
  contextSnippet: string = '',
): { displayValue: string; rawDisplay: string; displayFlag: DisplayFlag } {
  const safeRaw = rawDisplay || '';

  // Non-numeric types — no canonical form to validate against. Pass through
  // and let downstream reviewers eyeball them.
  if (record.type === 'date' || record.type === 'ratio' || record.type === 'count') {
    return { displayValue: safeRaw, rawDisplay: safeRaw, displayFlag: null };
  }
  if (amount == null || !Number.isFinite(amount)) {
    return { displayValue: safeRaw, rawDisplay: safeRaw, displayFlag: null };
  }

  const canonical = formatDisplay(amount, record.type, record.unit);
  if (!canonical) {
    return { displayValue: safeRaw, rawDisplay: safeRaw, displayFlag: null };
  }
  if (!safeRaw.trim()) {
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: null };
  }

  // Client FIX 14: reassembled values ALWAYS surface as PARSE_UNCERTAIN —
  // never the comfort flag SPACING_NORMALIZED. Per spec: "a flag that fires
  // on corruption must never imply the value is safe." Runs BEFORE
  // comma-malformation / spacing / value-cross-validation so a row whose
  // magnitude was rescued from corrupted PDF whitespace can't fall through
  // to a 'we cleaned this' badge.
  if (record.reassembled) {
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'PARSE_UNCERTAIN' };
  }

  // 1. Comma malformation (currency only) — runs FIRST because parsing
  // collapses commas and would hide misplaced-comma magnitude misreads.
  if (record.type === 'currency') {
    const commaFlag = detectCommaIssue(safeRaw);
    if (commaFlag === 'MAGNITUDE_ERROR') {
      return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'MAGNITUDE_ERROR' };
    }
    if (commaFlag === 'CORRECTED') {
      return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'CORRECTED' };
    }
  }

  // 2. Value cross-validation — parseable but numerically off?
  const parsed = parseDisplayToNumber(safeRaw, record.type);
  if (parsed == null) {
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'CORRECTED' };
  }
  const relErr = Math.abs(parsed - amount) / Math.max(Math.abs(amount), 1);
  if (relErr > 5) {
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'MAGNITUDE_ERROR' };
  }
  if (relErr > 0.01) {
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'CORRECTED' };
  }

  // FIX 14 (June 2026): magnitude-sanity bound — currency below $1,000 in a
  // Provision context, or percent above 100% outside a collateralisation
  // context, surfaces MAGNITUDE_SUSPECT. Runs AFTER the relative-error checks
  // (so a CORRECTED row keeps its more-specific flag) and BEFORE the spacing
  // check (so a clean row that's also suspect doesn't get a comfort flag).
  if (detectMagnitudeSuspect(record.type, amount, contextSnippet)) {
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'MAGNITUDE_SUSPECT' };
  }

  // 3. Value matches — check spacing.
  //
  // Ryan's MUFG/Revlon re-audit 2026-06-10 correction #2: SPACING_NORMALIZED
  // must be reserved for PURE cosmetic fixes that leave the parsed magnitude
  // identical. Earlier the flag fired on any string-level difference, which
  // meant a hypothetical aggressive normalization that dropped a digit would
  // still ship as "we cleaned this" — exactly the comfort flag that lets a
  // reviewer wave through a wrong value. Per FIX 14: re-parse the normalized
  // string and require an exact-magnitude match (≤1% relative error) before
  // emitting SPACING_NORMALIZED; otherwise surface PARSE_UNCERTAIN so the
  // reviewer is forced back to the source.
  const normalized = normalizeDisplaySpacing(safeRaw);
  if (normalized !== safeRaw) {
    const parsedAfterNormalization = parseDisplayToNumber(normalized, record.type);
    const magnitudePreserved =
      parsedAfterNormalization != null &&
      Math.abs(parsedAfterNormalization - parsed) / Math.max(Math.abs(parsed), 1) <= 0.01;
    if (!magnitudePreserved) {
      return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'PARSE_UNCERTAIN' };
    }
    return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: 'SPACING_NORMALIZED' };
  }

  // 4. Display is clean — emit canonical for output consistency, no flag.
  // The canonical may still differ trivially from raw (unit symbol vs code,
  // rounding); per spec 2e we always re-format and only flag when the user
  // would actually be surprised by the change.
  return { displayValue: canonical, rawDisplay: safeRaw, displayFlag: null };
}
// ─── END Fix #6 ─────────────────────────────────────────────────────────────


// ─── Fix #7 (June 2026): Canonical Operator Vocabulary ─────────────────────
// Client audit found 548/700 rows had Operator OR Symbol but not both, and
// the same directional constraint appeared in 4+ text variants ('at least',
// 'not less than', 'not fewer than', 'minimum'). Fix #7 collapses every
// operator expression to 5 canonical values so DB queries are reliable,
// drops the redundant Symbol column from Excel output, and preserves the
// original extracted text in Operator_Raw for audit.
//
//   • Operator column → always one of {=, >=, <=, >, <}
//   • Operator_Raw    → original human-readable text ('at least', 'as of', …)
//   • Operator_Flag   → UNRECOGNIZED_OPERATOR when extracted text isn't in
//                        the trigger table (defaulted to '=' for safety)
//   • Symbol column   → REMOVED from Excel; UI chip still renders the
//                        unicode glyph, derived dynamically from the
//                        canonical operator.
//
// UI display is intentionally UNTOUCHED — record.op (human text) and
// record.sym (unicode) continue to drive the OperatorChip exactly as before.
// Canonicalisation runs ONLY at Excel-export time, so the inline edit form,
// hover state, and badge chips have zero risk of regression.

type CanonicalOperator = '=' | '>=' | '<=' | '>' | '<';
type OperatorFlag = 'UNRECOGNIZED_OPERATOR' | null;

// Trigger phrases keyed by canonical operator. Kept as a literal config so
// onboarding a new phrase is a 1-line change — adding "minimum required"
// or similar variants doesn't touch the lookup logic.
const OPERATOR_TRIGGERS: Record<CanonicalOperator, string[]> = {
  '>=': [
    'at least', 'not less than', 'not fewer than', 'no less than',
    'minimum of', 'minimum', 'at minimum',
    'no lower than', 'not lower than',
    'equal to or greater than', 'greater than or equal to',
    'not below', 'on or after',
  ],
  '<=': [
    'not to exceed', 'not more than', 'no more than', 'not in excess of',
    'up to', 'at most',
    'maximum of', 'maximum', 'at maximum',
    'no greater than', 'no higher than', 'not greater than',
    'less than or equal to', 'not above',
    'no later than', 'on or before',
  ],
  '>':  ['exceeding', 'in excess of', 'greater than', 'more than', 'over'],
  '<':  ['less than', 'fewer than', 'below', 'under'],
  '=':  [
    'equal to', 'exactly',
    'on', 'as of', 'dated', 'effective', 'commencing', 'starting',
    'approximately',
  ],
};

// Inverse lookup built ONCE at module load — not inside normalizeOperator
// (would rebuild per call, hot-path waste). Keys are pre-lowercased.
const OPERATOR_LOOKUP: Map<string, CanonicalOperator> = (() => {
  const m = new Map<string, CanonicalOperator>();
  (Object.entries(OPERATOR_TRIGGERS) as Array<[CanonicalOperator, string[]]>).forEach(
    ([canonical, triggers]) => {
      triggers.forEach(t => m.set(t.toLowerCase(), canonical));
    },
  );
  return m;
})();

function normalizeOperator(rawText: string | undefined | null): {
  op_canonical: CanonicalOperator;
  op_flag: OperatorFlag;
} {
  // Spec default: "no operator text found" → '=', no flag. The unflagged
  // default is what 152/700 currently-correct rows look like and we must
  // not retroactively flag them.
  if (!rawText || !rawText.trim()) {
    return { op_canonical: '=', op_flag: null };
  }
  const norm = rawText
    .toLowerCase()
    .replace(/[.,;:!?()'"]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const canonical = OPERATOR_LOOKUP.get(norm);
  if (canonical) return { op_canonical: canonical, op_flag: null };
  // Unrecognized — default to '=' and emit a flag so a reviewer can audit
  // the row. Per spec the bare '=' is safer than dropping the row.
  return { op_canonical: '=', op_flag: 'UNRECOGNIZED_OPERATOR' };
}

// Derive the unicode glyph from a canonical operator. Symbol column is gone
// from output, but UI still surfaces a glyph in the chip — this is the
// single source of truth for that mapping. Exported so the module API
// keeps the mapping available for future re-introduction of the Symbol
// chip without re-deriving the glyph table from scratch.
export function symbolFromOperator(op: CanonicalOperator | string): string {
  switch (op) {
    case '>=': return '≥';
    case '<=': return '≤';
    case '>':  return '>';
    case '<':  return '<';
    case '=':  return '=';
    default:   return '';
  }
}
// ─── END Fix #7 ─────────────────────────────────────────────────────────────


// ─── Fix #10 (June 2026): PDF Structural Artifact Filtering ────────────────
// Client audit found §1.9 (Exchange Rates) showed captured value
// "# = 142-8033780" with "16 issuanc" typed as a date — these are PDF
// FOOTER artifacts (page number + document reference number stamped at the
// bottom of every page), NOT contractual values. Fix #10 detects and drops
// these before they reach the UI or Excel export.
//
// Three orthogonal detection signals (any one triggers a drop):
//
//   • DIRECT VALUE PATTERN — quant value matches doc-ID shape:
//       '8033780.16'  → /^\d{7,}\.\d{1,3}$/
//       '142-8033780' → /^\d{1,4}-\d{7,}$/
//
//   • LABEL PATTERN — quant.row_label / column_label contains a doc-ID
//     stamp (7+ digit number, optionally with sub-page suffix).
//
//   • POSITIONAL — quant.offset falls inside a footer region. A footer
//     region is detected by scanning raw_text for the canonical pattern
//     "-<page>-" followed by "<doc-id>.<sub-page>" within a short window.
//     This catches the "16 issuanc" case where '16' (from doc-id .16)
//     was extracted as a bare date — the value itself is too generic to
//     filter by pattern alone, but its OFFSET sits inside the footer span.
//
// Hard safety net: currency / percentage quants are NEVER dropped, even
// if their digits superficially look like a doc-ID (e.g. $12,345,678.90).
// The Python quant extractor has already classified `kind` reliably; we
// trust that and never second-guess a tagged $ or % value.

const DOC_ID_VALUE_RE     = /^\d{7,}\.\d{1,3}$/;
const PAGE_DOCID_COMBO_RE = /^\d{1,4}-\d{7,}$/;
const DOC_ID_LABEL_FRAG_RE = /\b\d{7,}(?:\.\d+)?\b/;
// Footer span: "-<page>-" immediately followed by "<doc-id>.<sub-page>",
// separated only by whitespace and any number of "[PAGE N]" markers the
// PDF parser may have stamped in. The original ≤40-char relaxation between
// the two halves let greedy regex match the FURTHER doc-id within a
// multi-footer clause and skip the closer one — tight separator coupling
// is both correct for the canonical MUFG footer shape and safer for
// multi-page clauses.
const FOOTER_SPAN_RE      = /-\d{1,4}-\s*(?:\[PAGE\s+\d+\]\s*)*\d{6,}\.\d{1,3}/g;

function findFooterRanges(rawText: string): Array<[number, number]> {
  if (!rawText) return [];
  const ranges: Array<[number, number]> = [];
  const re = new RegExp(FOOTER_SPAN_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(rawText)) !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    // Defensive: prevent infinite loop on zero-width match
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return ranges;
}

function isFooterArtifactQuant(
  quant: ContractQuant,
  footerRanges: Array<[number, number]>,
): boolean {
  if (!quant || !quant.value) return false;

  // Currency / percentage are NEVER footer artifacts — the extractor's
  // kind classification is authoritative. A $12,345,678.90 row will have
  // kind='currency' even though stripped digits superficially resemble
  // a doc-ID.
  if (quant.kind === 'currency' || quant.kind === 'percentage') return false;

  const v = quant.value.trim();
  // Real money/percent symbols anywhere in the value → never a footer.
  if (/[$£€¥%]/.test(v)) return false;

  // Strip whitespace + commas for shape comparison ($12,345 → 12345).
  const stripped = v.replace(/[\s,]/g, '');

  if (DOC_ID_VALUE_RE.test(stripped))     return true;
  if (PAGE_DOCID_COMBO_RE.test(stripped)) return true;

  if (quant.row_label    && DOC_ID_LABEL_FRAG_RE.test(quant.row_label))    return true;
  if (quant.column_label && DOC_ID_LABEL_FRAG_RE.test(quant.column_label)) return true;

  if (quant.offset != null && footerRanges.length > 0) {
    for (const [s, e] of footerRanges) {
      if (quant.offset >= s && quant.offset < e) return true;
    }
  }

  return false;
}

// Single source of truth for "the meaningful quants of this clause" —
// used everywhere `clause.quants` was previously read. Computes footer
// ranges per call (cheap on per-clause raw_text; the regex is anchored to
// short patterns so this is O(n) over the text length).
function usefulQuants(clause: ContractClause): ContractQuant[] {
  const quants = clause.quants || [];
  if (quants.length === 0) return quants;
  const footerRanges = findFooterRanges(clause.raw_text || '');
  return quants.filter(q => !isFooterArtifactQuant(q, footerRanges));
}
// ─── END Fix #10 ────────────────────────────────────────────────────────────


// ─── Fix #11 (June 2026): Data Model Expansion ─────────────────────────────
// Adds 5 controlled-vocab fields (Clause Type, Defined Term Name, Jurisdiction,
// Value Category, Effective-vs-Headline) so credit-agreement portfolios can be
// searched / compared across deals. Rule-based throughout — no AI calls.
//
//   • Clause Type:      Definition | Operative | Interpretive | Boilerplate
//                       | Schedule/Exhibit  (one per CLAUSE — repeats per row)
//   • Defined Term:     non-null only on Definition rows
//   • Jurisdiction:     US | EU | UK | Dutch | Swiss | German | Quebec |
//                       Australian | Multi | N/A  (one per CLAUSE)
//   • Value Category:   Cap | Floor | Threshold | Rate | Fee | Duration |
//                       Count | Ratio | Reference  (one per VALUE)
//   • Effective vs Headline: boolean + optional note (carve-out signal)
//
// Runs ONLY at Excel-export time. UI render path is untouched — no risk of
// surfacing wrong classifications in the workspace.

type ClauseType    = 'Definition' | 'Operative' | 'Interpretive' | 'Boilerplate' | 'Schedule/Exhibit';
type Jurisdiction  = string; // ISO 3166-1 alpha-2 + subdivision: US-NY, US-DE, GB, DE, NL, CH, AU, CA-QC, EU, Multi, N/A
type ValueCategory = 'Cap' | 'Floor' | 'Threshold' | 'Rate' | 'Fee' | 'Duration' | 'Count' | 'Ratio' | 'Reference';

function classifyClauseType(clause: ContractClause): ClauseType {
  // 1. Backend-tagged schedule/exhibit wins — already filtered upstream but
  //    we still emit the right Clause Type when the row IS in scope.
  if (clause.content_type === 'schedule' || clause.content_type === 'exhibit') {
    return 'Schedule/Exhibit';
  }
  // 2. Definition: explicit backend tag, or section 1.x with a quoted-term
  //    "means …" body. Restrict the "means" heuristic to clauses whose number
  //    starts with "1." so an operative clause that quotes a definition
  //    elsewhere ("the term 'Borrower' means …") isn't mis-classified.
  if (clause.content_type === 'definition') return 'Definition';
  const text  = (clause.raw_text || '').slice(0, 600);
  const title = (clause.clause_title || '').toLowerCase();
  const no    = clause.clause_no || '';
  // Client FIX 9 re-audit v2 (June 2026): extended to French guillemets,
  // optional-comma, and 'is defined as / to mean / to be' verb forms so
  // the §1.x classifier mirrors the atomization regex one-to-one. Without
  // this, an entry like '«Term» is defined as …' would atomize but get
  // classified as Operative, then Defined Term Name would stay empty.
  if (no.startsWith('1.') && /["“„«][^"”»\n]{2,80}["”»]\s*,?\s*(?:shall\s+)?(?:means?|(?:has|have|having)\s+the\s+meanings?|is\s+defined\s+(?:as|to\s+(?:mean|be)))\b/i.test(text)) {
    return 'Definition';
  }
  // 2b. Fix #11.1 (June 2026): title mentions "Definitions" or "Defined
  // Terms" → Definition. Closes the audit gap where §1.2 "OCR-Affected
  // Definitions" was tagged Operative because the body text didn't
  // contain a literal '"term" means' pattern. Additive — does not affect
  // any clause that doesn't have "definitions" in its title.
  if (/(\bdefinitions?\b|\bdefined\s+terms?\b)/i.test(title)) {
    return 'Definition';
  }
  // 3. Interpretive — construction / interpretation rules
  if (/(construction|interpretation|terms\s+generally|references?\s+to|usage|accounting\s+terms)/.test(title)) {
    return 'Interpretive';
  }
  // 4. Boilerplate — standard end-of-agreement provisions
  if (/(notices?|counterparts|severability|successors\s+and\s+assigns|governing\s+law|jurisdiction|waiver\s+of\s+jury|amendments?\b|integration|entire\s+agreement|further\s+assurances|headings?|electronic\s+execution)/.test(title)) {
    return 'Boilerplate';
  }
  // 5. Default — Operative
  return 'Operative';
}

function extractDefinedTermName(clause: ContractClause): string | null {
  // FIX 9: backend-atomized rows carry the term name verbatim — use it
  // directly rather than re-deriving from raw_text.
  const backendTerm = (clause.defined_term_name || '').trim();
  if (backendTerm) return backendTerm;
  if (classifyClauseType(clause) !== 'Definition') return null;
  const text = clause.raw_text || '';
  // Client FIX 9 re-audit v2 (June 2026): pattern set extended to match
  // DEFINED_TERM_RE one-to-one — curly + straight + French guillemets,
  // optional comma between closing quote and verb, 'is defined as / to
  // mean / to be' added alongside 'means / shall mean / has-meaning'.
  // Pattern A: '"Term Name", means …' / '"Term Name" shall mean …'
  //            / '«Term Name» means …' / '"Term Name" is defined as …'
  const mA = text.match(/["“„«]([^"”»\n]{2,80})["”»]\s*,?\s*(?:shall\s+)?(?:means?|is\s+defined\s+(?:as|to\s+(?:mean|be)))\b/i);
  if (mA) return mA[1].trim();
  // Pattern B: '"Term Name" has/have/having the meaning …'
  const mB = text.match(/["“„«]([^"”»\n]{2,80})["”»]\s*,?\s*(?:has|have|having)\s+the\s+meanings?\b/i);
  if (mB) return mB[1].trim();
  // Pattern C: clause_title is the term name (TOC-resolved definitions —
  //    backend already gives us a clean heading like "Aggregate Commitments").
  const title = (clause.clause_title || '').trim();
  if (title && title !== 'UNRESOLVED' && /^[A-Z][\w\s\-/.,&'’]{1,80}$/.test(title)) {
    return title;
  }
  return null;
}

const JURISDICTION_PATTERNS: Array<{ jurisdiction: Jurisdiction; re: RegExp }> = [
  { jurisdiction: 'NL',    re: /\b(Dutch|Netherlands|incorporated\s+under\s+the\s+laws\s+of\s+the\s+Netherlands)\b/i },
  { jurisdiction: 'CA-QC', re: /\b(Quebec|Qu[eé]bec|Civil\s+Code\s+of\s+Qu[eé]bec)\b/i },
  { jurisdiction: 'DE',    re: /\b(German|Germany|incorporated\s+under\s+the\s+laws\s+of\s+Germany|HGB)\b/i },
  { jurisdiction: 'CH',    re: /\b(Swiss|Switzerland|Swiss\s+Code\s+of\s+Obligations)\b/i },
  { jurisdiction: 'AU',    re: /\b(Australian|Australia|incorporated\s+under\s+the\s+laws\s+of\s+Australia)\b/i },
  { jurisdiction: 'GB',    re: /\b(English\s+law|England\s+and\s+Wales|United\s+Kingdom|U\.K\.)\b/i },
  { jurisdiction: 'EU',    re: /\b(European\s+Union|EU\s+Member\s+State|EEA\b|BRRD)\b/i },
  { jurisdiction: 'US',    re: /\b(New\s+York|Delaware|United\s+States|U\.S\.\s+Person|Internal\s+Revenue\s+Code|USA\b)\b/i },
];

// State-level resolution for US: tried when 'US' would otherwise be the sole hit.
// Ordered by credit-agreement frequency (NY and DE dominate).
const US_STATE_CODES: Array<{ code: string; re: RegExp }> = [
  { code: 'US-NY', re: /\bNew\s+York\b/i },
  { code: 'US-DE', re: /\bDelaware\b/i },
  { code: 'US-CA', re: /\bCalifornia\b/i },
  { code: 'US-IL', re: /\bIllinois\b/i },
  { code: 'US-TX', re: /\bTexas\b/i },
  { code: 'US-MA', re: /\bMassachusetts\b/i },
  { code: 'US-FL', re: /\bFlorida\b/i },
  { code: 'US-NV', re: /\bNevada\b/i },
  { code: 'US-GA', re: /\bGeorgia\b/i },
  { code: 'US-OH', re: /\bOhio\b/i },
  { code: 'US-MD', re: /\bMaryland\b/i },
  { code: 'US-CO', re: /\bColorado\b/i },
  { code: 'US-NJ', re: /\bNew\s+Jersey\b/i },
  { code: 'US-CT', re: /\bConnecticut\b/i },
  { code: 'US-MN', re: /\bMinnesota\b/i },
  { code: 'US-NC', re: /\bNorth\s+Carolina\b/i },
  { code: 'US-PA', re: /\bPennsylvania\b/i },
  { code: 'US-VA', re: /\bVirginia\b/i },
  { code: 'US-WA', re: /\bWashington\s+State|\bState\s+of\s+Washington\b/i },
];

function resolveUSState(haystack: string): Jurisdiction {
  for (const s of US_STATE_CODES) {
    if (s.re.test(haystack)) return s.code;
  }
  return 'US';
}

function classifyJurisdiction(clause: ContractClause): Jurisdiction {
  const text     = clause.raw_text || '';
  const title    = clause.clause_title || '';
  const haystack = `${title}\n${text.slice(0, 800)}`;
  const hits: Jurisdiction[] = [];
  for (const p of JURISDICTION_PATTERNS) {
    if (p.re.test(haystack)) hits.push(p.jurisdiction);
  }
  if (hits.length === 0) return 'N/A';
  if (hits.length === 1) return hits[0] === 'US' ? resolveUSState(haystack) : hits[0];
  // Two hits including US → assume the OTHER one is the jurisdictional carve-out
  // (US is the base governing law in MUFG/Revlon; foreign-law annexes
  // reference the local law plus a US cross-reference).
  if (hits.length === 2 && hits.includes('US')) {
    return hits.find(h => h !== 'US') as Jurisdiction;
  }
  return 'Multi';
}

function classifyValueCategory(
  contextBefore: string,
  contextAfter: string,
  type: ValueType,
  axisLabel?: string,
): ValueCategory {
  // Type-driven defaults — date/duration/ratio/count have unambiguous categories
  if (type === 'date')     return 'Reference';
  if (type === 'duration') return 'Duration';
  if (type === 'ratio')    return 'Ratio';
  if (type === 'count')    return 'Count';
  // Pipe-table axis dominates the fuzzy text window. A pricing-grid cell's
  // column ("Applicable Margin (Loans)" / "Commitment Fee" / "Total Net
  // Leverage Ratio") is the authoritative classifier — the deterministic
  // backend tags it per cell, so every tier categorises identically instead
  // of by accidental proximity to the table header in the 80-char window
  // (which only the first data row sees).
  const axis = (axisLabel || '').toLowerCase();
  if (axis) {
    if (/\bcommitment\s+fee\b|\bfee\b/.test(axis))                         return 'Fee';
    if (type === 'percent' && /\b(applicable\s+margin|margin|rate|spread|interest|coupon)\b/.test(axis)) return 'Rate';
  }
  // Currency / percent — context-driven, before-context dominates
  const before = (contextBefore || '').toLowerCase();
  const after  = (contextAfter  || '').toLowerCase();
  // Credit Spread Adjustment (CSA, KDE 27) — a fixed spread distinct from the
  // Applicable Margin. Detected before the generic rate/floor checks so the
  // per-tenor CSA percentages categorise consistently. The defining phrase
  // often precedes only the first value in a comma-separated tenor list, so a
  // per-tenor signature ("for a three-month tenor") also identifies CSA rows.
  // Per Fix #11 spec (June 2026): emits 'Rate' so the output stays within the
  // 9-value controlled vocabulary (Cap | Floor | Threshold | Rate | Fee |
  // Duration | Count | Ratio | Reference). CSA is semantically a rate (a
  // fixed spread added to a base rate), so 'Rate' is the spec-compliant
  // bucket. Detection logic kept intact for future per-CSA reporting needs.
  if (type === 'percent' && !/\bfloor\s+of\s*$/.test(before.trimEnd() + ' ')) {
    const csaPhraseBefore = /\b(credit\s+spread\s+adjustment|benchmark\s+replacement\s+adjustment|sofr\s+adjustment|spread\s+adjustment)\s+of\s*$/;
    const csaTenorAfter = /^\s*[\d.]+\s*%?\s+for\s+a\s+(?:one|two|three|six|twelve|\d+)[-\s]month\s+tenor\b/;
    if (csaPhraseBefore.test(before.trimEnd() + ' ') || csaTenorAfter.test(after)) {
      return 'Rate';
    }
  }
  // Cap signals — "shall not exceed", "no more than", "maximum of", "up to"
  if (/\b(not\s+to\s+exceed|shall\s+not\s+exceed|no\s+more\s+than|not\s+more\s+than|maximum\s+(?:of\s+)?|at\s+most|up\s+to|no\s+greater\s+than|not\s+in\s+excess\s+of|cap(?:ped|p)?\s+(?:at|of)?)\b/.test(before)) {
    return 'Cap';
  }
  // Floor signals — "at least", "no less than", "minimum of"
  if (/\b(at\s+least|no\s+less\s+than|not\s+less\s+than|minimum\s+(?:of\s+)?|at\s+minimum|no\s+lower\s+than|equal\s+to\s+or\s+greater\s+than|greater\s+than\s+or\s+equal\s+to|floor\s+(?:of|at))\b/.test(before)) {
    return 'Floor';
  }
  // Fee — currency in a fee context
  if (type === 'currency' && /\b(fee|commission|commitment\s+fee|upfront\s+fee|arrangement\s+fee|administration\s+fee|charge|expense)\b/.test(before + ' ' + after)) {
    return 'Fee';
  }
  // Rate — percent with rate-flavour keywords
  if (type === 'percent' && /\b(rate|interest|margin|per\s+annum|p\.a\.|libor|sofr|euribor|coupon|spread)\b/.test(before + ' ' + after)) {
    return 'Rate';
  }
  // Threshold — comparison keywords without explicit cap/floor language
  if (/\b(threshold|trigger|in\s+excess\s+of|exceeds?|greater\s+than|less\s+than|more\s+than|fewer\s+than|equals?\b)\b/.test(before)) {
    return 'Threshold';
  }
  // Default — Threshold for currency/percent (most uncategorised monetary
  // obligations in credit agreements are thresholds; reviewer can re-tag)
  return 'Threshold';
}

function classifyEffectiveVsHeadline(contextSnippet: string): { isEffective: boolean; note: string | null } {
  const ctx = (contextSnippet || '').toLowerCase();
  // Carve-out / adjustment indicators — value is operative-effective, not headline
  const m = ctx.match(/\b(excluding\s+[^.,;]{0,60}|net\s+of\s+[^.,;]{0,60}|subject\s+to\s+[^.,;]{0,60}|after\s+giving\s+effect\s+to\s+[^.,;]{0,60}|less\s+the\s+[^.,;]{0,60}|except\s+for\s+[^.,;]{0,60})\b/);
  if (m) return { isEffective: true, note: m[0].trim() };
  if (/\b(provided\s+that|provided,\s+however)\b/.test(ctx)) {
    return { isEffective: true, note: 'proviso carve-out detected' };
  }
  return { isEffective: false, note: null };
}

// ─── END Fix #11 ────────────────────────────────────────────────────────────


// ─── Fix #12 (June 2026): Multi-Currency Unit Classification ───────────────
// Client audit: row 15 showed Amount=$100,000 + Unit=USD + Display=EUR 100,000
// — three-column disagreement. Root cause: the unit/amount pipeline defaulted
// to USD whenever the value's verbatim string lacked a non-$ symbol, even when
// the surrounding context made the currency unambiguous (ISO code, keyword,
// or symbol immediately before/after the value).
//
// Fix #12 replaces per-column re-detection with a single source-of-truth
// detectCurrency() that walks both the value and the context with strict
// priority: ISO code > symbol > keyword > default-USD-for-bare-$ > UNKNOWN.
// UNKNOWN never silently falls back to USD — instead the row gets flagged so
// a reviewer sees uncertainty rather than confidently-wrong USD attribution
// (F-01 adjacent: VeriFact risk on non-dollar obligations).
//
// Runs ONLY at Excel-export time. UI / inferUnit kept as-is for back-compat;
// export-time detection OVERRIDES record.unit and the Amount cell number
// format when detection produces a more authoritative answer.

type CurrencyISO    = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'CHF' | 'JPY' | 'UNKNOWN';
type CurrencySource = 'iso_code' | 'symbol' | 'keyword' | 'default_usd' | 'unknown';
type CurrencyFlag   = 'UNKNOWN_CURRENCY' | null;

const CURRENCY_KEYWORDS: Array<{ iso: CurrencyISO; re: RegExp }> = [
  { iso: 'EUR', re: /\b(Euros?|Euro)\b/i },
  { iso: 'GBP', re: /\b(Sterling|British\s+Pounds?|Pounds?\s+Sterling)\b/i },
  { iso: 'CAD', re: /\bCanadian\s+Dollars?\b/i },
  { iso: 'AUD', re: /\bAustralian\s+Dollars?\b/i },
  { iso: 'CHF', re: /\bSwiss\s+Francs?\b/i },
  { iso: 'JPY', re: /\b(Japanese\s+Yen|Yen)\b/i },
  { iso: 'USD', re: /\b(U\.?S\.?\s+Dollars?|United\s+States\s+Dollars?|US\s+Dollars?)\b/i },
];

function detectCurrency(
  value: string,
  context: string,
  backendCurrency?: string,
): {
  iso: CurrencyISO;
  source: CurrencySource;
  flag: CurrencyFlag;
} {
  const v    = value   || '';
  const c    = context || '';
  const both = `${v} ${c}`;

  // 0. Backend-captured currency (FIX 12 re-test, 2026-06-22): the Python
  // extractor positively identifies the currency at extraction time via
  // _classify_currency() and attaches it as quant.currency. Trust it FIRST
  // so the Excel-export path uses the same authoritative answer the UI
  // inferUnit already uses — kills the cross-column disagreement at source.
  if (backendCurrency) {
    const code = backendCurrency.toUpperCase();
    const allowed: CurrencyISO[] = ['USD','EUR','GBP','CAD','AUD','CHF','JPY'];
    if ((allowed as string[]).includes(code)) {
      return { iso: code as CurrencyISO, source: 'iso_code', flag: null };
    }
  }

  // 1. ISO code (highest priority) — anywhere in value or context
  const isoMatch = both.match(/\b(EUR|GBP|CAD|AUD|CHF|JPY|USD)\b/);
  if (isoMatch) return { iso: isoMatch[1] as CurrencyISO, source: 'iso_code', flag: null };

  // 2. Currency symbols — distinct glyphs (€, £, ¥) are unambiguous
  if (/€/.test(both)) return { iso: 'EUR', source: 'symbol', flag: null };
  if (/£/.test(both)) return { iso: 'GBP', source: 'symbol', flag: null };
  if (/¥/.test(both)) return { iso: 'JPY', source: 'symbol', flag: null };
  // C$ / CAD$ → Canadian; A$ / AUD$ → Australian (must check BEFORE bare $
  // since these contain $)
  if (/\bC\$|\bCAD\$/.test(both)) return { iso: 'CAD', source: 'symbol', flag: null };
  if (/\bA\$|\bAUD\$/.test(both)) return { iso: 'AUD', source: 'symbol', flag: null };

  // 3. Keyword in context — "Euros", "Sterling", "Canadian Dollars" etc.
  for (const k of CURRENCY_KEYWORDS) {
    if (k.re.test(c)) return { iso: k.iso, source: 'keyword', flag: null };
  }

  // 4. Bare $ in the value itself — assume USD (US is the base jurisdiction
  // in the MUFG/Revlon agreement). Flag-free because $ in a US-governed
  // credit agreement is overwhelmingly USD.
  if (/\$/.test(v)) return { iso: 'USD', source: 'default_usd', flag: null };

  // 5. UNKNOWN — no signal at all. Do NOT silently default to USD; surface
  // to the reviewer instead. This is the spec's F-01 mitigation.
  return { iso: 'UNKNOWN', source: 'unknown', flag: 'UNKNOWN_CURRENCY' };
}

// Excel cell number-format string for an Amount column whose unit was
// resolved by detectCurrency(). EUR/GBP/CAD/AUD/CHF use ISO prefix (matches
// the spec Display Value form "EUR 100,000"); USD and JPY use their universal
// single-char glyphs; UNKNOWN renders plain digits with no symbol.
function currencyExcelFormat(iso: CurrencyISO): string {
  switch (iso) {
    case 'USD':     return '$#,##0';
    case 'EUR':     return '"EUR "#,##0';
    case 'GBP':     return '"GBP "#,##0';
    case 'CAD':     return '"CAD "#,##0';
    case 'AUD':     return '"AUD "#,##0';
    case 'CHF':     return '"CHF "#,##0';
    case 'JPY':     return '"¥"#,##0';
    case 'UNKNOWN': return '#,##0';
  }
}

// ─── END Fix #12 ────────────────────────────────────────────────────────────


// ─── Fix #13 (June 2026): Page Number Attribution from PDF Footer ──────────
// Client audit: Column C ('Page') was '1' for every value across a 544-page
// PDF. Root cause: clause.page from the backend is unreliable on PDFs (page
// boundaries detected only at TOC level, defaulting to 1 elsewhere). The
// audit trail to verify a value in the source PDF is broken without correct
// page attribution.
//
// Fix #13 reads the printed page number from the footer pattern Fix #10
// already detects (e.g., '-142- 8033780.16') BEFORE that footer is stripped
// from the value extraction layer. Per-value attribution: a value at offset
// X is on the page of the FIRST footer with offset >= X (footers appear at
// the BOTTOM of each page, so they mark the end of the page above them).
//
// Same shape as Fix #10's FOOTER_SPAN_RE but with a capture group on the
// page-number portion. Tolerant fallback: if no footer at all is found in
// the clause (DOCX inputs, OCR'd PDFs without footers), fall back to
// clause.page; if that's null too, page='' and source='NONE' so a reviewer
// sees uncertainty rather than a silent '1'.

const FOOTER_PAGE_RE = /-(\d{1,4})-\s*(?:\[PAGE\s+\d+\]\s*)*\d{6,}\.\d{1,3}/g;

interface FooterPage {
  offset: number;
  endOffset: number;
  page: number;
}

function findFooterPages(rawText: string): FooterPage[] {
  if (!rawText) return [];
  const out: FooterPage[] = [];
  const re = new RegExp(FOOTER_PAGE_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(rawText)) !== null) {
    const page = parseInt(m[1], 10);
    if (Number.isFinite(page)) {
      out.push({ offset: m.index, endOffset: m.index + m[0].length, page });
    }
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return out;
}

// [P12] 'VALUE' = the page was resolved from the value's OWN offset within the
// clause (per-value attribution via printed_page_offsets), as opposed to the
// clause-level 'CLAUSE'/'FOOTER' fallbacks. Surfaced in the Page Source column.
type PageSource = 'FOOTER' | 'CLAUSE' | 'VALUE' | 'NONE';

function extractPrintedPage(
  clause: ContractClause,
  offset?: number,
): { page: number | null; source: PageSource } {
  // FIX 13 v2 (June 2026): per-value attribution. The backend now emits a
  // `printed_page_offsets` array — sorted [offset, printed_page] tuples
  // pinpointing each PDF-page boundary inside the clause's raw_text. When
  // an offset is provided we binary-search this list: the value at offset
  // X is on the page of the LAST boundary with boundary_offset <= X. This
  // replaces the clause-level short-circuit that gave 47 Preamble values
  // (spanning ~100 pages) the same Page 1 in Ryan's QA. Falls through to
  // the clause-level chain below when offsets are absent (older cached
  // payloads, DOCX inputs with no [PAGE N] markers).
  const offsets = clause.printed_page_offsets;
  if (offset != null && Array.isArray(offsets) && offsets.length > 0) {
    let chosen: number | null = null;
    for (const tuple of offsets) {
      const o = tuple?.[0];
      const p = tuple?.[1];
      if (typeof o !== 'number' || typeof p !== 'number') continue;
      if (o <= offset) chosen = p;
      else break;
    }
    if (chosen != null && Number.isFinite(chosen)) {
      // [P12] Resolved from the value's own offset → value-level attribution.
      // Label 'VALUE' (not 'FOOTER') so the Page Source column reflects that
      // this page pinpoints where the VALUE sits, not just where the clause
      // begins. A value deep in a multi-page clause now reports its real page.
      return { page: chosen, source: 'VALUE' };
    }
  }

  // Client FIX 10 (June 2026): backend's _strip_pdf_footers pre-pass now
  // captures the printed page from the centered '-NNN-' footer pattern and
  // attaches it to the clause as `printed_page` BEFORE removing the footer
  // from raw_text (so quant extraction never sees footer artifacts). When
  // present, trust it directly — that's the authoritative source for FIX
  // 13's FOOTER attribution. Falls through to the existing raw_text scan
  // for older cached responses + DOCX inputs that never had footers.
  if (clause.printed_page != null && Number.isFinite(clause.printed_page)) {
    return { page: clause.printed_page, source: 'FOOTER' };
  }

  const footers = findFooterPages(clause.raw_text || '');

  // No footer in raw_text → DOCX or footer-less PDF; trust backend clause.page
  if (footers.length === 0) {
    return clause.page != null
      ? { page: clause.page, source: 'CLAUSE' }
      : { page: null, source: 'NONE' };
  }

  // No offset (clause-level query) → use first footer's page (where clause
  // starts / first sub-region of the clause)
  if (offset == null) {
    return { page: footers[0].page, source: 'FOOTER' };
  }

  // Offset-precise: value at offset X is on the page of the first footer
  // with offset >= X. Footers appear at the BOTTOM of each page, AFTER the
  // values they belong to.
  for (const f of footers) {
    if (f.offset >= offset) return { page: f.page, source: 'FOOTER' };
  }

  // Value is past the last footer in the clause — same logical page as the
  // last footer (clause continues but no more page breaks were detected).
  return { page: footers[footers.length - 1].page, source: 'FOOTER' };
}

// ─── END Fix #13 ────────────────────────────────────────────────────────────


// ─── Fix #9 (June 2026): Definitions Section Atomization ───────────────────
// Spec problem: when §1.1 ("Definitions") spans 60-80 pages with 150+ defined
// terms, the entire section collapses into a single Excel row, and all 49
// quantitative values get attributed to the same parent row — no way to tell
// which term '$350,000,000' belongs to.
//
// Fix #9 atomizes at Excel-export time: when a clause's title flags it as a
// Definitions section (Fix #11.1 heuristic), the body is split on the
// canonical pattern '"Term" means …' and each match becomes its own virtual
// sub-clause. Quants are filtered to the offset range of their owning term
// and offsets are remapped to be relative to the sub-clause's sliced raw_text
// so downstream helpers (Fix #10 footer filter, Fix #11 value category, Fix
// #13 page attribution) all walk the sub-text correctly.
//
// Non-Definitions sections (§1.7, §1.8, operative covenants, schedules) are
// untouched — expandDefinitionsClauses() returns the clause as-is.
// UI render path is also untouched — atomization runs ONLY at Excel export.

// Client FIX 9 re-audit v2 (June 2026): the v1 regex (curly + straight
// quotes + means/shall mean/has-meaning) atomized MUFG/Revlon §1.1
// correctly but missed Swiss/British Loan Limits and one of the two
// Maturity Date defined terms on the real PDF — those entries used
// variants the v1 regex did not handle. v2 extends acceptance to:
//   • French guillemets «…»  (some jurisdictions)
//   • optional comma between closing quote and the verb
//   • 'is defined as' / 'is defined to mean' / 'is defined to be'
// All v1 cases (curly + straight + means + has/have/having the meaning)
// keep working byte-identically so the prior 54-test suite passes
// unchanged.
const DEFINED_TERM_RE = /["“„«]([^"”»\n]{2,80})["”»]\s*,?\s*(?:shall\s+)?(?:means?|(?:has|have|having)\s+the\s+meanings?|is\s+defined\s+(?:as|to\s+(?:mean|be)))\b/gi;

interface AtomizedDefinitionTerm {
  term: string;
  start: number;
}

function findDefinedTerms(rawText: string): AtomizedDefinitionTerm[] {
  if (!rawText) return [];
  const out: AtomizedDefinitionTerm[] = [];
  const re = new RegExp(DEFINED_TERM_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(rawText)) !== null) {
    const term = (m[1] || '').trim();
    if (term) out.push({ term, start: m.index });
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return out;
}

// Client FIX 9 re-audit (June 2026): minimum number of detected defined
// terms in a clause body that triggers atomization even when the title
// doesn't say 'Definitions'. The MUFG/Revlon audit found the (Preamble)
// row carrying 47 values across ~100 pages of unattributed defined-term
// prose — that span has no clause title at all, so a title-only check
// silently no-op'd. A threshold of 3 matches is conservative: real
// operative clauses occasionally quote a single term ('the term "Borrower"
// means …') without being a definitions section, but 3+ quoted-term-means
// patterns in the SAME body are only ever produced by a definitions span.
const FIX9_DENSITY_THRESHOLD = 3;

function isDefinitionsSection(clause: ContractClause): boolean {
  // Reuse Fix #11.1 title-based detection — same source-of-truth for
  // "what counts as a Definitions section".
  const title = (clause.clause_title || '').toLowerCase();
  if (/(\bdefinitions?\b|\bdefined\s+terms?\b)/i.test(title)) return true;
  // Client FIX 9 re-audit (June 2026): density fallback for untitled
  // definitions spans (the '(Preamble)' synthetic clause that holds the
  // pre-first-section body, and any clause whose backend title resolution
  // returned 'UNRESOLVED'). When a body carries FIX9_DENSITY_THRESHOLD or
  // more 'quoted-term means' matches it is a definitions span regardless
  // of how the title was tagged.
  if ((clause.raw_text || '').length > 0) {
    const matches = findDefinedTerms(clause.raw_text || '');
    if (matches.length >= FIX9_DENSITY_THRESHOLD) return true;
  }
  return false;
}

function atomizeDefinitionsClause(clause: ContractClause): ContractClause[] {
  if (!isDefinitionsSection(clause)) return [clause];
  const text  = clause.raw_text || '';
  const terms = findDefinedTerms(text);
  // Need at least 2 detected terms to atomize meaningfully — a single-term
  // definitions clause is already atomic. Keep the original clause so the
  // F-16 acceptance criterion ("non-definitions sections unaffected")
  // generalises to "uninformative atomization is also a no-op".
  if (terms.length < 2) return [clause];

  const out: ContractClause[] = [];
  for (let i = 0; i < terms.length; i++) {
    const start = terms[i].start;
    const end   = i < terms.length - 1 ? terms[i + 1].start : text.length;
    const body  = text.slice(start, end);
    // Filter quants to those whose offset falls inside [start, end) and
    // remap each offset to be relative to the sub-clause's sliced body, so
    // inferLabel / extractContextSnippet / findFooterPages all walk the
    // right substring without coordinate drift.
    const subQuants = (clause.quants || [])
      .filter(q => q.offset != null && q.offset >= start && q.offset < end)
      .map(q => ({ ...q, offset: (q.offset as number) - start }));
    out.push({
      ...clause,
      clause_no:    `${clause.clause_no} / "${terms[i].term}"`,
      clause_title: terms[i].term,
      raw_text:     body,
      // Explicit definition tag — bypasses the title-based heuristic in
      // Fix #11/#11.1 (which would mis-classify because the new title is
      // the term name, not "Definitions") and routes downstream Defined
      // Term extraction through the title fallback path cleanly.
      content_type: 'definition',
      quants:       subQuants,
    });
  }
  return out;
}

function expandDefinitionsClauses(clauses: ContractClause[]): ContractClause[] {
  // FIX 9 is now performed in the backend clause extractor, so rows can
  // arrive already atomized (flagged `atomized`). When any are, the backend
  // is the single source of truth — skip the client pass entirely rather than
  // re-splitting single-term bodies. Older cached responses (no `atomized`
  // flag) still fall through to the client atomizer for back-compat.
  if (clauses.some(c => c.atomized)) {
    return clauses;
  }
  const out: ContractClause[] = [];
  for (const c of clauses) {
    const subs = atomizeDefinitionsClause(c);
    for (const s of subs) out.push(s);
  }
  return out;
}

// ─── END Fix #9 ─────────────────────────────────────────────────────────────


// ─── Excel export (spec-compliant: Captured Values + Summary) ───────────────

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);


// ─── FIX 19 — Summary Sheet Flag Rollup ─────────────────────────────────────
//
// MUFG/Revlon audit 2026-06-10: Summary sheet reported "Flagged 0" while the
// Captured Values sheet carried 345+ row-level flags (INVALID_LABEL,
// MAGNITUDE_ERROR, NEEDS_REVIEW, UNKNOWN_CURRENCY, …). The flag machinery
// from FIX 3/5/6/12 was live but invisible at the rollup level — reviewers
// who opened the Summary first (intended triage surface) were told nothing
// needed attention on a document containing a MAGNITUDE_ERROR.
//
// Spec rule: "Flagged" = count of rows where ANY of:
//   - Label_Flag ∈ {INVALID_LABEL, NEEDS_REVIEW}
//   - Parse_Flag is not null
//   - Display_Flag ∈ {MAGNITUDE_ERROR, CORRECTED}
//   - Currency_Flag is not null
//   - Description_Flag = true
// DISAMBIGUATED and pure SPACING_NORMALIZED are INFORMATIONAL (rollup
// surfaces them on a separate line — never collapsed into the Flagged tally).
//
// Status column interaction (spec rule 3): a row meeting the review-blocking
// definition carries Status='Flagged' (not 'Pending') so the existing
// Approved/Flagged/Pending workflow routes it. User-set 'approved' /
// 'flagged' are respected verbatim — the auto-rollup only overrides
// 'pending' on review-blocking rows.

const FIX19_REVIEW_BLOCKING_LABEL_FLAGS: ReadonlySet<string> = new Set([
  'INVALID_LABEL',
  'NEEDS_REVIEW',
]);
const FIX19_REVIEW_BLOCKING_DISPLAY_FLAGS: ReadonlySet<string> = new Set([
  // Original spec values from the MUFG/Revlon audit 2026-06-10.
  'MAGNITUDE_ERROR',
  'CORRECTED',
  // FIX 19 classification gap closed 2026-06-12: the real MUFG/Revlon
  // re-export surfaced 9 rows (PARSE_UNCERTAIN 7 + MAGNITUDE_SUSPECT 2)
  // that landed in neither the Flagged nor the Informational bucket —
  // the spec was written against the legacy validateDisplayValue flag
  // names, but FIX 14 (numeric reassembly) and FIX 16 (cross-ref
  // resolution) added these semantically-equivalent variants:
  //   PARSE_UNCERTAIN       — rescued reassembled row, parse confidence low
  //   MAGNITUDE_SUSPECT     — collateralisation-check failure
  //   AMBIGUOUS_SOURCE_VALUE — malformed source, cross-ref ambiguity
  // All three carry the same "needs reviewer attention" semantics as the
  // original CORRECTED / MAGNITUDE_ERROR pair, so they enter the
  // review-blocking set here. Adding them is purely additive — no row
  // ever moves OUT of Flagged, only newly-recognised rows move INTO it.
  'PARSE_UNCERTAIN',
  'MAGNITUDE_SUSPECT',
  'AMBIGUOUS_SOURCE_VALUE',
]);
const FIX19_INFORMATIONAL_LABEL_FLAGS: ReadonlySet<string> = new Set([
  'DISAMBIGUATED',
]);
const FIX19_INFORMATIONAL_DISPLAY_FLAGS: ReadonlySet<string> = new Set([
  'SPACING_NORMALIZED',
  'RESOLVED_FROM_CROSS_REFERENCE',
]);

interface Fix19RowFlagSummary {
  labelFlag: string | null | undefined;
  parseFlag: string | null | undefined;
  displayFlag: string | null | undefined;
  currencyFlag: string | null | undefined;
  descriptionFlag: boolean;
}

function fix19IsReviewBlocking(f: Fix19RowFlagSummary): boolean {
  if (f.labelFlag && FIX19_REVIEW_BLOCKING_LABEL_FLAGS.has(f.labelFlag)) return true;
  if (f.parseFlag) return true;
  if (f.displayFlag && FIX19_REVIEW_BLOCKING_DISPLAY_FLAGS.has(f.displayFlag)) return true;
  if (f.currencyFlag) return true;
  if (f.descriptionFlag) return true;
  return false;
}

function fix19IsInformational(f: Fix19RowFlagSummary): boolean {
  // A row is "informational" if it carries any non-blocking flag and is
  // NOT review-blocking — counts surface on a separate Summary line so
  // reviewers can verify the disambiguation / normalization happened
  // without losing their triage signal.
  if (fix19IsReviewBlocking(f)) return false;
  if (f.labelFlag && FIX19_INFORMATIONAL_LABEL_FLAGS.has(f.labelFlag)) return true;
  if (f.displayFlag && FIX19_INFORMATIONAL_DISPLAY_FLAGS.has(f.displayFlag)) return true;
  return false;
}

// ─── END FIX 19 helpers ─────────────────────────────────────────────────────

async function downloadCapturedValuesAsXlsx(
  fileLabel: string,
  clauses: ContractClause[],
  liveByValueId: Map<string, LiveValueState>,
  fileIdx: number,
  scope: ScopeFilter = 'provisions',
) {
  const XLSX = await import('xlsx');

  // Apply scope filter at source — matches what the user is viewing in the UI.
  // Default is 'provisions' so the export honours the acceptance criterion:
  // schedule/exhibit rows are dropped from the default export.
  const scopedClauses = clauses.filter(c => {
    if (hasSublimitData(c)) return true;
    const isSchedule = isScheduleScopeClause(c);
    // Client F-02 fix: list-item noise rows (bare "64.", Unix timestamps)
    // are dropped from BOTH 'provisions' AND 'schedules' scopes — they
    // belong in neither, and including them in 'schedules' would just
    // shift the audit problem from one tab to another. 'all' scope keeps
    // them so an admin reviewer can still inspect what was filtered.
    if (scope !== 'all' && isExportNoiseClause(c)) return false;
    if (scope === 'provisions') return !isSchedule;
    if (scope === 'schedules')  return  isSchedule;
    return true;
  });

  // Fix #9: atomize each Definitions section into one row per defined term.
  // Non-definitions clauses pass through untouched. Sub-clauses inherit
  // page/schedule/etc. from the parent and carry a composite clause_no
  // ('§1.1 / "Aggregate Commitments"') so reviewers can trace each row back
  // to its origin. Runs AFTER scope filter so atomization respects the
  // user-selected scope (no point splitting a schedule that won't ship).
  const expandedClauses = expandDefinitionsClauses(scopedClauses);

  // Fix #11 polish (June 2026): atomized definition INTRO children (the
  // lead-in prose before the first defined term) inherit content_type
  // 'definition' from the parent but carry defined_term_name=null by design
  // — they belong to no specific term. Ryan QA AC2 ("All Definition rows
  // have a non-null Defined Term Name") flagged these as gaps. To satisfy
  // AC2 without misclassifying the intro, we build a parent_clause_no →
  // first_term lookup so an intro inherits the term of the FIRST sibling
  // definition it precedes. Purely additive — extractDefinedTermName's
  // existing Pattern A/B/C logic still runs first; this is the fallback.
  const parentClauseToFirstTerm = new Map<string, string>();
  for (const c of expandedClauses) {
    const parent = (c.parent_clause_no || '').trim();
    const term   = (c.defined_term_name || '').trim();
    if (parent && term && !parentClauseToFirstTerm.has(parent)) {
      parentClauseToFirstTerm.set(parent, term);
    }
  }

  // FIX P12 — document-level governing law jurisdiction.
  // Governing law clauses rarely carry numeric values so they never appear in
  // the per-value row loop below. Scan all clauses once to find the governing
  // law clause and extract a doc-wide jurisdiction; use it as the fallback for
  // every row whose individual clause text doesn't mention a jurisdiction.
  // We search up to 4000 chars of the governing law clause (not just 800) because
  // in long credit agreements the state name often appears well past char 800.
  const GOVERNING_LAW_TITLE_RE = /governing\s+law|choice\s+of\s+law|applicable\s+law/i;
  const GOVERNING_LAW_VERB_RE  = /\b(governed|construed|interpreted)\b[^.]{0,150}\blaws?\s+of\b/i;
  let docJurisdiction: Jurisdiction = 'N/A';
  for (const c of clauses) {
    const titleHit = GOVERNING_LAW_TITLE_RE.test(c.clause_title || '');
    const fullText = (c.raw_text || '').slice(0, 4000);
    const textHit  = GOVERNING_LAW_VERB_RE.test(fullText);
    if (titleHit || textHit) {
      const haystack = `${c.clause_title || ''}\n${fullText}`;
      console.log('[P12] govlaw candidate:', c.clause_no, c.clause_title, '| text[:200]:', fullText.slice(0, 200));
      for (const p of JURISDICTION_PATTERNS) {
        if (p.re.test(haystack)) {
          docJurisdiction = p.jurisdiction === 'US' ? resolveUSState(haystack) : p.jurisdiction;
          break;
        }
      }
      console.log('[P12] govlaw jurisdiction from full text:', docJurisdiction);
      if (docJurisdiction !== 'N/A') break;
    }
  }
  console.log('[P12] docJurisdiction =', docJurisdiction, '| total clauses scanned:', clauses.length);

  // Build (clause, record, live) tuples — apply any live-state edits.
  const flat: Array<{ clause: ContractClause; record: ValueRecord; live: LiveValueState }> = [];
  expandedClauses.forEach((c, ci) => {
    // Fix #10: drop PDF footer/page-number artifacts before they enter the
    // Excel pipeline (and downstream description/operator processing).
    const useful = usefulQuants(c);
    useful.forEach((q, qi) => {
      const base = quantToValueRecord(q, c, qi, fileIdx, ci);
      const live = liveByValueId.get(base.id) || { status: 'pending', edited: false };
      const record: ValueRecord = {
        ...base,
        label: live.label ?? base.label,
        op:    live.op    ?? base.op,
        sym:   live.sym   ?? base.sym,
        value: live.value ?? base.value,
        unit:  live.unit  ?? base.unit,
      };
      flat.push({ clause: c, record, live });
    });
    // FIX 9: a defined term with no captured value (e.g. "Banking Day",
    // "Material Adverse Effect") would otherwise vanish from the export, which
    // emits one row per value. Emit a single value-less placeholder row so
    // every atomized defined term still appears with its Title / Defined Term
    // Name / Description. Scoped to atomized definition rows only — operative
    // and schedule clauses with no values stay excluded as before.
    if (useful.length === 0 && c.atomized && !c.definition_intro && c.defined_term_name) {
      const id = `f${fileIdx}-c${ci}-q-1`;
      const live = liveByValueId.get(id) || { status: 'pending', edited: false };
      const record: ValueRecord = {
        id,
        type: 'count',
        label: c.defined_term_name,
        op: '', sym: '', value: '', unit: '',
        src: resolvedClauseTitle(c)
          ? `§${c.clause_no} — ${resolvedClauseTitle(c)}`
          : `§${c.clause_no}`,
        page: c.page ?? null,
        phrase: c.raw_text || '',
      };
      flat.push({ clause: c, record, live });
    }
  });

  // Fix #3: file-level disambiguation. Mutates record.label / record.label_flag
  // in place so duplicate (src, label) pairs get a "[N of M]" suffix and the
  // flag is set to 'DISAMBIGUATED'. Runs AFTER live edits so a reviewer who
  // already named two duplicates differently isn't second-guessed.
  disambiguateLabelsInPlace(flat.map(f => f.record));

  // Client FIX 16: malformed-source cross-reference resolution. Runs ONCE
  // across the full record set so non-malformed occurrences of $325,000,000
  // (Aggregate Tranche A Commitments, Schedule C-1) can corroborate the
  // '$325,00,000' typo in a different clause. Mutates record.malformed_resolution
  // / resolved_amount / resolution_note in place; the per-row export loop
  // below short-circuits validateDisplayValue when those fields are set.
  resolveMalformedInPlace(flat.map(f => f.record));

  const headers = [
    // FIX 21: Facility column sits right after Title so a Loan IQ reviewer can
    // sort/filter every row by its parent facility. FIX 22: Project Entity
    // follows Facility — together they form the Loan IQ booking-record key.
    'Clause', 'Title', 'Facility', 'Project Entity', 'Page', 'Content Type', 'Schedule Name',
    // Fix #7: Symbol column REMOVED (redundant with Operator); Operator now
    // canonical ({=,>=,<=,>,<}); original text → Operator Raw at end.
    'Value Label', 'Type', 'Operator',
    'Amount', 'Display Value', 'Unit', 'Source', 'Status', 'Edited', 'Note',
    'Label Flag', 'Context Snippet',
    'Description', 'Description Source', 'Description Flag',
    'Amount Confirmed', 'Parse Flag',
    // Fix #6: canonical-form display + raw extraction kept for audit
    'Display Flag', 'Raw Display',
    // Fix #7: original operator text preserved + unrecognized-operator flag
    'Operator Raw', 'Operator Flag',
    // Fix #11: data-model expansion — clause-level + value-level metadata for
    // cross-contract search and comparison. Appended at END so AMOUNT_COL,
    // existing column indices, and the !cols width array stay backward-
    // compatible with the prior export shape.
    'Clause Type', 'Defined Term Name', 'Jurisdiction',
    'Value Category', 'Effective vs Headline', 'EvH Note',
    // Fix #12: UNKNOWN_CURRENCY flag — set when detectCurrency could not
    // resolve an ISO from the value or context. Resolved currencies leave
    // this blank (Unit column carries the answer).
    'Currency Flag',
    // Fix #13: provenance of the Page column — FOOTER (extracted from PDF
    // footer, most reliable), CLAUSE (backend clause.page, typical DOCX
    // path), NONE (no signal at all).
    'Page Source',
  ];
  const aoa: any[][] = [headers];
  const fmts: (string | null)[] = [null];
  // Amount column index — derived from the header so it can never drift when
  // columns are inserted (FIX 21/22 added Facility + Project Entity, which
  // silently shifted a hardcoded index and broke the Type column's text
  // coercion + the Amount number format).
  const AMOUNT_COL = headers.indexOf('Amount');

  // FIX 19 — accumulate flag rollups in parallel with the row loop. We
  // tally per-flag-value counts here (e.g. 'INVALID_LABEL': 77) so the
  // Summary sheet can surface the same breakdown the audit log already
  // produces row-by-row, without requiring a second pass over `flat`.
  const fix19LabelFlagCounts: Record<string, number> = {};
  const fix19ParseFlagCounts: Record<string, number> = {};
  const fix19DisplayFlagCounts: Record<string, number> = {};
  const fix19CurrencyFlagCounts: Record<string, number> = {};
  let fix19DescriptionFlagCount = 0;
  let fix19AutoFlaggedCount = 0;
  let fix19InformationalRowCount = 0;

  flat.forEach(({ clause, record, live }) => {
    const { num: initialNum, fmt } = numericOf(record);
    // Fix #5: rebuild Amount from Context_Snippet when the backend parser
    // returned 0 from a truncated parenthetical, AND flag genuine zeros so
    // a "deemed to be $0" row isn't "corrected" away.
    const ctxForParse = record.context_snippet || extractContextSnippet(clause.raw_text || '', record.offset);
    const parsed = validateZeroAmount(record, initialNum, ctxForParse);
    const num = parsed.amount;
    // Fix #6: cross-validate Amount vs the document-extracted display string
    // (or the Fix #5 recovered display). On mismatch we emit a canonical
    // string and a flag; the original is retained in 'Raw Display' for audit.
    const rawDisplay = parsed.value ?? record.value;
    // Fix #12: single source-of-truth currency detection. Runs BEFORE
    // validateDisplayValue so the canonical Display Value, Unit column, and
    // Amount cell format all derive from the same authoritative ISO — kills
    // the row-15 three-column disagreement at the root. Non-currency types
    // pass through with their existing unit.
    const currencyInfo = record.type === 'currency'
      ? detectCurrency(rawDisplay, ctxForParse, record.currency)
      : null;
    const effectiveUnit = currencyInfo ? currencyInfo.iso : record.unit;
    const effectiveFmt  = currencyInfo ? currencyExcelFormat(currencyInfo.iso) : fmt;
    // FIX 14: carry the reassembled flag (and unit override) into the
    // display-validation step so PARSE_UNCERTAIN takes precedence over the
    // comfort flag SPACING_NORMALIZED on rescued rows. ctxForParse drives
    // the new MAGNITUDE_SUSPECT collateralisation check.
    const recordForDisplay = currencyInfo
      ? { type: record.type, unit: currencyInfo.iso, reassembled: record.reassembled }
      : { type: record.type, unit: record.unit, reassembled: record.reassembled };
    const dv = validateDisplayValue(recordForDisplay, num, rawDisplay, ctxForParse);
    // Client FIX 16: override Amount + Display Value + Display Flag when the
    // cross-reference resolver concluded this row is malformed. RESOLVED
    // emits the corroborated candidate; AMBIGUOUS emits BOTH with the
    // "source malformed" suffix and a null Amount — never a single silent
    // pick. Runs AFTER validateDisplayValue so the resolution outcome wins
    // over whatever Display Flag the validator would otherwise pick
    // (CORRECTED / MAGNITUDE_ERROR on a malformed token is the exact pattern
    // FIX 16 was written to eliminate).
    let effectiveNum: number | null = num;
    let effectiveDisplayValue = dv.displayValue;
    let effectiveDisplayFlag: DisplayFlag = dv.displayFlag;
    let resolutionNote = '';
    if (record.malformed_resolution === 'RESOLVED'
        && record.resolved_amount != null
        && Number.isFinite(record.resolved_amount)) {
      effectiveNum = record.resolved_amount;
      effectiveDisplayValue = formatDisplay(record.resolved_amount, record.type, effectiveUnit);
      effectiveDisplayFlag = 'RESOLVED_FROM_CROSS_REFERENCE';
      resolutionNote = record.resolution_note || '';
    } else if (record.malformed_resolution === 'AMBIGUOUS'
               && record.candidates
               && record.candidates.length >= 2) {
      effectiveNum = null;
      const cands = record.candidates.map(c => formatDisplay(c, record.type, effectiveUnit));
      effectiveDisplayValue = `${cands.join(' OR ')} — source malformed`;
      effectiveDisplayFlag = 'AMBIGUOUS_SOURCE_VALUE';
      resolutionNote = record.resolution_note || '';
    }
    const displayValue = effectiveDisplayValue;
    // Fix #4: per-value AI-generated plain English description, cleaned of
    // doc-id stamps and quality-flagged. Fix #4 re-audit (2026-06-13) added
    // backend describeAllValues so each quant carries its own
    // value_description; extractValueDescription prefers that field and falls
    // back to the clause-level summary on cached / pre-fix payloads.
    const quantForDesc = clause.quants?.[
      Number(record.id.split('-q')[1] || 0)
    ];
    const desc = quantForDesc
      ? extractValueDescription(quantForDesc, clause)
      : extractDescription(clause);
    // Fix #7: canonical operator vocabulary. record.op is still the original
    // human-readable text ('at least', 'not to exceed', …) — runs through
    // normalizeOperator at export time so user-edited operators are also
    // canonicalised.
    const opNorm = normalizeOperator(record.op);
    // Fix #11: rule-based clause-level + value-level metadata. Clause-level
    // tags (Clause Type / Defined Term / Jurisdiction) are derived once per
    // clause but repeat per row so each row remains independently filterable
    // in Excel — that matches the bank's cross-portfolio query workflow.
    const clauseType  = classifyClauseType(clause);
    // Fix #11 polish: prefer extractDefinedTermName's own derivation (backend
    // term / quoted-term pattern / clean-title fallback). If it returns null
    // on a Definition row that is atomized AND has a parent_clause_no, fall
    // through to the sibling map built above so intro / body-fragment rows
    // inherit the first term they precede (Ryan QA AC2 satisfaction).
    let definedTerm = extractDefinedTermName(clause);
    if (!definedTerm && clauseType === 'Definition' && clause.parent_clause_no) {
      const inherited = parentClauseToFirstTerm.get(clause.parent_clause_no.trim());
      if (inherited) definedTerm = inherited;
    }
    // FIX 9 placeholder rows (value-less defined terms — Banking Day, Material
    // Adverse Effect, Commitment) carry the 'count' sentinel type purely so the
    // term still exports; they hold no numeric value. Don't assert a
    // quantitative Type/Category on them — a definition with no value is
    // neither a Count nor any other measured quantity.
    const isValueLessPlaceholder =
      !!clause.atomized
      && record.type === 'count'
      && !(record.value || '').trim()
      && !(record.op || '').trim();
    const jurisdiction = classifyJurisdiction(clause) !== 'N/A'
      ? classifyJurisdiction(clause)
      : docJurisdiction;
    const valueCategory = classifyValueCategory(
      (clause.raw_text || '').slice(Math.max(0, (record.offset ?? 0) - 80), record.offset ?? 0),
      (clause.raw_text || '').slice(record.offset ?? 0, (record.offset ?? 0) + 80),
      record.type,
      record.column_label,
    );
    const evh = classifyEffectiveVsHeadline(record.context_snippet || '');
    // Fix #13: derive the printed page from the PDF footer pattern instead
    // of trusting clause.page (which is '1' for every row on PDFs after
    // 544-page audit). Offset-precise: a value past a footer is on the page
    // ABOVE that footer.
    const pp = extractPrintedPage(clause, record.offset);
    // Client FIX 16: merge resolution citation into the Note column so the
    // reviewer can read WHY the row was resolved or flagged ambiguous. A
    // user-set live.note still wins (don't overwrite manual notes); the
    // citation falls in as the default only when the cell would otherwise
    // be blank.
    const effectiveNote = live.note || resolutionNote || '';

    // FIX 19 — compute per-row flag rollup BEFORE writing the row so
    // (a) we can override Status='Pending' → 'Flagged' on auto-detected
    // review-blocking rows (spec rule 3 — the Approved/Flagged/Pending
    // workflow only routes rows whose Status carries the flag), and
    // (b) the Summary sheet's flag-type breakdown table sees every
    // emitted value exactly once (no double-counting across the data
    // sheet and the summary).
    const fix19FlagSummary: Fix19RowFlagSummary = {
      labelFlag:       record.label_flag || null,
      parseFlag:       parsed.parseFlag || null,
      displayFlag:     effectiveDisplayFlag || null,
      currencyFlag:    currencyInfo?.flag || null,
      descriptionFlag: !!desc.flag,
    };
    const fix19IsBlocking = fix19IsReviewBlocking(fix19FlagSummary);
    const fix19IsInfo     = fix19IsInformational(fix19FlagSummary);
    if (fix19FlagSummary.labelFlag) {
      fix19LabelFlagCounts[fix19FlagSummary.labelFlag] =
        (fix19LabelFlagCounts[fix19FlagSummary.labelFlag] || 0) + 1;
    }
    if (fix19FlagSummary.parseFlag) {
      fix19ParseFlagCounts[fix19FlagSummary.parseFlag] =
        (fix19ParseFlagCounts[fix19FlagSummary.parseFlag] || 0) + 1;
    }
    if (fix19FlagSummary.displayFlag) {
      fix19DisplayFlagCounts[fix19FlagSummary.displayFlag] =
        (fix19DisplayFlagCounts[fix19FlagSummary.displayFlag] || 0) + 1;
    }
    if (fix19FlagSummary.currencyFlag) {
      fix19CurrencyFlagCounts[fix19FlagSummary.currencyFlag] =
        (fix19CurrencyFlagCounts[fix19FlagSummary.currencyFlag] || 0) + 1;
    }
    if (fix19FlagSummary.descriptionFlag) fix19DescriptionFlagCount++;
    if (fix19IsInfo) fix19InformationalRowCount++;
    // Status override: user-set 'approved' / 'flagged' are respected
    // verbatim — auto-rollup only promotes 'pending' rows that meet
    // the review-blocking definition. We DO NOT mutate `live` itself
    // (that's the persisted user state); only the exported Status
    // cell reflects the auto-flag so reviewers see triage parity
    // between Summary count and data-sheet routing.
    let effectiveStatus = live.status;
    if (effectiveStatus === 'pending' && fix19IsBlocking) {
      effectiveStatus = 'flagged';
      fix19AutoFlaggedCount++;
    }

    aoa.push([
      clause.clause_no,
      deriveClauseTitle(clause),
      // A sublimit value carries its own facility (Exhibit J row → specific
      // facility); fall back to the clause-level facility for ordinary rows.
      record.facility_name || clause.facility_name || '',
      record.project_entity || '',
      pp.page ?? '',
      cap(clause.content_type || 'provision'),
      clause.schedule_name || '',
      record.label,
      isValueLessPlaceholder ? '' : TYPE_LABEL[record.type],
      opNorm.op_canonical,
      effectiveNum == null ? '' : effectiveNum,
      displayValue,
      effectiveUnit,
      record.src,
      cap(effectiveStatus),
      live.edited ? 'Yes' : '',
      effectiveNote,
      record.label_flag || '',
      record.context_snippet || '',
      desc.description,
      desc.source,
      desc.flag ? 'Yes' : '',
      parsed.amountConfirmed ? 'Yes' : '',
      parsed.parseFlag || '',
      effectiveDisplayFlag || '',
      dv.rawDisplay,
      record.op || '',
      opNorm.op_flag || '',
      // Fix #11 (in the order declared in the header)
      clauseType,
      definedTerm || '',
      jurisdiction,
      isValueLessPlaceholder ? 'Reference' : valueCategory,
      evh.isEffective ? 'Yes' : '',
      evh.note || '',
      // Fix #12: surface UNKNOWN_CURRENCY rows so a reviewer never sees a
      // confidently-wrong USD attribution. Resolved currencies (EUR/GBP/etc.)
      // leave this blank — the Unit column already carries the answer.
      currencyInfo?.flag || '',
      // Fix #13: provenance of the Page value so a reviewer can tell the
      // difference between a footer-derived page (high confidence) and a
      // backend-supplied clause.page (lower confidence on PDFs).
      pp.source,
    ]);
    fmts.push(effectiveFmt);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    // FIX 21/22: width 22 Facility + 22 Project Entity (after Title=26).
    9, 26, 22, 22, 6, 12, 24, 34, 10, 15, 16, 17, 13, 9, 11, 8, 30, 16, 56, 80, 16, 9, 14, 16, 17, 17, 18, 18,
    // Fix #11: column widths for the 6 new fields (Clause Type, Defined Term,
    // Jurisdiction, Value Category, Effective vs Headline, EvH Note)
    14, 28, 12, 14, 16, 40,
    // Fix #12: Currency Flag
    16,
    // Fix #13: Page Source
    12,
  ].map(wch => ({ wch }));
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: headers.length - 1 } }),
  };
  // Freeze the header row
  (ws as any)['!views'] = [{ state: 'frozen', ySplit: 1, xSplit: 0, topLeftCell: 'A2' }];

  // Apply per-row number format to Amount column
  for (let r = 1; r < aoa.length; r++) {
    const fmt = fmts[r];
    const addr = XLSX.utils.encode_cell({ c: AMOUNT_COL, r });
    const cell = ws[addr];
    if (cell && fmt) {
      cell.t = 'n';
      cell.z = fmt;
    }
  }

  // Force every non-Amount cell to text so SheetJS doesn't IEEE-754 the
  // Clause # column (e.g. 1.1 → 1.1000000000000001) — same defensive
  // pattern as the v1 export.
  if (ws['!ref']) {
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        if (C === AMOUNT_COL) continue; // Amount stays numeric
        const addr = XLSX.utils.encode_cell({ c: C, r: R });
        const cell = ws[addr];
        if (cell && cell.v != null) {
          cell.t = 's';
          cell.v = String(cell.v);
          delete (cell as any).w;
        }
      }
    }
  }

  // ── Summary sheet ─────────────────────────────────────────────────────────
  //
  // FIX 19: the legacy summary counted only user-set review states (live.status
  // = approved/flagged/pending). That made the rollup blind to every
  // row-level QA flag emitted by FIX 3/5/6/12 (INVALID_LABEL, MAGNITUDE_ERROR,
  // UNKNOWN_CURRENCY, …) — reviewers opening the Summary first were told
  // nothing needed attention on documents with hundreds of pending flags.
  // The new rollup auto-promotes pending rows that meet the review-blocking
  // definition to Status='Flagged' on the data sheet (already done in the
  // per-row loop above via `effectiveStatus`) and reports the resulting
  // Flagged total here. User-set approved/flagged remain authoritative.
  let userApproved = 0, userFlagged = 0, edited = 0;
  flat.forEach(({ live }) => {
    if (live.status === 'approved') userApproved++;
    else if (live.status === 'flagged') userFlagged++;
    if (live.edited) edited++;
  });
  const total = flat.length;
  // Flagged = user-explicit + auto-detected (review-blocking pending rows).
  const flagged = userFlagged + fix19AutoFlaggedCount;
  const approved = userApproved;
  // Arithmetic invariant (spec acceptance criterion 4):
  //   Pending = total − Approved − Flagged
  const pending = total - approved - flagged;

  // Counts for the summary so reviewers know what was kept / dropped.
  const provisionClauseCount = clauses.filter(c => !isScheduleScopeClause(c)).length;
  const scheduleClauseCount  = clauses.filter(isScheduleScopeClause).length;
  const scopeLabel =
    scope === 'provisions' ? 'Provisions only (schedules excluded)'
    : scope === 'schedules' ? 'Schedules / Exhibits only'
    : 'All content';

  // FIX 19 breakdown table — surface every distinct flag value so triage
  // severity is visible without opening the data sheet. Stable sort by
  // count (desc) so the noisiest flag class lands at the top of each
  // section. An empty section is omitted entirely (avoids "Parse Flags: 0"
  // noise on clean documents).
  const fix19BreakdownRows: any[][] = [];
  const pushBreakdownSection = (title: string, counts: Record<string, number>) => {
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (!entries.length) return;
    fix19BreakdownRows.push(['', '']);
    fix19BreakdownRows.push([title, '']);
    for (const [name, n] of entries) {
      fix19BreakdownRows.push([`  · ${name}`, n]);
    }
  };
  pushBreakdownSection('Label Flags',       fix19LabelFlagCounts);
  pushBreakdownSection('Parse Flags',       fix19ParseFlagCounts);
  pushBreakdownSection('Display Flags',     fix19DisplayFlagCounts);
  pushBreakdownSection('Currency Flags',    fix19CurrencyFlagCounts);
  if (fix19DescriptionFlagCount > 0) {
    fix19BreakdownRows.push(['', '']);
    fix19BreakdownRows.push(['Description Flags', '']);
    fix19BreakdownRows.push(['  · DESCRIPTION_FLAGGED', fix19DescriptionFlagCount]);
  }

  const sAoa: any[][] = [
    ['Contract Intelligence — Extraction Export', ''],
    ['', ''],
    ['Document', fileLabel],
    ['Generated', new Date().toLocaleString()],
    ['Export scope', scopeLabel],
    ['', ''],
    ['Clauses in document', clauses.length],
    ['  · Provisions', provisionClauseCount],
    ['  · Schedules / Exhibits', scheduleClauseCount],
    // Fix #9: 'rows in export' is the post-atomization count — what the
    // reviewer actually sees in the Captured Values sheet.
    ['Clauses in this export', expandedClauses.length],
    ['Values in this export', total],
    ['', ''],
    ['Approved', approved],
    ['Flagged', flagged],
    ['  · User-flagged', userFlagged],
    ['  · Auto-flagged (review-blocking)', fix19AutoFlaggedCount],
    ['Pending', pending],
    ['Edited', edited],
    ['', ''],
    // FIX 19 — informational flags surface separately so reviewers can
    // verify DISAMBIGUATED / SPACING_NORMALIZED actions happened without
    // those informational counts inflating the triage-blocking Flagged
    // tally. Spec rule 1 explicitly carves these out.
    ['Informational flags (not review-blocking)', fix19InformationalRowCount],
    // FIX 19 — per-flag-type breakdown table. Spec acceptance criterion
    // 2: "Summary contains a flag-type breakdown (counts per flag
    // value)". Each non-empty section appears once; clean documents see
    // no breakdown noise.
    ...fix19BreakdownRows,
  ];
  const sws = XLSX.utils.aoa_to_sheet(sAoa);
  sws['!cols'] = [{ wch: 24 }, { wch: 48 }];
  sws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Captured Values');
  XLSX.utils.book_append_sheet(wb, sws, 'Summary');
  const safe = fileLabel.replace(/[^a-z0-9._-]/gi, '_').slice(0, 60);
  XLSX.writeFile(wb, `${safe}.contract-intelligence.xlsx`);
}


// ─── FIX 27 — Structured (KDE-Mapped) Export ────────────────────────────────
// Generates a Loan IQ-format .xlsx with separate tabs:
//   Tab 1: Summary       — Agreement Header (FIX 23) + Parties + Lender Table (FIX 24)
//   Tab 2+: per facility — facility-level KDE block (FIX 25 categorical + FIX 24 commitments)
//   Final: Unattributed   — provisions not tagged to a facility (placeholder until FIX 21)
//   Final: Override_Log   — FIX 31 audit trail (header always present, even if empty)
// Loan IQ formatting rules per Domingo's Gina template:
//   - Dates  → DD/MM/YYYY
//   - Pcts   → numeric only (1.75 not "1.75%")
//   - Money  → numeric + currency column
// Reads the FIX 23/24/25/31 outputs from result.schema_v2 (assembled
// server-side by schema_v2_assembler.py). Falls back to a stub workbook
// containing a single "schema_v2 unavailable" sheet when the assembler
// returned null, so the button never silently fails.

function _formatLoanIqDate(iso: string | null): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function _sheetNameSafe(name: string): string {
  // Excel sheet names: max 31 chars, no : \ / ? * [ ]
  return name.replace(/[:\\/?*\[\]]/g, ' ').slice(0, 31).trim() || 'Sheet';
}

async function downloadStructuredXlsx(fileLabel: string, schema: SchemaV2 | null | undefined, verification?: ContractVerification) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();

  if (!schema) {
    const fallback = XLSX.utils.aoa_to_sheet([
      ['Structured Export unavailable'],
      ['The schema_v2 assembler did not return data for this file.'],
      ['Use the legacy "Download .xlsx" button instead, or check backend logs for FIX 27 errors.'],
    ]);
    XLSX.utils.book_append_sheet(wb, fallback, 'Summary');
    const safeName = fileLabel.replace(/[^a-z0-9._-]/gi, '_').slice(0, 60);
    XLSX.writeFile(wb, `${safeName}.loan-iq-structured.xlsx`);
    return;
  }

  // ── Tab 1: Summary ────────────────────────────────────────────────────
  const summaryRows: (string | number)[][] = [
    ['AGREEMENT HEADER (KDEs 1-4)'],
    ['Field', 'Value', 'Confidence'],
  ];
  const ah = schema.agreement_header;
  if (ah) {
    // FC-8: read per-field confidence; fall back to aggregate only when absent,
    // and mark the fallback explicitly so reviewers can see it was borrowed.
    const _fc = ah.field_confidence;
    const _fieldConf = (field: string): string => {
      if (_fc && field in _fc) {
        const v = _fc[field];
        return v != null ? v : 'UNKNOWN';
      }
      // Per-field value absent → borrow aggregate, label it so it is not
      // mistaken for a field-specific measurement.
      return ah.confidence ? `${ah.confidence} (AGGREGATE)` : 'UNKNOWN';
    };

    summaryRows.push(['Borrower',     ah.borrower     || '<MISSING — please verify>', _fieldConf('borrower')]);
    // I-P5: deal-size column sources aggregate_commitment_asserted ONLY.
    // If asserted is absent → blank + DEAL_SIZE_NOT_ASSERTED flag. Never derived.
    const _assertedDisplay = ah.aggregate_commitment_asserted?.display ?? null;
    if (process.env.NODE_ENV !== 'production' && !_assertedDisplay && ah.deal_amount_derived) {
      // I-P5 invariant check: derived must never fill the deal-size column.
      console.warn('[I-P5] deal_amount_derived present but aggregate_commitment_asserted absent — export cell blanked per policy');
    }
    summaryRows.push(['Deal Size',    _assertedDisplay ?? '', _assertedDisplay ? _fieldConf('deal_size') : 'DEAL_SIZE_NOT_ASSERTED']);
    if (ah.deal_amount_derived) {
      const _derivedLabel = `Deal Size — derived (${ah.deal_amount_derived.completeness_qualifier ?? 'diagnostic'})`;
      summaryRows.push([_derivedLabel, ah.deal_amount_derived.display, '']);
    }
    if (ah.ASSERTED_COMPUTED_DIVERGENCE) {
      summaryRows.push(['Deal Size — divergence', `asserted vs derived delta ${ah.ASSERTED_COMPUTED_DIVERGENCE.delta_pct}%`, 'ASSERTED_COMPUTED_DIVERGENCE']);
    }
    summaryRows.push(['Closing Date', _formatLoanIqDate(ah.closing_date) || '<MISSING — please verify>', _fieldConf('closing_date')]);
    summaryRows.push(['Admin Agent',  ah.admin_agent  || '<MISSING — please verify>', _fieldConf('admin_agent')]);
    if (ah.time_budget_exceeded && ah.time_budget_exceeded.length > 0) {
      summaryRows.push(['TIME_BUDGET_EXCEEDED', ah.time_budget_exceeded.join(', '), 'WARNING — fields above may be incomplete']);
    }
  } else {
    summaryRows.push(['Agreement Header', '<MISSING — assembler returned null>', '—']);
  }
  summaryRows.push([''], [''], ['PARTIES ROSTER']);
  summaryRows.push(['Name', 'Role', 'Aliases', 'First Page']);
  for (const p of schema.parties?.parties ?? []) {
    summaryRows.push([p.name, p.role, (p.aliases || []).join(', '), p.first_page]);
  }
  summaryRows.push([''], [''], ['LENDER COMMITMENT TABLE']);
  summaryRows.push(['Lender', 'Facility', 'Commitment Amount', 'Currency', 'Share %']);
  for (const l of schema.parties?.lender_table ?? []) {
    summaryRows.push([
      l.lender_name,
      l.facility || '',
      l.commitment_amount,
      l.currency,
      // Round to 2 dp — raw float math leaks "26.479999999999997" into the client sheet.
      l.commitment_share != null ? Math.round(l.commitment_share * 10000) / 100 : '',
    ]);
  }
  summaryRows.push([''], [''], ['COMPLETENESS']);
  const cs = schema.completeness_summary;
  summaryRows.push(['Header KDEs found', `${cs.header_kdes_found} / ${cs.header_kdes_total}`, '']);
  summaryRows.push(['Facility KDE avg %', cs.facility_kdes_found_avg_pct, '']);
  summaryRows.push(['Facility count',    cs.facility_count, '']);
  // FIX 18: surface the recall-gap counter on the Summary tab so reviewers
  // see at a glance whether the document carried unextracted material terms
  // (full detail lives on the Material_Term_Audit sheet).
  if (cs.material_term_flags_count != null) {
    summaryRows.push([
      'Material terms MISSING',
      cs.material_term_flags_count,
      cs.material_term_flags_count > 0 ? '⚠ see Material_Term_Audit tab' : '',
    ]);
  }
  if (schema.parties?.total_commitment_verified === false) {
    summaryRows.push([''], ['⚠ Sum of lender commitments does NOT match expected total',
                            `actual ${schema.parties.actual_total} vs expected ${schema.parties.expected_total}`]);
  }
  // I-Y1 — surface verification status so yield_low is visible in the export,
  // not just in the UI banner. Without this a near-total extraction failure
  // silently downloads a near-empty Excel with no signal.
  if (verification) {
    summaryRows.push([''], [''], ['VERIFICATION']);
    summaryRows.push(['Status', verification.status]);
    if (verification.summary?.text_coverage != null) {
      summaryRows.push(['Text coverage', `${(verification.summary.text_coverage * 100).toFixed(1)}%`]);
    }
    const yieldIssues = (verification.issues || []).filter(i => i.code === 'extraction_yield_low');
    for (const iss of yieldIssues) {
      summaryRows.push(['⚠ EXTRACTION_YIELD_LOW', iss.message]);
    }
    // Attribution yield (Asana 1218033424808341) — I-P3: the flag the backend
    // sets must survive into the delivered workbook.
    const attrIssues = (verification.issues || []).filter(i => i.code === 'attribution_yield_low');
    for (const iss of attrIssues) {
      summaryRows.push(['⚠ ATTRIBUTION_YIELD_LOW', iss.message]);
    }
    if (verification.summary?.attribution_rate != null) {
      summaryRows.push(['Provisions attributed to a facility', `${(verification.summary.attribution_rate * 100).toFixed(1)}%`]);
    }
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

  // ── Tab 2+: One per facility ──────────────────────────────────────────
  for (const f of schema.facilities) {
    // Loan IQ display layer (2026-09): when the backend supplied short values,
    // the sheet shows them in the Value column and keeps the verbatim clause
    // beside it so nothing extracted is lost and a reviewer can verify.
    const loanIq = f.kdes_loan_iq || null;
    const facilityRows: (string | number)[][] = [
      [`FACILITY: ${f.facility_name}`],
      [`Type: ${f.facility_type}`, '', `Completeness: ${f.completeness_score}%`],
      [''],
      loanIq ? ['KDE Field', 'Value (Loan IQ)', 'Source clause (verbatim)', 'Derived by'] : ['KDE Field', 'Value'],
    ];
    for (const [k, v] of Object.entries(f.kdes)) {
      let display: string | number = v as string | number;
      if (v === 'MISSING') {
        display = '<MISSING — please verify>';
      } else if (v === 'NOT_FOUND_IN_DOCUMENT') {
        display = '<NOT FOUND IN DOCUMENT — please verify>';
      } else if (Array.isArray(v)) {
        // FIX 26 follow-up — pricing_grid arrives as an array of tier objects
        // [{level, trigger_condition, applicable_margin_pct, sofr_floor}].
        // The default xlsx serialization renders arrays as "[object Object]"
        // which Ryan's QA reads as "no structured grid". Render each tier on
        // its own line so the reviewer sees the actual rows in the cell.
        display = v.map((row: any, i: number) => {
          if (row && typeof row === 'object') {
            const lvl   = row.level ?? `Tier ${i + 1}`;
            const cond  = row.trigger_condition || '—';
            const pct   = row.applicable_margin_pct != null
                          ? `${(row.applicable_margin_pct * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%`
                          : '—';
            const floor = row.sofr_floor != null
                          ? `${(row.sofr_floor * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%`
                          : '—';
            return `${lvl} | Trigger: ${cond} | Margin: ${pct} | Floor: ${floor}`;
          }
          return String(row);
        }).join('\n');
      }
      const trim = loanIq ? loanIq[k] : undefined;
      if (loanIq) {
        if (trim && trim.method !== 'as-is' && trim.method !== 'verbatim' && trim.value !== display) {
          facilityRows.push([k, trim.value, display, trim.method === 'rule' ? 'rule' : 'model, verified against source']);
        } else if (trim && trim.flag) {
          facilityRows.push([k, display, '', `verbatim (${trim.flag})`]);
        } else {
          facilityRows.push([k, display, '', '']);
        }
      } else {
        facilityRows.push([k, display]);
      }
    }
    // TRACK A (2026-07-31): surface per-KDE reviewer flags below the KDE
    // table. Flags include COMMITMENT_UNRESOLVED, SOURCES_DISAGREE,
    // SINGLE_ROW_NO_FACILITY_DISCRIMINATOR, etc. — absent means clean.
    if (f.kde_flags && Object.keys(f.kde_flags).length > 0) {
      facilityRows.push(['']);
      facilityRows.push(['KDE FLAGS — reviewer attention required', '']);
      for (const [fk, fv] of Object.entries(f.kde_flags)) {
        facilityRows.push([`⚠ ${fk}`, String(fv)]);
      }
    }
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(facilityRows),
      _sheetNameSafe(f.facility_name),
    );
  }

  // ── Unattributed Provisions ───────────────────────────────────────────
  const unattributedRows: (string | number)[][] = [
    ['UNATTRIBUTED PROVISIONS'],
    ['(Provisions not tagged to a specific facility — requires FIX 21 facility grouping.)'],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(unattributedRows), 'Unattributed');

  // ── Material Term Audit (FIX 18) ──────────────────────────────────────
  // Surfaces deal-defining terms that the backend material_term_auditor
  // detected in the source document but were absent from any KDE field.
  // The sheet is rendered unconditionally so the reviewer (and the client
  // QA workflow) always has visible proof that the recall gate ran — even
  // when zero gaps were detected. A silent-skip on clean docs was hiding
  // the audit pass and causing client retests to read as 'fix not applied'.
  // Only suppress the sheet on legacy payloads that pre-date the field.
  const materialFlags = schema.material_term_flags;
  if (materialFlags !== undefined) {
    const flags = materialFlags || [];
    const auditRows: (string | number)[][] = [
      ['MATERIAL TERM AUDIT — recall-gap flags'],
      ['(Terms detected in the source document but absent from extracted KDEs.)'],
      [`Audit ran: ${flags.length} material term gap(s) detected`],
      [''],
    ];
    if (flags.length === 0) {
      auditRows.push(
        ['Status', 'CLEAN — all checklist material KDEs present in extraction'],
        ['Checked KDEs', 'facility_commitment_global, maturity_date, margin_pct, fees'],
        ['Checked doc-level terms', 'Loan Limits (per jurisdiction), Tranche Maturity Dates, FCCR, DSCR, Commitment Fee Tiers'],
        [''],
        ['Note', 'Empty audit means no deal-defining term was found in the source text without a matching extracted value. This is the expected pass state.'],
      );
    } else {
      auditRows.push(['KDE ID', 'Term Name', 'Status', 'Page Ref', 'Note']);
      for (const f of flags) {
        auditRows.push([
          f.kde_id || '',
          f.term_name || '',
          f.status || 'MISSING',
          f.page_ref || '',
          f.note || '',
        ]);
      }
    }
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(auditRows),
      'Material_Term_Audit',
    );
  }

  // ── Override_Log (FIX 31) ─────────────────────────────────────────────
  const overrideRows = schema.override_log_rows && schema.override_log_rows.length > 0
    ? schema.override_log_rows
    : [['Document_ID', 'Clause', 'Facility', 'KDE_Label', 'Original_Value',
        'Override_Value', 'Override_Type', 'Rationale', 'Reviewer_ID',
        'Timestamp', 'Source_Clarification']];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(overrideRows), 'Override_Log');

  // ── Demoted_Facilities (TRACK A) ─────────────────────────────────────
  // Facilities demoted from the grid during sublimit/incremental demotion.
  // Always rendered so the reviewer can verify demotion was intentional.
  const demotedRows: (string | number)[][] = [
    ['TRACK A — DEMOTED FACILITIES (sublimits / incrementals removed from grid)'],
    ['(These facilities were detected but removed because they lack independent commitment evidence.)'],
    [''],
    ['Facility Name', 'Reason'],
  ];
  const demoted = schema.demoted_candidates || [];
  if (demoted.length === 0) {
    demotedRows.push(['(none — no facilities were demoted)', '']);
  } else {
    for (const d of demoted) {
      demotedRows.push([d.facility_name, d.reason]);
    }
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(demotedRows), 'Demoted_Facilities');

  // ── _failed_blocks (if any) ───────────────────────────────────────────
  if (schema._failed_blocks && schema._failed_blocks.length > 0) {
    const failedRows: (string | number)[][] = [
      ['FAILED ASSEMBLY BLOCKS (graceful degradation)'],
      ['Block', 'Error'],
      ...schema._failed_blocks.map(b => [b.block, b.error]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(failedRows), '_failed_blocks');
  }

  const safeName = fileLabel.replace(/[^a-z0-9._-]/gi, '_').slice(0, 60);
  XLSX.writeFile(wb, `${safeName}.loan-iq-structured.xlsx`);
}


// ─── Combined multi-document Structured Export ───────────────────────────────
// Aggregates facilities from ALL uploaded files into one workbook. Each facility
// sheet is populated exclusively from its own source document's schema_v2 — no
// facility object is ever reused across documents. Fixes the duplication bug
// (Task 1217065862795813) where the first doc's facility was mistakenly written
// into every subsequent doc's slot.
async function downloadCombinedStructuredXlsx(results: ContractAnalysisFileResult[]) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();

  const usable = results.filter(r => !r.error && r.schema_v2);
  if (usable.length === 0) {
    const s = XLSX.utils.aoa_to_sheet([['No schema_v2 available for any uploaded document.']]);
    XLSX.utils.book_append_sheet(wb, s, 'Summary');
    XLSX.writeFile(wb, 'combined-loan-iq-structured.xlsx');
    return;
  }

  // ── Combined Summary tab ────────────────────────────────────────────────
  const summaryRows: (string | number)[][] = [
    ['COMBINED STRUCTURED EXPORT'],
    [`${usable.length} document(s) included`],
    [''],
  ];
  for (const r of usable) {
    const schema = r.schema_v2!;
    const ah = schema.agreement_header;
    summaryRows.push([`DOCUMENT: ${r.file}`]);
    summaryRows.push(['Field', 'Value']);
    summaryRows.push(['Borrower',     ah?.borrower     || '<MISSING>']);
    // I-P5: deal-size column sources aggregate_commitment_asserted ONLY (combined export).
    const _assertedDisp = ah?.aggregate_commitment_asserted?.display ?? null;
    summaryRows.push(['Deal Size',    _assertedDisp ?? '', _assertedDisp ? '' : 'DEAL_SIZE_NOT_ASSERTED']);
    if (ah?.deal_amount_derived) {
      summaryRows.push([`Deal Size — derived (${ah.deal_amount_derived.completeness_qualifier ?? 'diagnostic'})`, ah.deal_amount_derived.display]);
    }
    summaryRows.push(['Admin Agent',  ah?.admin_agent  || '<MISSING>']);
    if (ah?.time_budget_exceeded && ah.time_budget_exceeded.length > 0) {
      summaryRows.push(['TIME_BUDGET_EXCEEDED', ah.time_budget_exceeded.join(', ')]);
    }
    summaryRows.push(['Facilities',   schema.facilities.map(f => f.facility_name).join(', ') || '(none)']);
    if (r.verification && r.verification.status !== 'ok') {
      summaryRows.push(['Verification', r.verification.status]);
      const yieldIssue = (r.verification.issues || []).find((i: any) => i.code === 'extraction_yield_low');
      if (yieldIssue) summaryRows.push(['⚠ EXTRACTION_YIELD_LOW', yieldIssue.message]);
      const attrIssue = (r.verification.issues || []).find((i: any) => i.code === 'attribution_yield_low');
      if (attrIssue) summaryRows.push(['⚠ ATTRIBUTION_YIELD_LOW', attrIssue.message]);
    }
    summaryRows.push(['']);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

  // ── Per-document, per-facility sheets ───────────────────────────────────
  // IMPORTANT: each facility block reads exclusively from its own result's
  // schema_v2 — never from another document's object. This is the fix for
  // the duplication bug: prior code reused the first document's facility list.
  const usedSheetNames = new Set<string>();
  for (const r of usable) {
    const schema = r.schema_v2!;
    const docStem = r.file.replace(/\.[^.]+$/, '').replace(/[^a-z0-9]/gi, '_').slice(0, 12);

    for (const f of schema.facilities) {
      const facilityRows: (string | number)[][] = [
        [`DOCUMENT: ${r.file}`],
        [`FACILITY: ${f.facility_name}`],
        [`Type: ${f.facility_type}`, '', `Completeness: ${f.completeness_score}%`],
        [''],
        ['KDE Field', 'Value'],
      ];
      for (const [k, v] of Object.entries(f.kdes)) {
        let display: string | number = v as string | number;
        if (v === 'MISSING') display = '<MISSING — please verify>';
        else if (v === 'NOT_FOUND_IN_DOCUMENT') display = '<NOT FOUND IN DOCUMENT — please verify>';
        else if (Array.isArray(v)) display = (v as any[]).map(String).join('\n');
        facilityRows.push([k, display]);
      }
      if (f.kde_flags && Object.keys(f.kde_flags).length > 0) {
        facilityRows.push(['']);
        facilityRows.push(['KDE FLAGS — reviewer attention required', '']);
        for (const [fk, fv] of Object.entries(f.kde_flags)) {
          facilityRows.push([`⚠ ${fk}`, String(fv)]);
        }
      }
      // Sheet name: doc prefix + facility, deduplicated
      let sheetName = `${docStem}__${_sheetNameSafe(f.facility_name)}`.slice(0, 31);
      let attempt = 0;
      while (usedSheetNames.has(sheetName)) {
        sheetName = `${sheetName.slice(0, 28)}_${++attempt}`;
      }
      usedSheetNames.add(sheetName);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(facilityRows), sheetName);
    }

    // Demoted candidates for this document
    const demoted = schema.demoted_candidates || [];
    if (demoted.length > 0) {
      const dRows: (string | number)[][] = [
        [`DOCUMENT: ${r.file} — TRACK A DEMOTIONS`],
        ['Facility Name', 'Reason'],
        ...demoted.map(d => [d.facility_name, d.reason]),
      ];
      let dName = `${docStem}__Demoted`.slice(0, 31);
      let di = 0;
      while (usedSheetNames.has(dName)) dName = `${dName.slice(0,28)}_${++di}`;
      usedSheetNames.add(dName);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(dRows), dName);
    }
  }

  XLSX.writeFile(wb, 'combined-loan-iq-structured.xlsx');
}


// ─── File-tab nav — true tab switching, one document visible at a time ───────

function FileNav({
  results,
  selectedIdx,
  onSelect,
}: {
  results: ContractAnalysisFileResult[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
}) {
  if (results.length < 2) return null;
  return (
    <div className="-mx-1 mb-3 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 rounded-xl border border-gray-200 p-2 shadow-sm">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-2 py-1.5">Files</span>
        {results.map((r, i) => {
          const isActive = selectedIdx === i;
          const failed = !!r.error;
          return (
            <button
              key={i}
              onClick={() => onSelect(i)}
              className={[
                'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-all max-w-[280px]',
                isActive
                  ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                  : failed
                    ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300',
              ].join(' ')}
              title={r.file}
            >
              {failed
                ? <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                : <FileText className="w-3 h-3 flex-shrink-0" />}
              <span className="truncate">{r.file}</span>
              {!failed && r.summary && (
                <span className={`text-[10px] flex-shrink-0 ${isActive ? 'text-sky-100' : 'text-gray-400'}`}>
                  · {r.summary.total_clauses}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─── Verification banner — surfaces backend extraction issues only ──────────
// The Download button moved to the new workspace toolbar, but per-file
// verification warnings still live here as their own banner.

// ─── FIX 21 Phase 2-lite — Facility Registry (diagnostic) ──────────────────
// Read-only. Renders schema_v2.facility_registry (the assembler recompute),
// and reports whether the threaded stage output (facility_registry_stage)
// arrived alongside it — that side-by-side is the Phase 2 acceptance
// evidence QA is asked to check. Nothing here is editable and nothing here
// feeds the export; this panel exists so a live session can be inspected
// without a database query. Degrades to null when the key is absent so
// sessions stored before this build render exactly as they do today.

const _REGISTRY_FLAG_BLURB: Record<string, string> = {
  NO_DECLARED_ENUMERATION_FOUND:
    'No declared facility enumeration was found in the document — the roster below came from heading heuristics, not from the agreement\u2019s own list. Expected on documents that never enumerate their facilities.',
  EMPTY_REGISTRY: 'The registry produced no facilities at all.',
  REGISTRY_STAGE_DID_NOT_RUN:
    'The extractor\u2019s facility-identity stage did not run — this is not the same as running and finding nothing.',
};

function FacilityRegistryPanel({
  block,
  stage,
}: {
  block?: FacilityRegistryBlock | null;
  stage?: FacilityRegistryStage | null;
}) {
  const [open, setOpen] = useState(false);

  // Silent degrade: nothing to diagnose, render nothing.
  if (!block && !stage) return null;

  const facilities = block?.facilities || [];
  const sublimits  = block?.sublimits  || [];
  const flags      = block?.flags      || [];
  // Prefer the assembler's own count; fall back to counting so a partial
  // payload still shows a truthful number rather than a blank.
  const declaredCount = block?.declared_count
    ?? facilities.filter(f => f.status === 'DECLARED').length;

  const stageFacilities = stage?.facilities || [];
  const stageDeclared = stageFacilities.filter(f => f.status === 'DECLARED').length;
  const countsAgree = !!stage && stageDeclared === declaredCount;

  return (
    <div className="border border-slate-200 bg-slate-50 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
            <ScrollText className="w-4 h-4 flex-shrink-0" />
            <span>Facility Registry (diagnostic)</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600">
              read-only
            </span>
          </div>
          <p className="text-xs mt-1 text-slate-600">
            <span className="font-semibold">{facilities.length}</span> facilit{facilities.length === 1 ? 'y' : 'ies'}
            {' · '}
            <span className="font-semibold">{declaredCount}</span> declared
            {' · '}
            <span className="font-semibold">{sublimits.length}</span> sublimit{sublimits.length === 1 ? '' : 's'}
            {block?.source && <> · <span className="font-mono text-[11px]">{block.source}</span></>}
          </p>
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:underline flex-shrink-0"
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {open ? 'Hide' : 'Show'} detail
        </button>
      </div>

      {/* Flags are the point of the panel — surface them whether or not the
          detail section is expanded. */}
      {flags.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {flags.map((flag, i) => (
            <li
              key={`${flag}-${i}`}
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2"
            >
              <AlertTriangle className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
              <span className="min-w-0">
                <span className="font-mono text-[11px] font-bold text-amber-900">{flag}</span>
                {_REGISTRY_FLAG_BLURB[flag] && (
                  <span className="block text-[11px] text-amber-800 mt-0.5">
                    {_REGISTRY_FLAG_BLURB[flag]}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          {facilities.length === 0 ? (
            <p className="text-xs italic text-slate-500">
              The registry returned no facilities for this document.
            </p>
          ) : (
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-slate-500 mb-1.5">
                Facilities
              </div>
              <ul className="space-y-1">
                {facilities.map((f, i) => (
                  <li
                    key={f.facility_id || `${f.display_name}-${i}`}
                    className="flex items-start gap-2 text-xs text-slate-700"
                  >
                    <span
                      className={
                        'px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0 '
                        + (f.status === 'DECLARED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-200 text-slate-600')
                      }
                      title={f.status === 'DECLARED'
                        ? 'Named in the agreement\u2019s own facility enumeration'
                        : 'Inferred from headings — not declared by the agreement'}
                    >
                      {f.status || 'UNKNOWN'}
                    </span>
                    <span className="min-w-0">
                      <span className="font-medium">{f.display_name || f.facility_id || '—'}</span>
                      {f.facility_type && (
                        <span className="text-slate-500"> · {f.facility_type}</span>
                      )}
                      {!!(f.aliases && f.aliases.length) && (
                        <span className="block text-[11px] text-slate-500">
                          aka {f.aliases.join(', ')}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sublimits.length > 0 && (
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-slate-500 mb-1.5">
                Sublimits &amp; carve-outs
              </div>
              <ul className="space-y-1">
                {sublimits.map((s, i) => (
                  <li key={`${s.name}-${i}`} className="text-xs text-slate-700">
                    <span className="font-medium">{s.name || '—'}</span>
                    {s.kind && <span className="text-slate-500"> · {s.kind}</span>}
                    {s.parent_id && (
                      <span className="text-slate-500"> · under <span className="font-mono text-[11px]">{s.parent_id}</span></span>
                    )}
                    {s.reason && (
                      <span className="block text-[11px] text-slate-500">{s.reason}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Provenance comparison — the Phase 2 acceptance evidence. */}
          <div className="border-t border-slate-200 pt-2.5 text-[11px] text-slate-600">
            <div className="font-semibold text-slate-500 uppercase tracking-[0.5px] text-[10.5px] mb-1">
              Provenance
            </div>
            <div>
              <span className="font-mono">schema_v2.facility_registry</span> (recompute):{' '}
              <span className="font-semibold">{declaredCount}</span> declared of{' '}
              <span className="font-semibold">{facilities.length}</span>
            </div>
            <div className="mt-0.5">
              <span className="font-mono">facility_registry_stage</span> (threaded):{' '}
              {stage ? (
                <>
                  <span className="font-semibold">{stageDeclared}</span> declared of{' '}
                  <span className="font-semibold">{stageFacilities.length}</span>
                  {stage.registry_segment_note && (
                    <span className="font-mono text-slate-500"> · {stage.registry_segment_note}</span>
                  )}
                  {stage.registry_segment_note === 'FIRST_SEGMENT_ONLY' && (
                    <span className="block text-slate-500">
                      Segmented document ({stage.segment_count ?? '?'} segments) — the threaded
                      registry covers segment 1 only, so a lower count here is expected and is
                      not a disagreement. The recompute above covers the full text.
                    </span>
                  )}
                  {stage.registry_segment_note === 'WHOLE_DOCUMENT' && !countsAgree && (
                    <span className="block text-amber-700">
                      Counts differ on a single-segment document — worth a look.
                    </span>
                  )}
                </>
              ) : (
                <span className="italic text-slate-500">
                  not present (session predates the Node threading fix)
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function VerificationBanner({
  fileLabel,
  summary,
  verification,
}: {
  fileLabel: string;
  summary?: { total_clauses: number; total_quants: number };
  verification?: ContractVerification;
}) {
  const [showIssues, setShowIssues] = useState(false);
  const status = verification?.status || 'ok';
  const issues = verification?.issues || [];
  const isOk = status === 'ok' && issues.length === 0;

  const wrap = isOk
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-amber-50 border-amber-200 text-amber-800';

  return (
    <div className={`border rounded-xl p-4 ${wrap}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-bold">
            {isOk ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span className="truncate">{fileLabel}</span>
          </div>
          <p className="text-xs mt-1 opacity-90">
            <span className="font-semibold">{summary?.total_clauses ?? 0}</span> clause{(summary?.total_clauses ?? 0) === 1 ? '' : 's'}
            {' · '}
            <span className="font-semibold">{summary?.total_quants ?? 0}</span> quantitative value{(summary?.total_quants ?? 0) === 1 ? '' : 's'}
            {' · '}
            verification {isOk ? 'passed' : `flagged ${issues.length} item${issues.length === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>
      {issues.length > 0 && (
        <button
          onClick={() => setShowIssues(s => !s)}
          className="mt-3 flex items-center gap-1 text-xs font-semibold hover:underline"
        >
          {showIssues ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {showIssues ? 'Hide' : 'Show'} verification issues ({issues.length})
        </button>
      )}
      {showIssues && issues.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs">
          {issues.map((iss, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="font-mono font-bold mt-0.5">{iss.clause_no || '—'}</span>
              <span className="opacity-90">{iss.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ─── Source-text segmenting for inline mark highlighting ────────────────────
// Builds an ordered list of segments from the clause body, each tagged with
// the value record it belongs to (or undefined for plain text). Uses the
// extractor-supplied byte offset when available — falls back to a first-
// substring search so we degrade gracefully when offset is missing.

interface Segment {
  text: string;
  recordId?: string;
  type?: ValueType;
}

// [P11] Source Text readability. PDF extraction breaks every visual line into
// its own "\n", so a clause renders as a tall, hard-to-read column. This
// collapses those SOFT (mid-sentence) line breaks into spaces while KEEPING
// the meaningful ones — blank-line paragraph gaps, sentence/clause ends, and
// list / clause-number / heading starts.
//
// Length-preserving by construction: every original "\n" becomes exactly one
// character (a space when collapsed, or "\n" when kept), and the line bodies
// are untouched — so value-highlight offsets into raw_text stay valid and the
// downstream extractor (which reads the unmodified backend raw_text) is never
// affected. This is a display-only transform.
function reflowSourceText(text: string): string {
  if (!text) return text;
  const lines = text.split('\n');
  let out = lines[0];
  for (let i = 1; i < lines.length; i++) {
    const prev = lines[i - 1].trimEnd();
    const curTrim = lines[i].trimStart();
    const keepBreak =
      prev === '' || curTrim === '' ||                       // paragraph gap
      /[.:;]["'')\]]?$/.test(prev) ||                        // sentence / clause end
      /^\(?[a-z0-9ivxIVX]{1,4}[)\.]\s/.test(curTrim) ||      // "(a)", "(iv)", "1." list marker
      /^[••\-–—*]\s/.test(curTrim) ||                   // bullet
      /^(ARTICLE|SECTION|Article|Section|Schedule|Exhibit|Annex|Part)\b/.test(curTrim) || // heading kw
      /^[A-Z][A-Z0-9 ,&/()'-]{6,}$/.test(curTrim);           // ALL-CAPS heading line
    out += (keepBreak ? '\n' : ' ') + lines[i];
  }
  return out;
}


function buildSourceSegments(text: string, records: ValueRecord[]): Segment[] {
  if (!text) return [];
  // Resolve a [start, end] span for each record. Skip if neither offset nor
  // a non-empty unique substring is available.
  type Span = { start: number; end: number; record: ValueRecord };
  const usedRanges: Span[] = [];
  for (const r of records) {
    let start: number | null = null;
    if (typeof r.offset === 'number' && r.offset >= 0 && r.offset < text.length) {
      // Verify the offset actually points at the phrase (the Python extractor
      // is reliable but the raw_text we receive may have been lightly
      // post-processed). If it doesn't match, fall through to search.
      if (text.slice(r.offset, r.offset + r.phrase.length) === r.phrase) {
        start = r.offset;
      }
    }
    if (start == null && r.phrase) {
      // First occurrence not already consumed by another mark.
      let from = 0;
      while (from < text.length) {
        const idx = text.indexOf(r.phrase, from);
        if (idx < 0) break;
        const claimed = usedRanges.some(s => idx < s.end && idx + r.phrase.length > s.start);
        if (!claimed) { start = idx; break; }
        from = idx + 1;
      }
    }
    if (start == null) continue;
    usedRanges.push({ start, end: start + r.phrase.length, record: r });
  }
  usedRanges.sort((a, b) => a.start - b.start);

  const segments: Segment[] = [];
  let cursor = 0;
  for (const span of usedRanges) {
    if (span.start < cursor) continue;
    if (span.start > cursor) segments.push({ text: text.slice(cursor, span.start) });
    segments.push({ text: text.slice(span.start, span.end), recordId: span.record.id, type: span.record.type });
    cursor = span.end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor) });
  return segments;
}


// ─── Source-text renderer with marks ────────────────────────────────────────

function SourceTextWithMarks({
  text,
  records,
  liveById,
  hoverId,
  setHoverId,
  recordIndexById,
}: {
  text: string;
  records: ValueRecord[];
  liveById: Map<string, LiveValueState>;
  hoverId: string | null;
  setHoverId: (id: string | null) => void;
  recordIndexById: Map<string, number>;
}) {
  // [P11] Reflow soft line breaks for readability before segmenting. Offset-
  // preserving, so value-highlight spans still line up with raw_text.
  const segments = useMemo(() => buildSourceSegments(reflowSourceText(text), records), [text, records]);

  return (
    <p className="m-0 font-serif text-[13.8px] leading-[1.75] text-[#2c2a25] whitespace-pre-wrap">
      {segments.map((seg, i) => {
        if (!seg.recordId) return <React.Fragment key={i}>{seg.text}</React.Fragment>;
        const live = liveById.get(seg.recordId) || { status: 'pending', edited: false };
        const type = seg.type || 'count';
        const tok = TYPE_TOKENS[type];
        const isHovered = hoverId === seg.recordId;
        const isFlagged = live.status === 'flagged';
        const isApproved = live.status === 'approved';
        const idx = recordIndexById.get(seg.recordId);
        // Per-state styles
        let cls = `relative px-[3px] py-[1px] rounded-[3px] font-medium cursor-pointer transition-colors duration-100 `;
        let styleColor: React.CSSProperties = {};
        if (isHovered) {
          cls += isFlagged
            ? `bg-[#c0503c] text-white shadow-[0_1px_6px_rgba(192,80,60,0.4)] `
            : `text-white shadow-[0_1px_6px_rgba(0,0,0,0.18)] `;
          if (!isFlagged) styleColor = { backgroundColor: TYPE_RAIL_HEX[type] };
        } else if (isFlagged) {
          cls += 'bg-[#fbeae6] text-[#c0503c] line-through ';
        } else if (isApproved) {
          cls += `${tok.tint} ${tok.text} `;
        } else {
          cls += `${tok.text} `;
        }
        // Subtle underline so pending marks read as marks
        if (!isHovered && !isFlagged) {
          styleColor = { ...styleColor, borderBottom: `2px solid ${TYPE_RAIL_HEX[type]}` };
        }
        return (
          <mark
            key={i}
            className={cls}
            style={styleColor}
            onMouseEnter={() => setHoverId(seg.recordId!)}
            onMouseLeave={() => setHoverId(null)}
          >
            {seg.text}
            {typeof idx === 'number' && (
              <sup className="font-mono text-[9px] font-bold ml-[1px] opacity-90 align-super">
                {idx + 1}
              </sup>
            )}
          </mark>
        );
      })}
    </p>
  );
}


// ─── Operator chip ──────────────────────────────────────────────────────────

function OperatorChip({ sym, op }: { sym: string; op: string }) {
  if (!sym && !op) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-[1px] rounded-full bg-[#f5f3ee] border border-[#e7e4dc]">
      {sym && <span className="font-mono text-[12px] font-semibold text-[#1b1a17]">{sym}</span>}
      {op && <span className="text-[10.5px] font-medium text-[#52504a]">{op}</span>}
    </span>
  );
}


// ─── Inline edit form ───────────────────────────────────────────────────────

function InlineEditForm({
  record,
  live,
  numberIndex,
  onSave,
  onCancel,
}: {
  record: ValueRecord;
  live: LiveValueState;
  numberIndex: number;
  onSave: (next: Partial<LiveValueState>) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(live.label ?? record.label);
  const [sym,   setSym]   = useState(live.sym   ?? record.sym);
  const [op,    setOp]    = useState(live.op    ?? record.op);
  const [value, setValue] = useState(live.value ?? record.value);
  const [unit,  setUnit]  = useState(live.unit  ?? record.unit);
  const [note,  setNote]  = useState(live.note  ?? '');

  return (
    <div
      className="col-span-full rounded-[9px] border border-[#9ab8e0] bg-[#f7faff] p-[14px] mt-[2px]"
      style={{ gridColumn: '1 / -1' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center justify-center w-5 h-5 rounded-[5px] text-white font-mono text-[11px] font-bold ${TYPE_TOKENS[record.type].rail}`}
        >
          {numberIndex + 1}
        </span>
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-[#52504a]">
          Editing value · Corrections reset it to pending review
        </span>
      </div>
      <div className="space-y-2">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#8a877e] mb-0.5">Label</label>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 bg-white"
          />
        </div>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-2">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#8a877e] mb-0.5">Sym</label>
            <select
              value={sym}
              onChange={e => setSym(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 bg-white font-mono"
            >
              {['', '≤', '≥', '=', '<', '>', '≈'].map(s => (
                <option key={s || 'none'} value={s}>{s || '—'}</option>
              ))}
            </select>
          </div>
          <div className="col-span-4">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#8a877e] mb-0.5">Context</label>
            <input
              type="text"
              value={op}
              onChange={e => setOp(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 bg-white"
            />
          </div>
          <div className="col-span-3">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#8a877e] mb-0.5">Value</label>
            <input
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 bg-white font-mono"
            />
          </div>
          <div className="col-span-3">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#8a877e] mb-0.5">Unit</label>
            <input
              type="text"
              value={unit}
              onChange={e => setUnit(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 bg-white font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#8a877e] mb-0.5">Note</label>
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Reason for correction or flag…"
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 bg-white"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-semibold text-[#52504a] bg-white border border-[#e7e4dc] rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ label, sym, op, value, unit, note, status: 'pending', edited: true })}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-[#1b1a17] rounded-md hover:bg-[#2c2a25]"
          >
            Save correction
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Value record card ──────────────────────────────────────────────────────

function ValueRecordCard({
  record,
  live,
  numberIndex,
  density,
  isHovered,
  isEditing,
  onHover,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onToggleApprove,
  onToggleFlag,
}: {
  record: ValueRecord;
  live: LiveValueState;
  numberIndex: number;
  density: 'comfortable' | 'compact';
  isHovered: boolean;
  isEditing: boolean;
  onHover: (id: string | null) => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (next: Partial<LiveValueState>) => void;
  onToggleApprove: () => void;
  onToggleFlag: () => void;
}) {
  if (isEditing) {
    return (
      <InlineEditForm
        record={record}
        live={live}
        numberIndex={numberIndex}
        onSave={onSaveEdit}
        onCancel={onCancelEdit}
      />
    );
  }

  const tok = TYPE_TOKENS[record.type];
  const status = live.status;
  // Resolve user-edited display fields
  const dLabel = live.label ?? record.label;
  const dOp    = live.op    ?? record.op;
  const dSym   = live.sym   ?? record.sym;
  const dValue = live.value ?? record.value;
  const dUnit  = live.unit  ?? record.unit;

  let cardCls = 'flex items-start gap-[11px] rounded-[9px] border transition-shadow duration-100 ';
  if (status === 'approved') cardCls += 'border-[#cfe8da] bg-[#f4faf6] ';
  else if (status === 'flagged') cardCls += 'border-[#ecc9c4] bg-[#fdf4f2] ';
  else cardCls += 'border-[#e7e4dc] bg-white ';
  if (isHovered) cardCls += 'shadow-[0_2px_10px_rgba(0,0,0,0.07)] ';
  cardCls += density === 'compact' ? 'px-[11px] py-[9px] ' : 'px-[13px] py-[12px] ';

  return (
    <div
      className={cardCls}
      style={isHovered ? { borderColor: TYPE_RAIL_HEX[record.type] } : undefined}
      onMouseEnter={() => onHover(record.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Number badge */}
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-[5px] text-white font-mono text-[11px] font-bold flex-shrink-0 mt-[1px] ${tok.rail}`}
      >
        {numberIndex + 1}
      </span>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-1.5 flex-wrap">
          <span className={`w-[7px] h-[7px] rounded-[2px] flex-shrink-0 mt-[5px] ${tok.dot}`} />
          <span
            className="text-[13px] font-medium text-[#1b1a17] leading-snug flex-1 min-w-0"
            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'normal', overflowWrap: 'normal' }}
            title={dLabel || TYPE_LABEL[record.type]}
          >
            {dLabel || TYPE_LABEL[record.type]}
          </span>
          {live.edited && (
            <span className="text-[9.5px] font-bold uppercase tracking-wider text-[#7a6a3a] bg-[#f3ecd6] rounded px-1 py-[1px]">
              edited
            </span>
          )}
          {record.ocr_corrected && (
            <span
              className="text-[9.5px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1 py-[1px]"
              title="Value recovered from OCR correction (O→0, l→1, rnonths→months). Audit recommended."
            >
              ocr
            </span>
          )}
          {record.range_low && (
            <span
              className="text-[9.5px] font-bold uppercase tracking-wider text-[#52504a] bg-[#f5f3ee] border border-[#e7e4dc] rounded px-1 py-[1px]"
              title="Low endpoint of a numeric range (e.g. 8–14 days)."
            >
              ·low
            </span>
          )}
          {record.definition_compound && (
            <span
              className="text-[9.5px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 rounded px-1 py-[1px]"
              title={record.definition_alternative
                ? `${record.definition_compound_modifier || 'Either'} of: ${record.value} OR ${record.definition_alternative}`
                : 'Compound definition — alternative limb is a calculation, not a fixed value.'}
            >
              +alt
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mt-[6px] flex-wrap">
          <OperatorChip sym={dSym} op={dOp} />
          <span className="font-mono text-[10.5px] text-[#8a877e]">
            {record.src}
            {record.page != null && <> · p.{record.page}</>}
          </span>
        </div>

        {(live.note || record.original_value || record.resolved_from) && (
          <div className="mt-[6px] flex flex-col gap-[2px]">
            {record.original_value && (
              <span className="text-[11.5px] text-[#8a877e]">
                <span className="opacity-70">tracked change: </span>
                <span className="line-through opacity-70 mr-1">{record.original_value}</span>
                <span className="opacity-70">→</span> {record.value}
              </span>
            )}
            {record.resolved_from && (
              <span className="text-[11.5px] text-[#8a877e]">
                <span className="opacity-70">defined term: </span>{record.resolved_from}
              </span>
            )}
            {live.note && (
              <span className={`text-[11.5px] ${status === 'flagged' ? 'text-[#c0503c]' : 'text-[#8a877e]'}`}>
                <span className="opacity-70">✎ </span>{live.note}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Value — capped width so long spelled-out values ("three hundred
          sixty-five (365) days") wrap instead of squeezing the label. */}
      <div className="text-right flex-shrink min-w-0 max-w-[55%]">
        <div className="font-mono text-[16px] font-semibold text-[#1b1a17] tabular-nums leading-tight" style={{ wordBreak: 'normal', overflowWrap: 'normal' }}>
          {dValue}
        </div>
        <div className="font-mono text-[10.5px] text-[#8a877e] mt-[1px]">{dUnit}</div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-[5px] flex-shrink-0">
        <button
          onClick={onEdit}
          className="w-7 h-7 rounded-[7px] border border-[#cdc9bf] bg-white text-[#8a877e] hover:bg-gray-50 flex items-center justify-center"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onToggleFlag}
          className={
            status === 'flagged'
              ? 'w-7 h-7 rounded-[7px] border border-[#c0503c] bg-[#c0503c] text-white flex items-center justify-center'
              : 'w-7 h-7 rounded-[7px] border border-[#cdc9bf] bg-white text-[#8a877e] hover:bg-gray-50 flex items-center justify-center'
          }
          title={status === 'flagged' ? 'Unflag' : 'Flag'}
        >
          <Flag className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onToggleApprove}
          className={
            status === 'approved'
              ? 'w-7 h-7 rounded-[7px] border border-[#1f8a5b] bg-[#1f8a5b] text-white flex items-center justify-center'
              : 'w-7 h-7 rounded-[7px] border border-[#cdc9bf] bg-white text-[#8a877e] hover:bg-gray-50 flex items-center justify-center'
          }
          title={status === 'approved' ? 'Unapprove' : 'Approve'}
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


// ─── Per-clause flag badges (preserves all Doc 1-10 audit annotations) ──────

function ClauseFlagsBadges({ clause }: { clause: ContractClause }) {
  const hasConflicts  = !!(clause.conflicts_with && clause.conflicts_with.length);
  const hasAmbiguous  = !!clause.ambiguous;
  const hasSupersedes = !!clause.supersedes;
  const missingRefs   = clause.delegated_missing || [];
  const allRefs       = clause.delegated_to || [];
  const providedRefs  = allRefs.filter(r => !missingRefs.includes(r));
  const hasMissing    = missingRefs.length > 0;
  const hasProvided   = providedRefs.length > 0;
  const hasTruncated     = !!clause.truncated;
  // Fix #10: ignore footer-artifact quants when computing badge flags so a
  // doc-id stamp tagged as ocr_corrected never trips the OCR badge.
  const hasOcr           = !!usefulQuants(clause).some(q => q.ocr_corrected);
  const hasTrackedChanges = !!clause.tracked_changes;
  const compoundQuant     = usefulQuants(clause).find(q => q.definition_compound);
  const hasCompound       = !!compoundQuant;
  const hasCarveout       = !!clause.has_carveout;

  if (!hasConflicts && !hasAmbiguous && !hasSupersedes && !hasMissing &&
      !hasProvided && !hasTruncated && !hasOcr && !hasTrackedChanges &&
      !hasCompound && !hasCarveout) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {hasConflicts && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-amber-50 text-amber-900 border-amber-200"
              title={`Conflicts with: ${clause.conflicts_with!.join(', ')}`}>
          <AlertTriangle className="w-3 h-3" /> Conflicts
        </span>
      )}
      {hasAmbiguous && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-orange-50 text-orange-900 border-orange-200"
              title={clause.ambiguity_note || 'Ambiguous'}>
          <AlertTriangle className="w-3 h-3" /> Ambiguous
        </span>
      )}
      {hasSupersedes && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-sky-50 text-sky-900 border-sky-200"
              title={`Supersedes ${clause.supersedes}`}>
          → Supersedes
        </span>
      )}
      {hasMissing && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-rose-50 text-rose-900 border-rose-300"
              title={`Value delegated to ${missingRefs.join(', ')} — not provided`}>
          <AlertTriangle className="w-3 h-3" /> MISSING — {missingRefs.join(', ')}
        </span>
      )}
      {hasProvided && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-slate-50 text-slate-700 border-slate-200"
              title={`Delegated to: ${providedRefs.join(', ')}`}>
          Delegated: {providedRefs.join(', ')}
        </span>
      )}
      {hasTruncated && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-rose-50 text-rose-900 border-rose-300"
              title="Source document is an excerpt — pages marked as not reproduced.">
          <AlertTriangle className="w-3 h-3" /> Document truncated
        </span>
      )}
      {hasOcr && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-indigo-50 text-indigo-800 border-indigo-200"
              title="Values recovered from OCR letter-for-digit substitutions. Audit before use.">
          OCR-corrected
        </span>
      )}
      {hasTrackedChanges && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-amber-50 text-amber-900 border-amber-300"
              title="Clause carries values amended from an earlier version. Records show pre/post pair.">
          <AlertTriangle className="w-3 h-3" /> Tracked changes
        </span>
      )}
      {hasCompound && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-purple-50 text-purple-900 border-purple-200"
              title={compoundQuant?.definition_alternative
                ? `${compoundQuant.definition_compound_modifier || 'Either'} of: ${compoundQuant.value} OR ${compoundQuant.definition_alternative}`
                : 'Compound definition'}>
          <AlertTriangle className="w-3 h-3" /> Compound definition
        </span>
      )}
      {hasCarveout && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-md bg-orange-50 text-orange-900 border-orange-200"
              title="Clause contains a carve-out / exception / reduction phrase that materially modifies a numeric value.">
          <AlertTriangle className="w-3 h-3" /> Carve-out
        </span>
      )}
    </div>
  );
}


// ─── Clause row ─────────────────────────────────────────────────────────────

function ClauseReviewRow({
  clause,
  fileIdx,
  clauseIdx,
  liveById,
  setLive,
  hoverId,
  setHoverId,
  editingId,
  setEditingId,
  density,
  showPlain,
  gutterTop,
  zebra,
}: {
  clause: ContractClause;
  fileIdx: number;
  clauseIdx: number;
  liveById: Map<string, LiveValueState>;
  setLive: (id: string, next: Partial<LiveValueState>) => void;
  hoverId: string | null;
  setHoverId: (id: string | null) => void;
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  density: 'comfortable' | 'compact';
  showPlain: boolean;
  gutterTop: number;
  zebra: boolean;
}) {
  const records = useMemo<ValueRecord[]>(
    // Fix #10: footer-artifact quants are removed at the SOURCE of value
    // records so the UI cards, badges, edit form, and ID-keyed live state
    // all see a consistent, footer-free quant list.
    () => usefulQuants(clause).map((q, i) => quantToValueRecord(q, clause, i, fileIdx, clauseIdx)),
    [clause, fileIdx, clauseIdx],
  );
  const recordIndexById = useMemo(() => {
    const m = new Map<string, number>();
    records.forEach((r, i) => m.set(r.id, i));
    return m;
  }, [records]);

  const title = useMemo(() => deriveClauseTitle(clause), [clause]);
  const approvedCount = records.filter(r => (liveById.get(r.id)?.status || 'pending') === 'approved').length;
  const flaggedCount  = records.filter(r => (liveById.get(r.id)?.status || 'pending') === 'flagged').length;
  const allApproved   = records.length > 0 && approvedCount === records.length;

  const handleApproveAll = () => {
    if (allApproved) {
      records.forEach(r => setLive(r.id, { status: 'pending' }));
    } else {
      records.forEach(r => setLive(r.id, { status: 'approved' }));
    }
  };

  const isPreamble = clause.clause_no === '(Preamble)';
  const isComfortable = density === 'comfortable';
  const gutterWidth = isComfortable ? 168 : 150;
  const padX = isComfortable ? '34px' : '28px';
  const padY = isComfortable ? '24px' : '18px';
  const gap  = isComfortable ? '30px' : '22px';

  const isSchedule = isScheduleScopeClause(clause);
  const scheduleKind = clause.content_type === 'exhibit' ? 'Exhibit' : 'Schedule';
  // Muted, slightly cooler tint for schedule rows so they're visually
  // distinct from operative provisions when scope='all' is active.
  const baseBg = zebra ? '#faf9f6' : '#ffffff';
  const scheduleBg = zebra ? '#f1efe8' : '#f6f4ed';

  return (
    <div
      className="border-b-2 border-[#1b1a17]"
      style={{ background: isSchedule ? scheduleBg : baseBg }}
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: `${gutterWidth}px 1fr`,
          gap,
          padding: `${padY} ${padX}`,
        }}
      >
        {/* Gutter */}
        <div className="self-start" style={{ position: 'sticky', top: gutterTop }}>
          <div
            className="inline-block w-[80px] text-center rounded-[6px] bg-[#1b1a17] text-white font-mono text-[13.5px] font-semibold py-[3px] px-1"
            style={{ letterSpacing: '-0.2px' }}
          >
            {isPreamble ? 'Preamble' : `§${clause.clause_no}`}
          </div>
          <div className="font-sans text-[14px] font-semibold text-[#1b1a17] mt-2 leading-snug" style={{ letterSpacing: '-0.2px' }}>
            {title}
          </div>
          {clause.facility_name && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 mt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.4px] rounded-[5px] bg-[#e4eef7] text-[#2c5d86] w-fit"
              title={`Facility: ${clause.facility_name}${clause.facility_confidence ? ` (${clause.facility_confidence} confidence)` : ''}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#3a7bb0]" />
              {clause.facility_name}
            </div>
          )}
          {clause.page != null && (
            <div className="font-mono text-[11px] text-[#8a877e] mt-1">page {clause.page}</div>
          )}
          {isSchedule && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 mt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.4px] rounded-[5px] bg-[#ece8db] text-[#7a6a3a] w-fit"
              title={clause.schedule_name || `${scheduleKind} content`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#b89d4a]" />
              {scheduleKind}
            </div>
          )}
          {isSchedule && clause.schedule_name && (
            <div
              className="font-mono text-[10.5px] text-[#8a877e] mt-1 leading-tight"
              title={clause.schedule_name}
            >
              {clause.schedule_name}
            </div>
          )}
          {records.length > 0 && (
            <div className="mt-2 flex flex-col gap-1">
              <span className={
                allApproved
                  ? 'inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] font-semibold rounded-full bg-[#e9f5ee] text-[#1f8a5b] w-fit'
                  : 'inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] font-semibold rounded-full bg-[#f6efda] text-[#b0851f] w-fit'
              }>
                <span className={`w-1.5 h-1.5 rounded-full ${allApproved ? 'bg-[#1f8a5b]' : 'bg-[#c0892a]'}`} />
                {approvedCount}/{records.length} approved
              </span>
              {flaggedCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] font-semibold rounded-full bg-[#fbeae6] text-[#c0503c] w-fit">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c0503c]" />
                  {flaggedCount} flagged
                </span>
              )}
              <button
                onClick={handleApproveAll}
                className={
                  allApproved
                    ? 'mt-1 px-2 py-1 text-[11.5px] font-semibold rounded-md bg-white border border-[#e7e4dc] text-[#8a877e] hover:bg-gray-50'
                    : 'mt-1 px-2 py-1 text-[11.5px] font-semibold rounded-md bg-[#1f8a5b] text-white hover:bg-[#187549]'
                }
              >
                {allApproved ? 'Unapprove all' : `Approve all ${records.length}`}
              </button>
            </div>
          )}
          <ClauseFlagsBadges clause={clause} />
        </div>

        {/* Main */}
        <div className="min-w-0">
          <div className={showPlain ? 'grid gap-7' : 'grid gap-7'} style={{ gridTemplateColumns: showPlain ? '1.25fr 1fr' : '1fr' }}>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#8a877e] mb-1.5">Source Text</div>
              <SourceTextWithMarks
                text={clause.raw_text || ''}
                records={records}
                liveById={liveById}
                hoverId={hoverId}
                setHoverId={setHoverId}
                recordIndexById={recordIndexById}
              />
            </div>
            {showPlain && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#8a877e] mb-1.5">Plain English</div>
                {clause.plain_english === '— generating —'
                  ? <p className="m-0 font-sans text-[13.8px] leading-[1.7] italic text-sky-500 animate-pulse">Generating description…</p>
                  : clause.plain_english
                    ? <p className="m-0 font-sans text-[13.8px] leading-[1.7] text-[#52504a] whitespace-pre-wrap">{clause.plain_english}</p>
                    : <p className="m-0 font-sans text-[13.8px] leading-[1.7] italic text-amber-600">— missing —</p>}
              </div>
            )}
          </div>

          {records.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#8a877e]">Captured Values</span>
                <span
                  className="font-mono text-[11px] font-medium text-white rounded-full px-1.5"
                  style={{ background: TYPE_RAIL_HEX[records[0].type] }}
                >
                  {records.length}
                </span>
                <span className="flex-1 h-px bg-[#e7e4dc]" />
                <span className="text-[11px] text-[#8a877e]">hover ↔ source · edit · flag · approve</span>
              </div>
              <div
                className="grid gap-[9px]"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}
              >
                {records.map((r, i) => {
                  const live = liveById.get(r.id) || { status: 'pending', edited: false };
                  return (
                    <ValueRecordCard
                      key={r.id}
                      record={r}
                      live={live}
                      numberIndex={i}
                      density={density}
                      isHovered={hoverId === r.id}
                      isEditing={editingId === r.id}
                      onHover={setHoverId}
                      onEdit={() => setEditingId(r.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onSaveEdit={(next) => {
                        setLive(r.id, next);
                        setEditingId(null);
                      }}
                      onToggleApprove={() =>
                        setLive(r.id, { status: live.status === 'approved' ? 'pending' : 'approved' })
                      }
                      onToggleFlag={() =>
                        setLive(r.id, { status: live.status === 'flagged' ? 'pending' : 'flagged' })
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── The new Clause Review workspace (replaces v1's ClauseTable) ────────────

const SOFT_CAP = 200;

function ClauseReviewWorkspace({
  fileLabel,
  clauses,
  fileIdx,
  summary,
  schemaV2,
  verification,
}: {
  fileLabel: string;
  clauses: ContractClause[];
  fileIdx: number;
  summary?: { total_clauses: number; total_quants: number };
  schemaV2?: SchemaV2 | null;
  verification?: ContractVerification;
}) {
  // Live state — maps valueId → user overrides + status
  const [liveByValueId, setLiveByValueId] = useState<Map<string, LiveValueState>>(new Map());
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  // Default 'provisions' so schedule/exhibit rows are hidden on first load
  // (matches the client's spec — a 700-row PDF surfaces ~446 operative rows
  // by default with the remaining 254 schedule rows available behind the
  // toggle).
  const [scope, setScope] = useState<ScopeFilter>('provisions');
  const [density] = useState<'comfortable' | 'compact'>('comfortable');
  const [showPlain] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [headerH, setHeaderH] = useState(96);

  // Measure sticky header height so the per-clause gutter sticks just below it.
  const headerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!headerRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const h = (e.target as HTMLElement).offsetHeight;
        // Functional setState — avoids capturing headerH in the closure so
        // the effect can have empty deps (one-time observer setup) without
        // an ESLint exhaustive-deps disable comment.
        if (h) setHeaderH(prev => (prev === h ? prev : h));
      }
    });
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  const setLive = useCallback((id: string, next: Partial<LiveValueState>) => {
    setLiveByValueId(prev => {
      const cur = prev.get(id) || { status: 'pending' as ValueStatus, edited: false };
      const merged: LiveValueState = { ...cur, ...next };
      const m = new Map(prev);
      m.set(id, merged);
      return m;
    });
  }, []);

  // Build full record list once per (file, clauses) so search/filter is cheap.
  const allRecords = useMemo<Array<{ clause: ContractClause; record: ValueRecord; clauseIdx: number }>>(() => {
    const out: Array<{ clause: ContractClause; record: ValueRecord; clauseIdx: number }> = [];
    clauses.forEach((c, ci) => {
      // Fix #10: source-of-truth record list excludes footer/page-number
      // artifacts so document-level totals and search match the UI cards.
      usefulQuants(c).forEach((q, qi) => {
        out.push({ clause: c, record: quantToValueRecord(q, c, qi, fileIdx, ci), clauseIdx: ci });
      });
    });
    return out;
  }, [clauses, fileIdx]);

  // Document-level totals
  const totalValues = allRecords.length;
  const approvedTotal = allRecords.filter(r => (liveByValueId.get(r.record.id)?.status || 'pending') === 'approved').length;
  const flaggedTotal  = allRecords.filter(r => (liveByValueId.get(r.record.id)?.status || 'pending') === 'flagged').length;

  // Document-level scope counts so the toolbar pills can show how many rows
  // live in each scope without re-walking on every render.
  const provisionCount = useMemo(
    () => clauses.filter(c => !isScheduleScopeClause(c)).length,
    [clauses],
  );
  const scheduleCount = useMemo(
    () => clauses.filter(isScheduleScopeClause).length,
    [clauses],
  );

  // Search + scope + status filter on clauses
  const filteredClauses = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clauses
      .map((c, ci) => ({ c, ci }))
      .filter(({ c, ci }) => {
        // Scope filter (Fix #1) — applied FIRST so schedule rows never reach
        // the value-record render path when the reviewer is in 'provisions'
        // scope (the default). Backward compat: clauses without content_type
        // are treated as provisions.
        const isSchedule = isScheduleScopeClause(c);
        if (scope === 'provisions' && isSchedule) return false;
        if (scope === 'schedules' && !isSchedule) return false;

        // Search
        if (q) {
          // Fix #10: don't match the search query against footer-artifact
          // quants — a doc-id stamp like '8033780.16' should not surface as
          // a hit just because someone searched for '8033'.
          const valuesMatch = usefulQuants(c).some((quant, qi) => {
            const rec = quantToValueRecord(quant, c, qi, fileIdx, ci);
            return (rec.label || '').toLowerCase().includes(q) ||
                   (rec.value || '').toLowerCase().includes(q);
          });
          const matches =
            c.clause_no.toLowerCase().includes(q) ||
            (c.raw_text || '').toLowerCase().includes(q) ||
            deriveClauseTitle(c).toLowerCase().includes(q) ||
            (c.schedule_name || '').toLowerCase().includes(q) ||
            valuesMatch;
          if (!matches) return false;
        }
        // Status filter (clause-level)
        if (filter !== 'all') {
          // Fix #10: status filter must walk the same quant list as the
          // render path — otherwise a clause whose only "pending" rows are
          // footer artifacts would falsely match the Pending filter.
          const recIds = usefulQuants(c).map((_, qi) => `f${fileIdx}-c${ci}-q${qi}`);
          if (recIds.length === 0) return filter === 'pending';
          const statuses = recIds.map(id => liveByValueId.get(id)?.status || 'pending');
          if (filter === 'approved') return statuses.every(s => s === 'approved');
          if (filter === 'pending')  return statuses.some(s => s === 'pending');
          if (filter === 'flagged')  return statuses.some(s => s === 'flagged');
        }
        return true;
      })
      .map(({ c, ci }) => ({ clause: c, clauseIdx: ci }));
  }, [clauses, query, filter, scope, liveByValueId, fileIdx]);

  const visible = showAll ? filteredClauses : filteredClauses.slice(0, SOFT_CAP);
  const truncated = filteredClauses.length > visible.length;

  if (clauses.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic px-4 py-6 border border-dashed border-gray-200 rounded-lg">
        No clauses found in this document.
      </div>
    );
  }

  const approvedPct = totalValues > 0 ? (approvedTotal / totalValues) * 100 : 0;
  const flaggedPct  = totalValues > 0 ? (flaggedTotal  / totalValues) * 100 : 0;

  const budgetExceeded = schemaV2?.agreement_header?.time_budget_exceeded;

  return (
    <div className="bg-[#fcfcfa] border border-[#e7e4dc] rounded-xl overflow-hidden">
      {/* FC-10 / I-U1 — budget warning banner (Linux production only) */}
      {budgetExceeded && budgetExceeded.length > 0 && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-[12px]">
          <span className="font-bold shrink-0">⚠ TIME_BUDGET_EXCEEDED</span>
          <span>
            Pipeline hit its 10s wall-clock budget on this document. The following
            header field(s) may be incomplete:{' '}
            <span className="font-mono">{budgetExceeded.join(', ')}</span>.
            Re-run on a trimmed extract or review manually.
          </span>
        </div>
      )}
      {/* Sticky header wrapper (toolbar + column header). The combined height
          is measured at runtime so the per-clause gutter sticks just below. */}
      <div ref={headerRef} className="sticky top-0 z-20">
        {/* Toolbar */}
        <div
          className="flex flex-wrap items-center gap-4 gap-y-2.5 border-b border-[#e7e4dc]"
          style={{
            background: 'rgba(252,252,250,0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            padding: '13px 34px',
          }}
        >
          {/* Doc identity */}
          <div className="flex items-center gap-2 min-w-[80px]">
            <div className="w-[30px] h-[30px] rounded-lg bg-[#eaf4ee] flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-[#1f8a5b]" />
            </div>
            <div className="min-w-0">
              <div className="font-sans text-[14px] font-semibold text-[#1b1a17] truncate" title={fileLabel}>
                {fileLabel}
              </div>
              <div className="font-mono text-[11px] text-[#8a877e] truncate">
                {summary?.total_clauses ?? clauses.length} clauses · {summary?.total_quants ?? totalValues} values
                {filteredClauses.length !== clauses.length && ` · showing ${filteredClauses.length}`}
              </div>
            </div>
          </div>

          <div className="flex-1" />

          {/* Progress */}
          <div className="flex flex-col gap-1" style={{ flex: '0 0 auto', width: 150 }}>
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#8a877e]">Validated</span>
              <span className="font-mono text-[11px] font-semibold text-[#1b1a17]">
                {approvedTotal}/{totalValues}
              </span>
            </div>
            <div className="relative h-[6px] rounded-full overflow-hidden bg-[#eceae3]">
              <div
                className="absolute top-0 left-0 h-full bg-[#1f8a5b] transition-[width] duration-250"
                style={{ width: `${approvedPct}%` }}
              />
              <div
                className="absolute top-0 h-full bg-[#c0503c] transition-[width] duration-250"
                style={{ left: `${approvedPct}%`, width: `${flaggedPct}%` }}
              />
            </div>
          </div>

          {/* Flagged chip */}
          {flaggedTotal > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11.5px] font-semibold rounded-full bg-[#fbeae6] text-[#c0503c]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#c0503c]" />
              {flaggedTotal} flagged
            </span>
          )}

          {/* Search */}
          <div className="relative" style={{ width: 180 }}>
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8a877e]" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter clauses…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#e7e4dc] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e9f0fa] focus:border-[#3a6fc4] bg-white"
            />
          </div>

          {/* Scope pills (Fix #1) — Provisions is the default; schedules and
              exhibits live behind the toggle so dense credit agreements don't
              bury operative clauses under capitalisation tables. Hidden when
              the document has nothing in the non-default scopes. */}
          {scheduleCount > 0 && (
            <div className="inline-flex items-center bg-[#f5f3ee] rounded-[9px] p-[3px]" title="Show only operative provisions, only schedules/exhibits, or both">
              {([
                { key: 'provisions' as const, label: 'Provisions', count: provisionCount },
                { key: 'schedules'  as const, label: 'Schedules',  count: scheduleCount },
                { key: 'all'        as const, label: 'All',        count: provisionCount + scheduleCount },
              ]).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setScope(opt.key)}
                  className={
                    scope === opt.key
                      ? 'px-2.5 py-1 text-[12px] font-semibold rounded-[7px] bg-white text-[#1b1a17] shadow-sm'
                      : 'px-2.5 py-1 text-[12px] font-medium text-[#8a877e] hover:text-[#1b1a17]'
                  }
                >
                  {opt.label} <span className="opacity-60">{opt.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Filter pills */}
          <div className="inline-flex items-center bg-[#f5f3ee] rounded-[9px] p-[3px]">
            {(['all', 'pending', 'approved', 'flagged'] as StatusFilter[]).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={
                  filter === f
                    ? 'px-2.5 py-1 text-[12px] font-semibold rounded-[7px] bg-white text-[#1b1a17] shadow-sm capitalize'
                    : 'px-2.5 py-1 text-[12px] font-medium text-[#8a877e] hover:text-[#1b1a17] capitalize'
                }
              >
                {f}
              </button>
            ))}
          </div>

          {/* Download */}
          <button
            onClick={() => downloadCapturedValuesAsXlsx(fileLabel, clauses, liveByValueId, fileIdx, scope)}
            className="inline-flex items-center gap-1.5 px-[15px] py-[9px] text-[13px] font-semibold rounded-lg bg-[#1f8a5b] text-white hover:bg-[#187549]"
          >
            <Download className="w-3.5 h-3.5" /> Download .xlsx
          </button>

          {/* FIX 27 — Structured (Loan IQ) Export. Additive: appears next to
              the legacy Download button. Disabled when schema_v2 was not
              assembled (older docs or assembler failure) so reviewers know
              the new view is unavailable rather than silently downloading
              a stub. */}
          <button
            onClick={() => downloadStructuredXlsx(fileLabel, schemaV2, verification)}
            disabled={!schemaV2}
            title={schemaV2
              ? 'Loan IQ-format multi-tab export: Agreement Header + Parties + per-facility KDE blocks + Override_Log'
              : 'Structured export unavailable — re-run analysis to generate FIX 27 schema_v2'}
            className={
              'inline-flex items-center gap-1.5 px-[15px] py-[9px] text-[13px] font-semibold rounded-lg ' +
              (schemaV2
                ? 'bg-[#1f6a8a] text-white hover:bg-[#185675]'
                : 'bg-[#3a3a3a] text-[#888] cursor-not-allowed')
            }
          >
            <Download className="w-3.5 h-3.5" /> Structured Export
          </button>
        </div>

        {/* Column header */}
        <div
          className="grid"
          style={{
            gridTemplateColumns: `${density === 'comfortable' ? 168 : 150}px 1fr`,
            gap: 30,
            background: '#1b1a17',
            padding: '11px 34px',
          }}
        >
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#cdcbc4]">Clause</span>
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#cdcbc4]">
            Source text, plain English &amp; captured values
          </span>
        </div>
      </div>

      {/* Rows */}
      <div>
        {visible.length === 0 ? (
          <div className="px-8 py-12 text-center text-sm italic text-gray-400">
            {query
              ? `No clauses match "${query}".`
              : filter !== 'all'
                ? `No clauses in the "${filter}" state.`
                : 'No clauses to show.'}
          </div>
        ) : (
          visible.map(({ clause, clauseIdx }, vIdx) => (
            <ClauseReviewRow
              key={`${clause.clause_no}-${clauseIdx}`}
              clause={clause}
              fileIdx={fileIdx}
              clauseIdx={clauseIdx}
              liveById={liveByValueId}
              setLive={setLive}
              hoverId={hoverId}
              setHoverId={setHoverId}
              editingId={editingId}
              setEditingId={setEditingId}
              density={density}
              showPlain={showPlain}
              gutterTop={headerH + 8}
              zebra={vIdx % 2 === 0}
            />
          ))
        )}
      </div>

      {truncated && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full px-3 py-2 text-xs font-semibold text-electric-blue bg-blue-50 hover:bg-blue-100 transition-colors border-t border-gray-200"
        >
          Showing first {SOFT_CAP} of {filteredClauses.length} matches — show all
        </button>
      )}
    </div>
  );
}


// ─── Overall summary strip (multi-file aggregate) ──────────────────────────

function OverallSummary({ results }: { results: ContractAnalysisFileResult[] }) {
  const okFiles  = results.filter(r => !r.error).length;
  const failed   = results.length - okFiles;
  const clauses  = results.reduce((s, r) => s + (r.summary?.total_clauses ?? 0), 0);
  const quants   = results.reduce((s, r) => s + (r.summary?.total_quants  ?? 0), 0);
  const flagged  = results.filter(r => r.verification && r.verification.status !== 'ok').length;
  const hasAnySchema = results.some(r => !r.error && r.schema_v2);

  if (results.length < 2) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-100 rounded-xl">
      <ScrollText className="w-4 h-4 text-sky-700 flex-shrink-0" />
      <span className="text-xs font-semibold text-sky-900">
        {okFiles}/{results.length} file{results.length === 1 ? '' : 's'} analyzed
        {failed > 0 && <span className="text-red-700"> · {failed} failed</span>}
      </span>
      <span className="text-xs text-gray-400">·</span>
      <span className="text-xs text-gray-700">
        <span className="font-semibold">{clauses}</span> total clause{clauses === 1 ? '' : 's'}
      </span>
      <span className="text-xs text-gray-400">·</span>
      <span className="text-xs text-gray-700">
        <span className="font-semibold">{quants}</span> total quantitative value{quants === 1 ? '' : 's'}
      </span>
      {flagged > 0 && (
        <>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-amber-700 font-semibold">
            {flagged} flagged for review
          </span>
        </>
      )}
      {hasAnySchema && (
        <>
          <span className="text-xs text-gray-400 ml-auto">·</span>
          <button
            onClick={() => downloadCombinedStructuredXlsx(results)}
            title="Combined Loan IQ export — one facility sheet per document, each populated from its own source. Never reuses facility data across documents."
            className="inline-flex items-center gap-1.5 px-3 py-1 text-[12px] font-semibold rounded-lg bg-[#1f6a8a] text-white hover:bg-[#185675]"
          >
            <Download className="w-3 h-3" /> Combined Structured Export
          </button>
        </>
      )}
    </div>
  );
}


// ─── Top-level component ───────────────────────────────────────────────────

const ContractAnalysisResults: React.FC<ContractAnalysisResultsProps> = ({ results }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Clamp selectedIdx when results array shrinks (e.g. session rehydration).
  const safeIdx = Math.min(selectedIdx, results.length - 1);

  if (!results || results.length === 0) return null;

  const file = results[safeIdx];

  return (
    <div className="space-y-4">
      <OverallSummary results={results} />
      <FileNav results={results} selectedIdx={safeIdx} onSelect={setSelectedIdx} />

      <div className="space-y-3">
        {file.error ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm font-bold">
              <AlertTriangle className="w-4 h-4" />
              {file.file}
            </div>
            <p className="text-xs mt-1">{file.error}</p>
            {file.extracted_text_preview && (
              <details className="mt-2">
                <summary className="text-xs cursor-pointer hover:underline">Show extracted text preview</summary>
                <pre className="text-[11px] mt-1 whitespace-pre-wrap font-mono opacity-80 max-h-48 overflow-y-auto">
                  {file.extracted_text_preview}
                </pre>
              </details>
            )}
          </div>
        ) : (
          <>
            <VerificationBanner
              fileLabel={file.file}
              summary={file.summary}
              verification={file.verification}
            />
            {/* FIX 21 Phase 2-lite — renders nothing when neither
                registry key is present, so older sessions are
                byte-identical to today. */}
            <FacilityRegistryPanel
              block={file.schema_v2?.facility_registry}
              stage={file.facility_registry_stage}
            />
            <ClauseReviewWorkspace
              fileLabel={file.file}
              clauses={file.clauses || []}
              fileIdx={safeIdx}
              summary={file.summary}
              schemaV2={file.schema_v2}
              verification={file.verification}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ContractAnalysisResults;
